import React from 'react';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import xss from 'xss';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */
function arrayReduce(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array == null ? 0 : array.length;

  if (initAccum && length) {
    accumulator = array[++index];
  }
  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }
  return accumulator;
}

var _arrayReduce = arrayReduce;

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyOf(object) {
  return function(key) {
    return object == null ? undefined : object[key];
  };
}

var _basePropertyOf = basePropertyOf;

/** Used to map Latin Unicode letters to basic Latin letters. */
var deburredLetters = {
  // Latin-1 Supplement block.
  '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
  '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
  '\xc7': 'C',  '\xe7': 'c',
  '\xd0': 'D',  '\xf0': 'd',
  '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
  '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
  '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
  '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
  '\xd1': 'N',  '\xf1': 'n',
  '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
  '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
  '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
  '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
  '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
  '\xc6': 'Ae', '\xe6': 'ae',
  '\xde': 'Th', '\xfe': 'th',
  '\xdf': 'ss',
  // Latin Extended-A block.
  '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
  '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
  '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
  '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
  '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
  '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
  '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
  '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
  '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
  '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
  '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
  '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
  '\u0134': 'J',  '\u0135': 'j',
  '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
  '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
  '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
  '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
  '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
  '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
  '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
  '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
  '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
  '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
  '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
  '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
  '\u0163': 't',  '\u0165': 't', '\u0167': 't',
  '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
  '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
  '\u0174': 'W',  '\u0175': 'w',
  '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
  '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
  '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
  '\u0132': 'IJ', '\u0133': 'ij',
  '\u0152': 'Oe', '\u0153': 'oe',
  '\u0149': "'n", '\u017f': 's'
};

/**
 * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
 * letters to basic Latin letters.
 *
 * @private
 * @param {string} letter The matched letter to deburr.
 * @returns {string} Returns the deburred letter.
 */
var deburrLetter = _basePropertyOf(deburredLetters);

var _deburrLetter = deburrLetter;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/** Used to match Latin Unicode letters (excluding mathematical operators). */
var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

/** Used to compose unicode character classes. */
var rsComboMarksRange = '\\u0300-\\u036f',
    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange = '\\u20d0-\\u20ff',
    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange;

/** Used to compose unicode capture groups. */
var rsCombo = '[' + rsComboRange + ']';

/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */
var reComboMark = RegExp(rsCombo, 'g');

/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */
function deburr(string) {
  string = toString_1(string);
  return string && string.replace(reLatin, _deburrLetter).replace(reComboMark, '');
}

var deburr_1 = deburr;

/** Used to match words composed of alphanumeric characters. */
var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

/**
 * Splits an ASCII `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function asciiWords(string) {
  return string.match(reAsciiWord) || [];
}

var _asciiWords = asciiWords;

/** Used to detect strings that need a more robust regexp to match words. */
var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

/**
 * Checks if `string` contains a word composed of Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a word is found, else `false`.
 */
function hasUnicodeWord(string) {
  return reHasUnicodeWord.test(string);
}

var _hasUnicodeWord = hasUnicodeWord;

/** Used to compose unicode character classes. */
var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange$1 = '\\u0300-\\u036f',
    reComboHalfMarksRange$1 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$1 = '\\u20d0-\\u20ff',
    rsComboRange$1 = rsComboMarksRange$1 + reComboHalfMarksRange$1 + rsComboSymbolsRange$1,
    rsDingbatRange = '\\u2700-\\u27bf',
    rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
    rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
    rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
    rsPunctuationRange = '\\u2000-\\u206f',
    rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
    rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
    rsVarRange = '\\ufe0e\\ufe0f',
    rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

/** Used to compose unicode capture groups. */
var rsApos = "['\u2019]",
    rsBreak = '[' + rsBreakRange + ']',
    rsCombo$1 = '[' + rsComboRange$1 + ']',
    rsDigits = '\\d+',
    rsDingbat = '[' + rsDingbatRange + ']',
    rsLower = '[' + rsLowerRange + ']',
    rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo$1 + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsUpper = '[' + rsUpperRange + ']',
    rsZWJ = '\\u200d';

/** Used to compose unicode regexes. */
var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
    rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
    rsOptContrLower = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
    rsOptContrUpper = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
    reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange + ']?',
    rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
    rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq;

/** Used to match complex or compound words. */
var reUnicodeWord = RegExp([
  rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
  rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')',
  rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower,
  rsUpper + '+' + rsOptContrUpper,
  rsOrdUpper,
  rsOrdLower,
  rsDigits,
  rsEmoji
].join('|'), 'g');

/**
 * Splits a Unicode `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function unicodeWords(string) {
  return string.match(reUnicodeWord) || [];
}

var _unicodeWords = unicodeWords;

/**
 * Splits `string` into an array of its words.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to inspect.
 * @param {RegExp|string} [pattern] The pattern to match words.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the words of `string`.
 * @example
 *
 * _.words('fred, barney, & pebbles');
 * // => ['fred', 'barney', 'pebbles']
 *
 * _.words('fred, barney, & pebbles', /[^, ]+/g);
 * // => ['fred', 'barney', '&', 'pebbles']
 */
function words(string, pattern, guard) {
  string = toString_1(string);
  pattern = guard ? undefined : pattern;

  if (pattern === undefined) {
    return _hasUnicodeWord(string) ? _unicodeWords(string) : _asciiWords(string);
  }
  return string.match(pattern) || [];
}

var words_1 = words;

/** Used to compose unicode capture groups. */
var rsApos$1 = "['\u2019]";

/** Used to match apostrophes. */
var reApos = RegExp(rsApos$1, 'g');

/**
 * Creates a function like `_.camelCase`.
 *
 * @private
 * @param {Function} callback The function to combine each word.
 * @returns {Function} Returns the new compounder function.
 */
function createCompounder(callback) {
  return function(string) {
    return _arrayReduce(words_1(deburr_1(string).replace(reApos, '')), callback, '');
  };
}

var _createCompounder = createCompounder;

/**
 * Converts `string` to
 * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the kebab cased string.
 * @example
 *
 * _.kebabCase('Foo Bar');
 * // => 'foo-bar'
 *
 * _.kebabCase('fooBar');
 * // => 'foo-bar'
 *
 * _.kebabCase('__FOO_BAR__');
 * // => 'foo-bar'
 */
var kebabCase = _createCompounder(function(result, word, index) {
  return result + (index ? '-' : '') + word.toLowerCase();
});

var kebabCase_1 = kebabCase;

function generateDataAttr(data) {
  const props = {};
  getDataAttr(data).filter(v => v).forEach(item => props[item.key] = item.value);
  return props;
}

function getDataAttr(val = []) {
  return val.map(item => {
    const [key, val] = item.split('=');

    if (key && val !== undefined) {
      const value = val.replace(/["']/g, '');
      return {
        key,
        value
      };
    }
  });
}

const prefix = 'dorik';

function Nav({
  className,
  wrapperClass,
  item,
  attrs
}) {
  const {
    brand,
    btn,
    links,
    toggle
  } = item;

  const renderBrand = () => {
    var _brand$style;

    if (!(brand === null || brand === void 0 ? void 0 : brand.logo)) return null;
    return React.createElement("a", {
      href: brand.url || '#'
    }, React.createElement("img", {
      src: brand.logo,
      alt: brand.logoAlt,
      width: (_brand$style = brand.style) === null || _brand$style === void 0 ? void 0 : _brand$style.width
    }));
  };

  const renderIcon = icon => {
    return icon ? React.createElement(FontAwesomeIcon, {
      fixedWidth: true,
      icon: icon
    }) : null;
  };

  const renderLinks = () => {
    var _links$items;

    if (!(links === null || links === void 0 ? void 0 : (_links$items = links.items) === null || _links$items === void 0 ? void 0 : _links$items.length)) return null;
    return links.items.map((link, indx) => React.createElement("li", {
      key: indx
    }, React.createElement("a", _extends({
      target: link.target,
      href: link.url || '#',
      className: `${prefix}-nav--link ${kebabCase_1(link.label)}`
    }, generateDataAttr(link.data)), link.icon && React.createElement("span", {
      className: "dorik-nav--link-icon"
    }, renderIcon(link.icon)), React.createElement("span", {
      className: "dorik-nav--link-text"
    }, link.label))));
  };

  const renderBtns = () => {
    var _btn$items;

    if (!(btn === null || btn === void 0 ? void 0 : (_btn$items = btn.items) === null || _btn$items === void 0 ? void 0 : _btn$items.length)) return null;
    return btn.items.map((item, indx) => React.createElement("li", {
      key: indx
    }, React.createElement("a", _extends({
      href: item.url || '#',
      target: item.target,
      className: `${prefix}-nav--btn ${prefix}-nav--btn-${item.id} ${kebabCase_1(item.label)}`
    }, generateDataAttr(item.data)), item.icon && React.createElement("span", {
      className: "dorik-nav--btn-icon"
    }, renderIcon(item.icon)), React.createElement("span", {
      className: "dorik-nav--btn-text"
    }, item.label))));
  };

  const renderToggleIcon = () => {
    return React.createElement("button", {
      className: `${prefix}-navbar--toggle`,
      "data-target": `#${prefix}NavbarCollapse`
    }, React.createElement(FontAwesomeIcon, {
      fixedWidth: true,
      icon: ['fas', 'bars']
    }), ' ', toggle === null || toggle === void 0 ? void 0 : toggle.text);
  };

  const linkItems = renderLinks();
  const buttonItems = renderBtns();
  return React.createElement("div", {
    className: `${prefix}-navbar--wrapper ${wrapperClass}`
  }, React.createElement("div", _extends({
    className: `${prefix}-navbar ${className}`
  }, attrs), React.createElement("div", {
    className: `${prefix}-navbar--brand`
  }, renderBrand()), renderToggleIcon(), React.createElement("div", {
    className: `${prefix}-navbar--collapse collapse`,
    id: `${prefix}NavbarCollapse`
  }, React.createElement("div", {
    className: `${prefix}-navbar--navs`
  }, linkItems && React.createElement("ul", {
    className: `${prefix}-nav--links normalize`
  }, linkItems), buttonItems && React.createElement("ul", {
    className: `${prefix}-nav--btns normalize`
  }, buttonItems)))));
}

function Row({
  className,
  children,
  item,
  attrs
}) {
  const {
    wrapper
  } = item;
  const container = (wrapper === null || wrapper === void 0 ? void 0 : wrapper.width) ? 'container-fluid' : 'container';
  return React.createElement("div", _extends({
    className: container
  }, attrs), React.createElement("div", {
    className: className + ' flex'
  }, children));
}

function Tabs({
  className,
  wrapperClass,
  item: {
    tabs: {
      items,
      options
    }
  }
}) {
  const rendertTabTitle = items.map(({
    label,
    id
  }, index) => React.createElement("li", {
    role: "presentation",
    key: `tab-${id}`
  }, React.createElement("a", {
    href: `#section${index + 1}`,
    role: "tab" // id={`tab${index + 1}`}
    ,
    "aria-controls": `section${index + 1}`,
    "aria-selected": "true",
    "data-panel-class": `panel-${id}`,
    className: `tabs-trigger js-tabs-trigger`
  }, label)));
  const renderTabContent = items.map(({
    label,
    content,
    id
  }, index) => React.createElement("section", {
    key: `panel-${id}` // id={`section${index + 1}`}
    ,
    role: "tabpanel",
    "aria-labelledby": `tab${index + 1}`,
    className: `tabs-panel js-tabs-panel panel-${id}`,
    tabIndex: "0"
  }, React.createElement("div", {
    className: `accordion-trigger js-accordion-trigger accordion-${id}`,
    "aria-controls": `section${index + 1}`,
    "aria-expanded": "true",
    tabIndex: "0"
  }, label, React.createElement("div", {
    className: "accordion-trigger-icon"
  }, React.createElement("span", {
    className: "label--open"
  }, "Open"), React.createElement("span", {
    className: "label--close"
  }, "Close"), React.createElement("svg", {
    "aria-hidden": "true",
    focusable: "false",
    viewBox: "0 0 20 20"
  }, React.createElement("rect", {
    className: "vert",
    height: "18",
    width: "2",
    fill: "currentColor",
    y: "1",
    x: "9"
  }), React.createElement("rect", {
    height: "2",
    width: "18",
    fill: "currentColor",
    y: "9",
    x: "1"
  })))), React.createElement("div", {
    className: "content",
    "aria-hidden": "true",
    dangerouslySetInnerHTML: {
      __html: content
    }
  })));
  return React.createElement("div", {
    className: `${className} ${wrapperClass}`
  }, React.createElement("div", {
    className: "accordion-tabs dorik-tabs"
  }, React.createElement("ul", {
    role: "tablist",
    className: "tabs-tab-list"
  }, rendertTabTitle), renderTabContent), React.createElement("span", {
    "data-breakpoint": options.breakpoint,
    "data-selected-tab": options.selectedTab,
    className: "get-tabs-options",
    "data-tabs-wrapper": wrapperClass
  }));
}

function Text({
  className,
  children,
  attrs
}) {
  return React.createElement('div', {
    className: `dorik-text ${className}`,
    dangerouslySetInnerHTML: {
      __html: xss(children)
    },
    ...attrs
  });
}

const Line = ({
  className,
  wrapperClass,
  attrs
}) => {
  return React.createElement("div", {
    className: wrapperClass
  }, React.createElement("hr", _extends({
    className: className
  }, attrs)));
};

function Icon({
  className,
  children,
  wrapperClass,
  item,
  attrs
}) {
  const {
    icon = {},
    settings = {},
    attr = {}
  } = item;
  const {
    icon: iconArgs
  } = icon;
  const Component = attr.href ? 'a' : 'span';
  return React.createElement("div", {
    className: `${wrapperClass}`
  }, React.createElement(Component, _extends({
    className: `dorik-icon ${className}`
  }, attrs), iconArgs && React.createElement(FontAwesomeIcon, {
    fixedWidth: true,
    className: "icon",
    icon: [iconArgs.prefix, iconArgs.iconName]
  })));
}

var Icon$1 = React.memo(Icon);

function Code({
  item,
  className,
  interactionDisabled
}) {
  const {
    advanced = {}
  } = item;
  const {
    html = ''
  } = advanced;

  if (interactionDisabled && !html.trim()) {
    return React.createElement("div", {
      className: className,
      style: {
        fontSize: 14,
        color: '#ffffff',
        padding: '20px',
        fontStyle: 'italic',
        textAlign: 'center',
        borderRadius: '5px',
        background: '#484B5E'
      }
    }, React.createElement(FontAwesomeIcon, {
      icon: ['far', 'laptop-code']
    }), " Custom HTML Block");
  } else if (interactionDisabled) {
    return React.createElement("div", {
      className: className,
      style: {
        fontSize: 14,
        color: '#ffffff',
        padding: '20px',
        fontStyle: 'italic',
        borderRadius: '5px',
        background: '#484B5E'
      }
    }, React.createElement(FontAwesomeIcon, {
      icon: ['far', 'laptop-code']
    }), " Custom code will only appear on the published/exported site.");
  } else {
    return React.createElement("div", {
      className: className,
      dangerouslySetInnerHTML: {
        __html: html
      }
    });
  }
}

function Title(props) {
  var _settings$dynamicText, _wrapper$attr;

  const {
    className,
    children,
    item,
    attrs,
    wrapperClass
  } = props;
  const {
    titleType,
    wrapper = {},
    settings = {},
    dynamicText = {}
  } = item;
  const {
    items = [],
    options: dynamicTextOptions = {}
  } = dynamicText;
  const {
    loop,
    backSpeed,
    typeSpeed,
    cursorChar
  } = dynamicTextOptions;
  let TagName = xss(titleType),
      dynamic = (_settings$dynamicText = settings.dynamicText) === null || _settings$dynamicText === void 0 ? void 0 : _settings$dynamicText._enabled;

  const renderHeading = () => React.createElement(TagName, _extends({
    className: `dorik-heading ${className} ${wrapperClass}`
  }, attrs), React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: xss(children)
    }
  }), dynamic && items.length > 0 && React.createElement(React.Fragment, null, React.createElement("span", {
    className: "dorik-heading__dynamic "
  }, React.createElement("div", {
    className: "typed-strings"
  }, items.map(item => React.createElement("span", {
    key: item.id,
    dangerouslySetInnerHTML: {
      __html: xss(item.label) || xss(item.content)
    }
  }))), React.createElement("span", {
    className: "typed"
  })), React.createElement("span", {
    "data-loop": loop,
    "data-backspeed": backSpeed,
    "data-typespeed": typeSpeed,
    "data-cursor-char": cursorChar,
    "data-dynamic-class": wrapperClass,
    className: "getOptions"
  })));

  if (wrapper === null || wrapper === void 0 ? void 0 : (_wrapper$attr = wrapper.attr) === null || _wrapper$attr === void 0 ? void 0 : _wrapper$attr.href) {
    return React.createElement("a", _extends({
      className: className
    }, wrapper.attr), renderHeading());
  } else {
    return renderHeading();
  }
}

function Links({
  item,
  className
}) {
  var _links$items;

  const {
    links
  } = item;
  if (!(links === null || links === void 0 ? void 0 : (_links$items = links.items) === null || _links$items === void 0 ? void 0 : _links$items.length)) return null;

  const renderIcon = icon => {
    return icon ? React.createElement(FontAwesomeIcon, {
      fixedWidth: true,
      icon: icon
    }) : null;
  };

  return React.createElement("ul", {
    className: `dorik-list ${className}`
  }, links.items.map((link, indx) => React.createElement("li", {
    className: "dorik-list--item",
    key: indx
  }, React.createElement("a", {
    href: link.url,
    target: link.target,
    className: "dorik-list--link"
  }, React.createElement("span", {
    className: "dorik-list--icon"
  }, renderIcon(link.icon)), React.createElement("span", {
    className: "dorik-list--text"
  }, link.label)))));
}

function Video(props) {
  const {
    item = {},
    wrapperClass
  } = props;
  const {
    settings = {}
  } = item;

  if (settings.type !== 'embed') {
    return React.createElement("div", {
      className: wrapperClass
    }, React.createElement(HTMLVideo, props));
  } else {
    return React.createElement("div", {
      className: wrapperClass
    }, React.createElement(EmbedVideo, props));
  }
}

function HTMLVideo({
  className,
  item
}) {
  const {
    sources = {},
    attr = {}
  } = item;
  const items = Object.entries(sources).filter(([key, value]) => value);
  const autoplay = attr.autoplay ? true : null;
  const controls = attr.controls !== undefined ? attr.controls : true;
  return React.createElement("video", _extends({}, attr, {
    controls: controls,
    autoPlay: autoplay,
    className: className,
    playsInline: autoplay,
    poster: !items.length && '/assets/images/video-placeholder.jpg'
  }), items.map(([key, src]) => React.createElement("source", {
    key: key,
    src: src,
    type: `video/${key}`
  })), "Your browser doesn't support HTML5 video tag.");
}

function EmbedVideo(props) {
  const {
    className,
    item = {}
  } = props;
  const {
    attr = {},
    settings = {}
  } = item;
  let embedUrl = formatIframeSrc(settings.url);
  const query = getQueryString(attr);
  embedUrl += embedUrl ? `?${query}` : '';
  return React.createElement("div", {
    className: `dorik-iframe ${className}`
  }, React.createElement("iframe", {
    src: embedUrl,
    title: item.id,
    frameborder: "0",
    allow: getAllowPropString(attr),
    allowFullScreen: attr.fullscreen
  }));
}

function formatIframeSrc(url = '') {
  if (/vimeo/.test(url)) {
    const video = url.match(/vimeo\.com\/(\d+)/);
    return video ? `https://player.vimeo.com/video/${video[1]}` : '';
  } else if (/youtu(be)?\.(com|be)/.test(url)) {
    const ytRegx = /(.*?)(^|\/|v=)([a-z0-9_-]{11})/i;
    const video = url.match(ytRegx);
    return video ? `https://www.youtube.com/embed/${video[3]}` : '';
  } else {
    return url;
  }
}

function getQueryString(item = {}) {
  const options = {
    fs: item.fullscreen !== undefined ? item.fullscreen : 1,
    muted: item.muted ? 1 : 0,
    loop: item.loop ? 1 : 0,
    autoplay: item.autoplay ? 1 : 0,
    controls: item.controls !== undefined ? item.controls : 1
  };
  return Object.entries(options).map(([key, value]) => `${key}=${Number(value)}`).join('&');
}

function getAllowPropString(item) {
  const options = {
    autoplay: item.autoplay,
    fullscreen: item.fullscreen
  };
  return Object.entries(options).filter(([key, val]) => val).map(([key]) => key).join(';');
}

function Image(props) {
  const {
    className,
    item,
    wrapperClass,
    attrs = {}
  } = props;
  const {
    wrapper = {}
  } = item;
  let {
    attr = {}
  } = wrapper;
  let WrapperEl = 'div';

  if (wrapper.type === 'link' || wrapper.type === 'img-file') {
    WrapperEl = 'a';

    if (wrapper.type === 'img-file') {
      attr.href = attrs.src;
    }
  } else {
    attr = {};
  }

  return React.createElement(WrapperEl, _extends({
    className: `dorik-wrapper ${wrapperClass}`
  }, attr), React.createElement("img", _extends({}, attrs, {
    className: className
  })));
}

function Button({
  item,
  attrs,
  children,
  className,
  wrapperClass,
  interactionDisabled
}) {
  const {
    attr,
    icon = {},
    settings = {}
  } = item;
  const {
    icon: iconSvg = {}
  } = icon;
  const hasIcon = settings.buttonType !== 'text-only' && icon.icon;
  const inlineBtnCls = settings.inlineButton ? 'el-ib' : '';
  return React.createElement("div", {
    className: `${wrapperClass} ${inlineBtnCls}`
  }, React.createElement("a", _extends({
    // onClick={(e) => interactionDisabled && e.preventDefault()}
    className: `dorik-btn ${className}`
  }, attrs), hasIcon && React.createElement(FontAwesomeIcon, {
    fixedWidth: true,
    className: "icon",
    icon: [iconSvg.prefix, iconSvg.iconName]
  }), children));
}

function Iframe(props) {
  const {
    className,
    item = {}
  } = props;
  const {
    attr = {}
  } = item;
  return React.createElement("div", {
    className: `dorik-iframe ${className}`
  }, React.createElement("iframe", _extends({}, attr, {
    title: item.id,
    frameborder: "0",
    allowfullscreen: true
  })));
}

function isObject(val) {
  return Object.prototype.toString.call(val) === '[object Object]';
}

function getClasses(item) {
  var _item$settings;

  let dorikClass = `dorik-${item.type}-${item.id}`;
  dorikClass += ' ' + getHideClass(item === null || item === void 0 ? void 0 : (_item$settings = item.settings) === null || _item$settings === void 0 ? void 0 : _item$settings.hideOn);

  if (isObject(item.attr)) {
    Object.entries(item.attr).forEach(([key, value]) => {
      if (value && (key === 'class' || key.startsWith('__class__'))) {
        dorikClass += ' ' + value;
      }
    });
  }

  return dorikClass;
}
function getHideClass(hideOn) {
  if (!hideOn) return '';
  const obj = {
    mobile: 'hidden-sm',
    tablet: 'hidden-md',
    desktop: 'hidden-lg'
  };
  return hideOn.split('+').map(item => obj[item]).join(' ');
}

function Column({
  className,
  children,
  item,
  attrs,
  ...restOfProps
}) {
  var _item$settings;

  const gridClasses = getColumnClasses(item);
  const hideClass = getHideClass(item === null || item === void 0 ? void 0 : (_item$settings = item.settings) === null || _item$settings === void 0 ? void 0 : _item$settings.hideOn);
  return React.createElement("div", _extends({
    className: `${gridClasses} ${hideClass}`
  }, attrs), React.createElement("div", {
    className: `column-inner ${className}`
  }, children));
}

Column.defaultProps = {
  className: ''
};

function getColumnClasses(item) {
  var _media$tablet, _media$tablet$attr, _media$mobile, _media$mobile$attr;

  const {
    attr = {},
    media
  } = item;
  const colTabSize = media === null || media === void 0 ? void 0 : (_media$tablet = media.tablet) === null || _media$tablet === void 0 ? void 0 : (_media$tablet$attr = _media$tablet.attr) === null || _media$tablet$attr === void 0 ? void 0 : _media$tablet$attr.__class__columnSize;
  const colMobSize = media === null || media === void 0 ? void 0 : (_media$mobile = media.mobile) === null || _media$mobile === void 0 ? void 0 : (_media$mobile$attr = _media$mobile.attr) === null || _media$mobile$attr === void 0 ? void 0 : _media$mobile$attr.__class__columnSize;
  const classes = {
    [`col-lg-${attr.__class__columnSize}`]: attr.__class__columnSize != null,
    [`col-md-${colTabSize}`]: colTabSize != null,
    [`col-xs-${colMobSize}`]: colMobSize != null
  };
  return Object.entries(classes).map(([key, val]) => val && key).filter(v => v).join(' ');
}

function Section(props) {
  const {
    children,
    className,
    item,
    wrapperClass,
    attrs
  } = props;
  const [dorikClass] = className.split(' ');
  return React.createElement("section", _extends({
    className: `dorik-section ${className}`
  }, attrs), item.frames && Object.entries(item.frames).filter(([_, f]) => f.frameId).map(([position]) => React.createElement("div", {
    key: position,
    className: `${dorikClass}-frame-${position} dorik-frame-${position}`
  })), children);
}

function Icon$2({
  className,
  children,
  item,
  wrapperClass,
  attrs
}) {
  const {
    icon = {},
    settings = {}
  } = item;
  const {
    icon: iconArgs
  } = icon;
  return React.createElement("div", {
    className: `${wrapperClass}`
  }, React.createElement("div", _extends({
    className: className
  }, attrs), iconArgs && React.createElement(FontAwesomeIcon, {
    className: "icon",
    icon: [iconArgs.prefix, iconArgs.iconName]
  }), React.createElement("span", null, children)));
}

var IconText = React.memo(Icon$2);

function Accordion({
  className,
  item
}) {
  const {
    accordions
  } = item;
  let renderAccordion = accordions.items.map(({
    label,
    paragraph,
    id
  }) => React.createElement("details", {
    key: id
  }, React.createElement("summary", null, label || 'Accordion Label'), React.createElement("div", {
    className: "details-content",
    dangerouslySetInnerHTML: {
      __html: xss(paragraph) || '<p>Accordion Body</p>'
    }
  })));
  return React.createElement("div", {
    className: className
  }, renderAccordion);
}

var socialIcons = {
  Facebook: {
    brand: {
      circle: `<svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#2077F2"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M73.0618 127.86C70.1016 128.283 67.0761 128.502 64 128.502C60.1436 128.502 56.3668 128.158 52.6982 127.499V83.5H36V64.8125H52.6982V50.3906C52.6982 34.1406 62.4727 25 77.3382 25C84.4655 25 92 26.4219 92 26.4219V42.4688H83.6509C75.5055 42.4688 73.0618 47.3438 73.0618 52.625V64.8125H91.1855L88.3345 83.5H73.0618V127.86Z" fill="white"/>
            </svg>`,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#2077F2"/>
            <path d="M88.3345 82.9375L91.1855 64.4297H73.0618V52.3594C73.0618 47.1289 75.5055 42.3008 83.6509 42.3008H92V26.4082C92 26.4082 84.4655 25 77.3382 25C62.4727 25 52.6982 34.0527 52.6982 50.1465V64.4297H36V82.9375H52.6982V128H73.0618V82.9375H88.3345Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 95.3667 22.7424 121.641 52.6982 127.001V83.5H36V64.8125H52.6982V50.3906C52.6982 34.1406 62.4727 25 77.3382 25C84.4655 25 92 26.4219 92 26.4219V42.4688H83.6509C75.5055 42.4688 73.0618 47.3438 73.0618 52.625V64.8125H91.1855L88.3345 83.5H73.0618V127.361C104.117 122.94 128 96.144 128 64C128 28.7919 99.3462 0 64 0Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H73.0618V83.375H88.3345L91.1855 65.0469H73.0618V53.0938C73.0618 47.9141 75.5055 43.1328 83.6509 43.1328H92V27.3945C92 27.3945 84.4655 26 77.3382 26C62.4727 26 52.6982 34.9648 52.6982 50.9023V65.0469H36V83.375H52.6982V128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 94.0568 101.899 118.952 73.0618 123.32V83.2163H88.3345L91.1855 64.6194H73.0618V52.491C73.0618 47.2354 75.5055 42.384 83.6509 42.384H92V26.415C92 26.415 84.4655 25 77.3382 25C62.4727 25 52.6982 34.0963 52.6982 50.2675V64.6194H36V83.2163H52.6982V122.937C24.9642 117.651 4 93.2737 4 64C4 30.8629 30.8629 4 64 4ZM63.1728 127.995C28.2078 127.552 0 99.0701 0 64C0 28.6538 28.6538 0 64 0C99.3462 0 128 28.6538 128 64C128 98.9234 100.028 127.314 65.2655 127.988C64.8447 127.996 64.4228 128 64 128C63.7239 128 63.4481 127.998 63.1728 127.995Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H73.0618V83.375H88.3345L91.1855 65.0469H73.0618V53.0938C73.0618 47.9141 75.5055 43.1328 83.6509 43.1328H92V27.3945C92 27.3945 84.4655 26 77.3382 26C62.4727 26 52.6982 34.9648 52.6982 50.9023V65.0469H36V83.375H52.6982V124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM52.6982 128H9.14286C4.0934 128 0 123.907 0 118.857V9.14286C0 4.0934 4.0934 0 9.14286 0H118.857C123.907 0 128 4.0934 128 9.14286V118.857C128 123.907 123.907 128 118.857 128H73.0618H52.6982Z" />
                </svg>
                `
      }
    }
  },
  Messenger: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="url(#paint0_radial)"/>
            <path d="M64 26C42.5484 26 26 41.7823 26 62.9274C26 73.9597 30.4435 83.6129 37.9516 90.2016C39.1774 91.4274 38.871 92.0403 39.1774 99.0887C39.1774 100.774 40.5565 102 42.2419 102C42.5484 102 43.0081 102 43.4677 101.847C51.4355 98.3226 51.5887 98.0161 52.9677 98.3226C76.4113 104.758 102 89.7419 102 62.9274C102 41.7823 85.4516 26 64 26ZM86.8306 54.5L75.6452 72.121C74.7258 73.6532 72.5806 74.879 70.8952 74.879C69.8226 74.879 68.2903 74.2661 67.371 73.6532L58.4839 67.0645C58.1774 66.7581 57.5645 66.6048 57.1048 66.6048C56.7984 66.6048 56.1855 66.7581 55.7258 67.0645L43.7742 76.1048C42.0887 77.3306 40.0968 75.3387 41.1694 73.6532L52.3548 56.0323C53.2742 54.5 55.4194 53.4274 57.1048 53.4274C58.1774 53.4274 59.7097 53.8871 60.629 54.5L69.5161 61.0887C69.8226 61.3952 70.4355 61.5484 70.8952 61.5484C71.3548 61.5484 71.9677 61.3952 72.2742 61.0887L84.2258 52.0484C85.9113 50.8226 87.9032 52.8145 86.8306 54.5Z" fill="white"/>
            <defs>
            <radialGradient id="paint0_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(24.6367 127.815) scale(139.468 140.015)">
            <stop stop-color="#0099FF"/>
            <stop offset="0.609754" stop-color="#A033FF"/>
            <stop offset="0.934823" stop-color="#FF5280"/>
            <stop offset="1" stop-color="#FF7061"/>
            </radialGradient>
            </defs>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="url(#paint0_radial)"/>
            <path d="M64 26C42.5484 26 26 41.7823 26 62.9274C26 73.9597 30.4435 83.6129 37.9516 90.2016C39.1774 91.4274 38.871 92.0403 39.1774 99.0887C39.1774 100.774 40.5565 102 42.2419 102C42.5484 102 43.0081 102 43.4677 101.847C51.4355 98.3226 51.5887 98.0161 52.9677 98.3226C76.4113 104.758 102 89.7419 102 62.9274C102 41.7823 85.4516 26 64 26ZM86.8306 54.5L75.6452 72.121C74.7258 73.6532 72.5806 74.879 70.8952 74.879C69.8226 74.879 68.2903 74.2661 67.371 73.6532L58.4839 67.0645C58.1774 66.7581 57.5645 66.6048 57.1048 66.6048C56.7984 66.6048 56.1855 66.7581 55.7258 67.0645L43.7742 76.1048C42.0887 77.3306 40.0968 75.3387 41.1694 73.6532L52.3548 56.0323C53.2742 54.5 55.4194 53.4274 57.1048 53.4274C58.1774 53.4274 59.7097 53.8871 60.629 54.5L69.5161 61.0887C69.8226 61.3952 70.4355 61.5484 70.8952 61.5484C71.3548 61.5484 71.9677 61.3952 72.2742 61.0887L84.2258 52.0484C85.9113 50.8226 87.9032 52.8145 86.8306 54.5Z" fill="white"/>
            <defs>
            <radialGradient id="paint0_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(24.6367 127.315) scale(139.468)">
            <stop stop-color="#0099FF"/>
            <stop offset="0.609754" stop-color="#A033FF"/>
            <stop offset="0.934823" stop-color="#FF5280"/>
            <stop offset="1" stop-color="#FF7061"/>
            </radialGradient>
            </defs>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM26 62.9274C26 41.7823 42.5484 26 64 26C85.4516 26 102 41.7823 102 62.9274C102 89.7419 76.4113 104.758 52.9677 98.3226C51.87 98.0786 51.549 98.2231 47.445 100.07C46.3933 100.543 45.0931 101.128 43.4677 101.847C43.0081 102 42.5484 102 42.2419 102C40.5565 102 39.1774 100.774 39.1774 99.0887C39.1115 97.5736 39.074 96.3558 39.0434 95.3654C38.9319 91.7485 38.9139 91.1639 37.9516 90.2016C30.4435 83.6129 26 73.9597 26 62.9274ZM75.6452 72.121L86.8306 54.5C87.9032 52.8145 85.9113 50.8226 84.2258 52.0484L72.2742 61.0887C71.9677 61.3952 71.3548 61.5484 70.8952 61.5484C70.4355 61.5484 69.8226 61.3952 69.5161 61.0887L60.629 54.5C59.7097 53.8871 58.1774 53.4274 57.1048 53.4274C55.4194 53.4274 53.2742 54.5 52.3548 56.0323L41.1694 73.6532C40.0968 75.3387 42.0887 77.3306 43.7742 76.1048L55.7258 67.0645C56.1855 66.7581 56.7984 66.6048 57.1048 66.6048C57.5645 66.6048 58.1774 66.7581 58.4839 67.0645L67.371 73.6532C68.2903 74.2661 69.8226 74.879 70.8952 74.879C72.5806 74.879 74.7258 73.6532 75.6452 72.121Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM26 62.9274C26 41.7823 42.5484 26 64 26C85.4516 26 102 41.7823 102 62.9274C102 89.7419 76.4113 104.758 52.9677 98.3226C51.87 98.0786 51.549 98.2231 47.445 100.07C46.3933 100.543 45.0931 101.128 43.4677 101.847C43.0081 102 42.5484 102 42.2419 102C40.5565 102 39.1774 100.774 39.1774 99.0887C39.1115 97.5736 39.074 96.3558 39.0434 95.3654C38.9319 91.7485 38.9139 91.1639 37.9516 90.2016C30.4435 83.6129 26 73.9597 26 62.9274ZM75.6452 72.121L86.8306 54.5C87.9032 52.8145 85.9113 50.8226 84.2258 52.0484L72.2742 61.0887C71.9677 61.3952 71.3548 61.5484 70.8952 61.5484C70.4355 61.5484 69.8226 61.3952 69.5161 61.0887L60.629 54.5C59.7097 53.8871 58.1774 53.4274 57.1048 53.4274C55.4194 53.4274 53.2742 54.5 52.3548 56.0323L41.1694 73.6532C40.0968 75.3387 42.0887 77.3306 43.7742 76.1048L55.7258 67.0645C56.1855 66.7581 56.7984 66.6048 57.1048 66.6048C57.5645 66.6048 58.1774 66.7581 58.4839 67.0645L67.371 73.6532C68.2903 74.2661 69.8226 74.879 70.8952 74.879C72.5806 74.879 74.7258 73.6532 75.6452 72.121Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM26 62.9274C26 41.7823 42.5484 26 64 26C85.4516 26 102 41.7823 102 62.9274C102 89.7419 76.4113 104.758 52.9677 98.3226C51.87 98.0786 51.549 98.2231 47.445 100.07C46.3933 100.543 45.0931 101.128 43.4677 101.847C43.0081 102 42.5484 102 42.2419 102C40.5565 102 39.1774 100.774 39.1774 99.0887C39.1115 97.5736 39.074 96.3558 39.0434 95.3654C38.9319 91.7485 38.9139 91.1639 37.9516 90.2016C30.4435 83.6129 26 73.9597 26 62.9274ZM75.6452 72.121L86.8306 54.5C87.9032 52.8145 85.9113 50.8226 84.2258 52.0484L72.2742 61.0887C71.9677 61.3952 71.3548 61.5484 70.8952 61.5484C70.4355 61.5484 69.8226 61.3952 69.5161 61.0887L60.629 54.5C59.7097 53.8871 58.1774 53.4274 57.1048 53.4274C55.4194 53.4274 53.2742 54.5 52.3548 56.0323L41.1694 73.6532C40.0968 75.3387 42.0887 77.3306 43.7742 76.1048L55.7258 67.0645C56.1855 66.7581 56.7984 66.6048 57.1048 66.6048C57.5645 66.6048 58.1774 66.7581 58.4839 67.0645L67.371 73.6532C68.2903 74.2661 69.8226 74.879 70.8952 74.879C72.5806 74.879 74.7258 73.6532 75.6452 72.121Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM26 62.9274C26 41.7823 42.5484 26 64 26C85.4516 26 102 41.7823 102 62.9274C102 89.7419 76.4113 104.758 52.9677 98.3226C51.87 98.0786 51.549 98.2231 47.445 100.07C46.3933 100.543 45.0931 101.128 43.4677 101.847C43.0081 102 42.5484 102 42.2419 102C40.5565 102 39.1774 100.774 39.1774 99.0887C39.1115 97.5736 39.074 96.3558 39.0434 95.3654C38.9319 91.7485 38.9139 91.1639 37.9516 90.2016C30.4435 83.6129 26 73.9597 26 62.9274ZM75.6452 72.121L86.8306 54.5C87.9032 52.8145 85.9113 50.8226 84.2258 52.0484L72.2742 61.0887C71.9677 61.3952 71.3548 61.5484 70.8952 61.5484C70.4355 61.5484 69.8226 61.3952 69.5161 61.0887L60.629 54.5C59.7097 53.8871 58.1774 53.4274 57.1048 53.4274C55.4194 53.4274 53.2742 54.5 52.3548 56.0323L41.1694 73.6532C40.0968 75.3387 42.0887 77.3306 43.7742 76.1048L55.7258 67.0645C56.1855 66.7581 56.7984 66.6048 57.1048 66.6048C57.5645 66.6048 58.1774 66.7581 58.4839 67.0645L67.371 73.6532C68.2903 74.2661 69.8226 74.879 70.8952 74.879C72.5806 74.879 74.7258 73.6532 75.6452 72.121Z" />
                </svg>
                `
      }
    }
  },
  Pinterest: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#E71F27"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M43.8617 125.243C42.6024 124.823 41.3604 124.365 40.1371 123.869C39.8653 121.214 39.6562 118.599 39.6562 115.883C39.6562 106.282 43.2056 93.2861 46.2023 82.3134C47.0951 79.0445 47.9388 75.9552 48.625 73.1888C46.9167 69.9203 46.4896 66.2432 46.4896 62.7705C46.4896 46.428 66.5625 43.9767 66.5625 57.4592C66.5625 61.4685 65.1023 65.3744 63.6516 69.2549C62.2196 73.0853 60.7969 76.891 60.7969 80.7471C60.7969 85.8542 65.4948 89.5312 70.8333 89.5312C85.5677 89.5312 90.0521 69.3074 90.0521 58.4806C90.0521 43.9767 79.375 36.0097 64.6406 36.0097C47.7708 36.0097 34.7448 47.6537 34.7448 63.9962C34.7448 69.3778 37.0833 72.9884 38.601 75.3317C39.3301 76.4574 39.8698 77.2906 39.8698 77.8872C39.8698 79.5215 38.5885 85.037 36.4531 85.037C31.3281 85.037 23 76.8658 23 62.5662C23 39.8911 44.5677 26 66.5625 26C86.6354 26 105 39.2782 105 59.502C105 78.5001 94.75 99.7452 72.1146 99.7452C66.5625 99.7452 59.9427 97.0896 57.1667 92.3911C56.6293 94.4942 56.1561 96.3942 55.7236 98.131C52.6085 110.64 51.6002 114.689 43.8617 125.243Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#E71F27"/>
            <path d="M66.5625 26C44.5677 26 23 39.8911 23 62.5662C23 76.8658 31.3281 85.037 36.4531 85.037C38.5885 85.037 39.8698 79.5215 39.8698 77.8872C39.8698 76.0487 34.7448 71.9631 34.7448 63.9962C34.7448 47.6537 47.7708 36.0097 64.6406 36.0097C79.375 36.0097 90.0521 43.9767 90.0521 58.4806C90.0521 69.3074 85.5677 89.5312 70.8333 89.5312C65.4948 89.5312 60.7969 85.8542 60.7969 80.7471C60.7969 72.9845 66.5625 65.4261 66.5625 57.4592C66.5625 43.9767 46.4896 46.428 46.4896 62.7705C46.4896 66.2432 46.9167 69.9203 48.625 73.1888C45.6354 85.2413 39.6562 103.422 39.6562 115.883C39.6562 119.765 40.0833 123.442 40.5104 127.323C41.151 128.14 40.9375 128.14 42.0052 127.732C52.6823 113.636 52.4687 110.776 57.1667 92.3911C59.9427 97.0896 66.5625 99.7452 72.1146 99.7452C94.75 99.7452 105 78.5001 105 59.502C105 39.2782 86.6354 26 66.5625 26Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 90.7868 16.5859 113.86 40.0862 123.365C39.8396 120.882 39.6562 118.427 39.6562 115.883C39.6562 106.283 43.2052 93.2876 46.2018 82.3153L46.2023 82.3134C47.0951 79.0445 47.9388 75.9552 48.625 73.1888C46.9167 69.9203 46.4896 66.2432 46.4896 62.7705C46.4896 46.428 66.5625 43.9767 66.5625 57.4592C66.5625 61.4685 65.1023 65.3744 63.6516 69.2549C62.2196 73.0853 60.7969 76.891 60.7969 80.7471C60.7969 85.8542 65.4948 89.5312 70.8333 89.5312C85.5677 89.5312 90.0521 69.3074 90.0521 58.4806C90.0521 43.9767 79.375 36.0097 64.6406 36.0097C47.7708 36.0097 34.7448 47.6537 34.7448 63.9962C34.7448 69.3778 37.0833 72.9884 38.601 75.3317L38.6013 75.3322C39.3303 76.4576 39.8698 77.2907 39.8698 77.8872C39.8698 79.5215 38.5885 85.037 36.4531 85.037C31.3281 85.037 23 76.8658 23 62.5662C23 39.8911 44.5677 26 66.5625 26C86.6354 26 105 39.2782 105 59.502C105 78.5001 94.75 99.7452 72.1146 99.7452C66.5625 99.7452 59.9427 97.0896 57.1667 92.3911C56.6293 94.4942 56.1561 96.3942 55.7236 98.131L55.7236 98.1311C52.6475 110.483 51.6258 114.586 44.1496 124.849C50.3989 126.894 57.0708 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM23 62.5662C23 39.8911 44.5677 26 66.5625 26C86.6354 26 105 39.2782 105 59.502C105 78.5001 94.75 99.7452 72.1146 99.7452C66.5625 99.7452 59.9427 97.0896 57.1667 92.3911C56.6292 94.4944 56.156 96.3945 55.7235 98.1315C52.375 111.577 51.4608 115.249 42.0052 127.732C41.4674 127.938 41.2547 128.04 41.0941 127.986C40.9359 127.933 40.8283 127.728 40.5104 127.323C40.49 127.138 40.4697 126.954 40.4494 126.77C40.0436 123.091 39.6562 119.58 39.6562 115.883C39.6562 106.282 43.2056 93.2861 46.2023 82.3135C47.0951 79.0446 47.9388 75.9553 48.625 73.1888C46.9167 69.9203 46.4896 66.2432 46.4896 62.7705C46.4896 46.428 66.5625 43.9767 66.5625 57.4592C66.5625 61.4685 65.1023 65.3744 63.6516 69.2549C62.2196 73.0853 60.7969 76.891 60.7969 80.7471C60.7969 85.8542 65.4948 89.5312 70.8333 89.5312C85.5677 89.5312 90.0521 69.3074 90.0521 58.4806C90.0521 43.9767 79.375 36.0097 64.6406 36.0097C47.7708 36.0097 34.7448 47.6537 34.7448 63.9962C34.7448 69.3779 37.0833 72.9885 38.6011 75.3317C39.3301 76.4574 39.8698 77.2906 39.8698 77.8872C39.8698 79.5215 38.5885 85.037 36.4531 85.037C31.3281 85.037 23 76.8658 23 62.5662Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C57.7266 124 51.678 123.037 45.9941 121.251C51.8234 112.793 52.9206 108.387 55.7236 97.1311L55.7236 97.131C56.1561 95.3942 56.6293 93.4942 57.1667 91.3911C59.9427 96.0896 66.5625 98.7452 72.1146 98.7452C94.75 98.7452 105 77.5001 105 58.502C105 38.2782 86.6354 25 66.5625 25C44.5677 25 23 38.8911 23 61.5662C23 75.8658 31.3281 84.037 36.4531 84.037C38.5885 84.037 39.8698 78.5215 39.8698 76.8872C39.8698 76.2907 39.3303 75.4576 38.6013 74.3322L38.601 74.3317C37.0833 71.9884 34.7448 68.3778 34.7448 62.9962C34.7448 46.6537 47.7708 35.0097 64.6406 35.0097C79.375 35.0097 90.0521 42.9767 90.0521 57.4806C90.0521 68.3074 85.5677 88.5312 70.8333 88.5312C65.4948 88.5312 60.7969 84.8542 60.7969 79.7471C60.7969 75.891 62.2196 72.0853 63.6516 68.2549C65.1023 64.3744 66.5625 60.4685 66.5625 56.4592C66.5625 42.9767 46.4896 45.428 46.4896 61.7705C46.4896 65.2432 46.9167 68.9203 48.625 72.1888C47.9388 74.9552 47.0951 78.0445 46.2023 81.3134L46.2018 81.3153C43.2052 92.2876 39.6562 105.283 39.6562 114.883C39.6562 116.247 39.709 117.586 39.796 118.918C18.7141 109.613 4 88.5248 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H44.7639C51.6847 114.35 52.7338 110.137 55.7235 98.1315C56.156 96.3945 56.6292 94.4944 57.1667 92.3911C59.9427 97.0896 66.5625 99.7452 72.1146 99.7452C94.75 99.7452 105 78.5001 105 59.502C105 39.2782 86.6354 26 66.5625 26C44.5677 26 23 39.8911 23 62.5662C23 76.8658 31.3281 85.037 36.4531 85.037C38.5885 85.037 39.8698 79.5215 39.8698 77.8872C39.8698 77.2906 39.3301 76.4574 38.6011 75.3317C37.0833 72.9885 34.7448 69.3779 34.7448 63.9962C34.7448 47.6537 47.7708 36.0097 64.6406 36.0097C79.375 36.0097 90.0521 43.9767 90.0521 58.4806C90.0521 69.3074 85.5677 89.5312 70.8333 89.5312C65.4948 89.5312 60.7969 85.8542 60.7969 80.7471C60.7969 76.891 62.2196 73.0853 63.6516 69.2549C65.1023 65.3744 66.5625 61.4685 66.5625 57.4592C66.5625 43.9767 46.4896 46.428 46.4896 62.7705C46.4896 66.2432 46.9167 69.9203 48.625 73.1888C47.9388 75.9553 47.0951 79.0446 46.2023 82.3135C43.2056 93.2861 39.6562 106.282 39.6562 115.883C39.6562 118.643 39.8721 121.3 40.1505 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0Z" />
                </svg>
                `
      }
    }
  },
  Slack: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#4A154B"/>
            <path d="M41.9663 74.0225C41.9622 76.1385 41.1204 78.1668 39.625 79.664C38.1296 81.1611 36.1023 82.0053 33.9863 82.012C31.8692 82.007 29.8403 81.1635 28.3436 79.6662C26.8468 78.1689 26.0042 76.1396 26 74.0225C26.0059 71.9068 26.8494 69.8795 28.346 68.384C29.8426 66.8886 31.8706 66.0467 33.9863 66.0425H41.9663V74.0225ZM45.9912 74.0225C45.997 71.9073 46.8401 69.8805 48.336 68.3851C49.832 66.8898 51.8592 66.0475 53.9743 66.0425C56.0895 66.0475 58.1167 66.8898 59.6126 68.3851C61.1086 69.8805 61.9516 71.9073 61.9575 74.0225V94.0137C61.9525 96.1297 61.1099 98.1577 59.6139 99.6542C58.118 101.151 56.0903 101.994 53.9743 102C51.8583 101.994 49.8307 101.151 48.3347 99.6542C46.8388 98.1577 45.9962 96.1297 45.9912 94.0137V74.0225ZM53.9743 41.9663C51.8594 41.9605 49.8327 41.118 48.337 39.6228C46.8412 38.1276 45.9979 36.1013 45.9912 33.9863C45.9962 31.8703 46.8388 29.8424 48.3347 28.3458C49.8307 26.8492 51.8583 26.0059 53.9743 26C56.0903 26.0059 58.118 26.8492 59.6139 28.3458C61.1099 29.8424 61.9525 31.8703 61.9575 33.9863V41.9663H53.9743ZM53.9743 45.9912C56.0898 45.997 58.117 46.84 59.6128 48.3358C61.1087 49.8317 61.9516 51.8589 61.9575 53.9743C61.9516 56.0898 61.1087 58.117 59.6128 59.6128C58.117 61.1087 56.0898 61.9516 53.9743 61.9575H33.9863C31.8703 61.9525 29.8424 61.1099 28.3458 59.6139C26.8492 58.118 26.0059 56.0903 26 53.9743C26.0059 51.8583 26.8492 49.8307 28.3458 48.3347C29.8424 46.8388 31.8703 45.9962 33.9863 45.9912H53.9743ZM86.0273 53.9743C86.0332 51.8583 86.8766 49.8307 88.3731 48.3347C89.8697 46.8388 91.8977 45.9962 94.0137 45.9912C96.1297 45.9962 98.1577 46.8388 99.6542 48.3347C101.151 49.8307 101.994 51.8583 102 53.9743C101.994 56.0903 101.151 58.118 99.6542 59.6139C98.1577 61.1099 96.1297 61.9525 94.0137 61.9575H86.0273V53.9743ZM82.012 53.9743C82.0061 56.0909 81.1623 58.119 79.6651 59.6151C78.1678 61.1111 76.1391 61.9533 74.0225 61.9575C71.9073 61.9516 69.8805 61.1086 68.3851 59.6126C66.8898 58.1167 66.0475 56.0895 66.0425 53.9743V33.9863C66.0467 31.8706 66.8886 29.8426 68.384 28.346C69.8795 26.8494 71.9068 26.0059 74.0225 26C76.1396 26.0042 78.1689 26.8468 79.6662 28.3436C81.1635 29.8403 82.007 31.8692 82.012 33.9863V53.9743ZM74.0225 86.0273C76.1396 86.0315 78.1689 86.8742 79.6662 88.3709C81.1635 89.8676 82.007 91.8966 82.012 94.0137C82.007 96.1308 81.1635 98.1597 79.6662 99.6564C78.1689 101.153 76.1396 101.996 74.0225 102C71.9068 101.994 69.8795 101.151 68.384 99.654C66.8886 98.1574 66.0467 96.1294 66.0425 94.0137V86.0273H74.0225ZM74.0225 82.012C71.9062 82.0061 69.8785 81.1622 68.3829 79.6649C66.8874 78.1676 66.0458 76.1388 66.0425 74.0225C66.0475 71.9076 66.8899 69.8808 68.3853 68.3853C69.8808 66.8899 71.9076 66.0475 74.0225 66.0425H94.0137C96.1294 66.0467 98.1574 66.8886 99.654 68.384C101.151 69.8795 101.994 71.9068 102 74.0225C101.996 76.1396 101.153 78.1689 99.6564 79.6662C98.1597 81.1635 96.1308 82.007 94.0137 82.012H74.0225Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#4A154B"/>
            <path d="M41.9663 74.0225C41.9622 76.1385 41.1204 78.1668 39.625 79.664C38.1296 81.1611 36.1023 82.0053 33.9863 82.012C31.8692 82.007 29.8403 81.1635 28.3436 79.6662C26.8468 78.1689 26.0042 76.1396 26 74.0225C26.0059 71.9068 26.8494 69.8795 28.346 68.384C29.8426 66.8886 31.8706 66.0467 33.9863 66.0425H41.9663V74.0225ZM45.9912 74.0225C45.997 71.9073 46.8401 69.8805 48.336 68.3851C49.832 66.8898 51.8592 66.0475 53.9743 66.0425C56.0895 66.0475 58.1167 66.8898 59.6126 68.3851C61.1086 69.8805 61.9516 71.9073 61.9575 74.0225V94.0137C61.9525 96.1297 61.1099 98.1577 59.6139 99.6542C58.118 101.151 56.0903 101.994 53.9743 102C51.8583 101.994 49.8307 101.151 48.3347 99.6542C46.8388 98.1577 45.9962 96.1297 45.9912 94.0137V74.0225ZM53.9743 41.9663C51.8594 41.9605 49.8327 41.118 48.337 39.6228C46.8412 38.1276 45.9979 36.1013 45.9912 33.9863C45.9962 31.8703 46.8388 29.8424 48.3347 28.3458C49.8307 26.8492 51.8583 26.0059 53.9743 26C56.0903 26.0059 58.118 26.8492 59.6139 28.3458C61.1099 29.8424 61.9525 31.8703 61.9575 33.9863V41.9663H53.9743ZM53.9743 45.9912C56.0898 45.997 58.117 46.84 59.6128 48.3358C61.1087 49.8317 61.9516 51.8589 61.9575 53.9743C61.9516 56.0898 61.1087 58.117 59.6128 59.6128C58.117 61.1087 56.0898 61.9516 53.9743 61.9575H33.9863C31.8703 61.9525 29.8424 61.1099 28.3458 59.6139C26.8492 58.118 26.0059 56.0903 26 53.9743C26.0059 51.8583 26.8492 49.8307 28.3458 48.3347C29.8424 46.8388 31.8703 45.9962 33.9863 45.9912H53.9743ZM86.0273 53.9743C86.0332 51.8583 86.8766 49.8307 88.3731 48.3347C89.8697 46.8388 91.8977 45.9962 94.0137 45.9912C96.1297 45.9962 98.1577 46.8388 99.6542 48.3347C101.151 49.8307 101.994 51.8583 102 53.9743C101.994 56.0903 101.151 58.118 99.6542 59.6139C98.1577 61.1099 96.1297 61.9525 94.0137 61.9575H86.0273V53.9743ZM82.012 53.9743C82.0061 56.0909 81.1623 58.119 79.6651 59.6151C78.1678 61.1111 76.1391 61.9533 74.0225 61.9575C71.9073 61.9516 69.8805 61.1086 68.3851 59.6126C66.8898 58.1167 66.0475 56.0895 66.0425 53.9743V33.9863C66.0467 31.8706 66.8886 29.8426 68.384 28.346C69.8795 26.8494 71.9068 26.0059 74.0225 26C76.1396 26.0042 78.1689 26.8468 79.6662 28.3436C81.1635 29.8403 82.007 31.8692 82.012 33.9863V53.9743ZM74.0225 86.0273C76.1396 86.0315 78.1689 86.8742 79.6662 88.3709C81.1635 89.8676 82.007 91.8966 82.012 94.0137C82.007 96.1308 81.1635 98.1597 79.6662 99.6564C78.1689 101.153 76.1396 101.996 74.0225 102C71.9068 101.994 69.8795 101.151 68.384 99.654C66.8886 98.1574 66.0467 96.1294 66.0425 94.0137V86.0273H74.0225ZM74.0225 82.012C71.9062 82.0061 69.8785 81.1622 68.3829 79.6649C66.8874 78.1676 66.0458 76.1388 66.0425 74.0225C66.0475 71.9076 66.8899 69.8808 68.3853 68.3853C69.8808 66.8899 71.9076 66.0475 74.0225 66.0425H94.0137C96.1294 66.0467 98.1574 66.8886 99.654 68.384C101.151 69.8795 101.994 71.9068 102 74.0225C101.996 76.1396 101.153 78.1689 99.6564 79.6662C98.1597 81.1635 96.1308 82.007 94.0137 82.012H74.0225Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM39.625 79.664C41.1204 78.1668 41.9622 76.1385 41.9663 74.0225V66.0425H33.9863C31.8706 66.0467 29.8426 66.8886 28.346 68.384C26.8494 69.8795 26.0059 71.9068 26 74.0225C26.0042 76.1396 26.8468 78.1689 28.3436 79.6662C29.8403 81.1635 31.8692 82.007 33.9863 82.012C36.1023 82.0053 38.1296 81.1611 39.625 79.664ZM48.336 68.3851C46.8401 69.8805 45.997 71.9073 45.9912 74.0225V94.0137C45.9962 96.1297 46.8388 98.1577 48.3347 99.6542C49.8307 101.151 51.8583 101.994 53.9743 102C56.0903 101.994 58.118 101.151 59.6139 99.6542C61.1099 98.1577 61.9525 96.1297 61.9575 94.0137V74.0225C61.9516 71.9073 61.1086 69.8805 59.6126 68.3851C58.1167 66.8898 56.0895 66.0475 53.9743 66.0425C51.8592 66.0475 49.832 66.8898 48.336 68.3851ZM48.337 39.6228C49.8327 41.118 51.8594 41.9605 53.9743 41.9663H61.9575V33.9863C61.9525 31.8703 61.1099 29.8424 59.6139 28.3458C58.118 26.8492 56.0903 26.0059 53.9743 26C51.8583 26.0059 49.8307 26.8492 48.3347 28.3458C46.8388 29.8424 45.9962 31.8703 45.9912 33.9863C45.9979 36.1013 46.8412 38.1276 48.337 39.6228ZM59.6128 48.3358C58.117 46.84 56.0898 45.997 53.9743 45.9912H33.9863C31.8703 45.9962 29.8424 46.8388 28.3458 48.3347C26.8492 49.8307 26.0059 51.8583 26 53.9743C26.0059 56.0903 26.8492 58.118 28.3458 59.6139C29.8424 61.1099 31.8703 61.9525 33.9863 61.9575H53.9743C56.0898 61.9517 58.117 61.1087 59.6128 59.6128C61.1087 58.117 61.9517 56.0898 61.9575 53.9743C61.9517 51.8589 61.1087 49.8317 59.6128 48.3358ZM88.3731 48.3347C86.8766 49.8307 86.0332 51.8583 86.0273 53.9743V61.9575H94.0137C96.1297 61.9525 98.1577 61.1099 99.6542 59.6139C101.151 58.118 101.994 56.0903 102 53.9743C101.994 51.8583 101.151 49.8307 99.6542 48.3347C98.1577 46.8388 96.1297 45.9962 94.0137 45.9912C91.8977 45.9962 89.8697 46.8388 88.3731 48.3347ZM79.6651 59.6151C81.1623 58.119 82.0061 56.0909 82.012 53.9743V33.9863C82.007 31.8692 81.1635 29.8403 79.6662 28.3436C78.1689 26.8468 76.1396 26.0042 74.0225 26C71.9068 26.0059 69.8795 26.8494 68.384 28.346C66.8886 29.8426 66.0467 31.8706 66.0425 33.9863V53.9743C66.0475 56.0895 66.8898 58.1167 68.3851 59.6126C69.8805 61.1086 71.9073 61.9516 74.0225 61.9575C76.1391 61.9533 78.1678 61.1111 79.6651 59.6151ZM79.6662 88.3709C78.1689 86.8742 76.1396 86.0315 74.0225 86.0273H66.0425V94.0137C66.0467 96.1294 66.8886 98.1574 68.384 99.654C69.8795 101.151 71.9068 101.994 74.0225 102C76.1396 101.996 78.1689 101.153 79.6662 99.6564C81.1635 98.1597 82.007 96.1308 82.012 94.0137C82.007 91.8966 81.1635 89.8676 79.6662 88.3709ZM68.3829 79.6649C69.8784 81.1622 71.9062 82.0061 74.0225 82.012H94.0137C96.1308 82.007 98.1597 81.1635 99.6564 79.6662C101.153 78.1689 101.996 76.1396 102 74.0225C101.994 71.9068 101.151 69.8795 99.654 68.384C98.1574 66.8886 96.1294 66.0467 94.0137 66.0425H74.0225C71.9076 66.0475 69.8808 66.8899 68.3853 68.3853C66.8899 69.8808 66.0475 71.9076 66.0425 74.0225C66.0458 76.1388 66.8874 78.1676 68.3829 79.6649Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM39.625 79.664C41.1204 78.1668 41.9622 76.1385 41.9663 74.0225V66.0425H33.9863C31.8706 66.0467 29.8426 66.8886 28.346 68.384C26.8494 69.8795 26.0059 71.9068 26 74.0225C26.0042 76.1396 26.8468 78.1689 28.3436 79.6662C29.8403 81.1635 31.8692 82.007 33.9863 82.012C36.1023 82.0053 38.1296 81.1611 39.625 79.664ZM48.336 68.3851C46.8401 69.8805 45.997 71.9073 45.9912 74.0225V94.0137C45.9962 96.1297 46.8388 98.1577 48.3347 99.6542C49.8307 101.151 51.8583 101.994 53.9743 102C56.0903 101.994 58.118 101.151 59.6139 99.6542C61.1099 98.1577 61.9525 96.1297 61.9575 94.0137V74.0225C61.9516 71.9073 61.1086 69.8805 59.6126 68.3851C58.1167 66.8898 56.0895 66.0475 53.9743 66.0425C51.8592 66.0475 49.832 66.8898 48.336 68.3851ZM48.337 39.6228C49.8327 41.118 51.8594 41.9605 53.9743 41.9663H61.9575V33.9863C61.9525 31.8703 61.1099 29.8424 59.6139 28.3458C58.118 26.8492 56.0903 26.0059 53.9743 26C51.8583 26.0059 49.8307 26.8492 48.3347 28.3458C46.8388 29.8424 45.9962 31.8703 45.9912 33.9863C45.9979 36.1013 46.8412 38.1276 48.337 39.6228ZM59.6128 48.3358C58.117 46.84 56.0898 45.997 53.9743 45.9912H33.9863C31.8703 45.9962 29.8424 46.8388 28.3458 48.3347C26.8492 49.8307 26.0059 51.8583 26 53.9743C26.0059 56.0903 26.8492 58.118 28.3458 59.6139C29.8424 61.1099 31.8703 61.9525 33.9863 61.9575H53.9743C56.0898 61.9516 58.117 61.1087 59.6128 59.6128C61.1087 58.117 61.9516 56.0898 61.9575 53.9743C61.9516 51.8589 61.1087 49.8317 59.6128 48.3358ZM88.3731 48.3347C86.8766 49.8307 86.0332 51.8583 86.0273 53.9743V61.9575H94.0137C96.1297 61.9525 98.1577 61.1099 99.6542 59.6139C101.151 58.118 101.994 56.0903 102 53.9743C101.994 51.8583 101.151 49.8307 99.6542 48.3347C98.1577 46.8388 96.1297 45.9962 94.0137 45.9912C91.8977 45.9962 89.8697 46.8388 88.3731 48.3347ZM79.6651 59.6151C81.1623 58.119 82.0061 56.0909 82.012 53.9743V33.9863C82.007 31.8692 81.1635 29.8403 79.6662 28.3436C78.1689 26.8468 76.1396 26.0042 74.0225 26C71.9068 26.0059 69.8795 26.8494 68.384 28.346C66.8886 29.8426 66.0467 31.8706 66.0425 33.9863V53.9743C66.0475 56.0895 66.8898 58.1167 68.3851 59.6126C69.8805 61.1086 71.9073 61.9516 74.0225 61.9575C76.1391 61.9533 78.1678 61.1111 79.6651 59.6151ZM79.6662 88.3709C78.1689 86.8742 76.1396 86.0315 74.0225 86.0273H66.0425V94.0137C66.0467 96.1294 66.8886 98.1574 68.384 99.654C69.8795 101.151 71.9068 101.994 74.0225 102C76.1396 101.996 78.1689 101.153 79.6662 99.6564C81.1635 98.1597 82.007 96.1308 82.012 94.0137C82.007 91.8966 81.1635 89.8676 79.6662 88.3709ZM68.3829 79.6649C69.8785 81.1622 71.9062 82.0061 74.0225 82.012H94.0137C96.1308 82.007 98.1597 81.1635 99.6564 79.6662C101.153 78.1689 101.996 76.1396 102 74.0225C101.994 71.9068 101.151 69.8795 99.654 68.384C98.1574 66.8886 96.1294 66.0467 94.0137 66.0425H74.0225C71.9076 66.0475 69.8808 66.8899 68.3853 68.3853C66.8899 69.8808 66.0475 71.9076 66.0425 74.0225C66.0458 76.1388 66.8874 78.1676 68.3829 79.6649Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM39.625 79.664C41.1204 78.1668 41.9622 76.1385 41.9663 74.0225V66.0425H33.9863C31.8706 66.0467 29.8426 66.8886 28.346 68.384C26.8494 69.8795 26.0059 71.9068 26 74.0225C26.0042 76.1396 26.8468 78.1689 28.3436 79.6662C29.8403 81.1635 31.8692 82.007 33.9863 82.012C36.1023 82.0053 38.1296 81.1611 39.625 79.664ZM48.336 68.3851C46.8401 69.8805 45.997 71.9073 45.9912 74.0225V94.0137C45.9962 96.1297 46.8388 98.1577 48.3347 99.6542C49.8307 101.151 51.8583 101.994 53.9743 102C56.0903 101.994 58.118 101.151 59.6139 99.6542C61.1099 98.1577 61.9525 96.1297 61.9575 94.0137V74.0225C61.9516 71.9073 61.1086 69.8805 59.6126 68.3851C58.1167 66.8898 56.0895 66.0475 53.9743 66.0425C51.8592 66.0475 49.832 66.8898 48.336 68.3851ZM48.337 39.6228C49.8327 41.118 51.8594 41.9605 53.9743 41.9663H61.9575V33.9863C61.9525 31.8703 61.1099 29.8424 59.6139 28.3458C58.118 26.8492 56.0903 26.0059 53.9743 26C51.8583 26.0059 49.8307 26.8492 48.3347 28.3458C46.8388 29.8424 45.9962 31.8703 45.9912 33.9863C45.9979 36.1013 46.8412 38.1276 48.337 39.6228ZM59.6128 48.3358C58.117 46.84 56.0898 45.997 53.9743 45.9912H33.9863C31.8703 45.9962 29.8424 46.8388 28.3458 48.3347C26.8492 49.8307 26.0059 51.8583 26 53.9743C26.0059 56.0903 26.8492 58.118 28.3458 59.6139C29.8424 61.1099 31.8703 61.9525 33.9863 61.9575H53.9743C56.0898 61.9516 58.117 61.1087 59.6128 59.6128C61.1087 58.117 61.9516 56.0898 61.9575 53.9743C61.9516 51.8589 61.1087 49.8317 59.6128 48.3358ZM88.3731 48.3347C86.8766 49.8307 86.0332 51.8583 86.0273 53.9743V61.9575H94.0137C96.1297 61.9525 98.1577 61.1099 99.6542 59.6139C101.151 58.118 101.994 56.0903 102 53.9743C101.994 51.8583 101.151 49.8307 99.6542 48.3347C98.1577 46.8388 96.1297 45.9962 94.0137 45.9912C91.8977 45.9962 89.8697 46.8388 88.3731 48.3347ZM79.6651 59.6151C81.1623 58.119 82.0061 56.0909 82.012 53.9743V33.9863C82.007 31.8692 81.1635 29.8403 79.6662 28.3436C78.1689 26.8468 76.1396 26.0042 74.0225 26C71.9068 26.0059 69.8795 26.8494 68.384 28.346C66.8886 29.8426 66.0467 31.8706 66.0425 33.9863V53.9743C66.0475 56.0895 66.8898 58.1167 68.3851 59.6126C69.8805 61.1086 71.9073 61.9516 74.0225 61.9575C76.1391 61.9533 78.1678 61.1111 79.6651 59.6151ZM79.6662 88.3709C78.1689 86.8742 76.1396 86.0315 74.0225 86.0273H66.0425V94.0137C66.0467 96.1294 66.8886 98.1574 68.384 99.654C69.8795 101.151 71.9068 101.994 74.0225 102C76.1396 101.996 78.1689 101.153 79.6662 99.6564C81.1635 98.1597 82.007 96.1308 82.012 94.0137C82.007 91.8966 81.1635 89.8676 79.6662 88.3709ZM68.3829 79.6649C69.8785 81.1622 71.9062 82.0061 74.0225 82.012H94.0137C96.1308 82.007 98.1597 81.1635 99.6564 79.6662C101.153 78.1689 101.996 76.1396 102 74.0225C101.994 71.9068 101.151 69.8795 99.654 68.384C98.1574 66.8886 96.1294 66.0467 94.0137 66.0425H74.0225C71.9076 66.0475 69.8808 66.8899 68.3853 68.3853C66.8899 69.8808 66.0475 71.9076 66.0425 74.0225C66.0458 76.1388 66.8874 78.1676 68.3829 79.6649Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM39.625 79.664C41.1204 78.1668 41.9622 76.1385 41.9663 74.0225V66.0425H33.9863C31.8706 66.0467 29.8426 66.8886 28.346 68.384C26.8494 69.8795 26.0059 71.9068 26 74.0225C26.0042 76.1396 26.8468 78.1689 28.3436 79.6662C29.8403 81.1635 31.8692 82.007 33.9863 82.012C36.1023 82.0053 38.1296 81.1611 39.625 79.664ZM48.336 68.3851C46.8401 69.8805 45.997 71.9073 45.9912 74.0225V94.0137C45.9962 96.1297 46.8388 98.1577 48.3347 99.6542C49.8307 101.151 51.8583 101.994 53.9743 102C56.0903 101.994 58.118 101.151 59.6139 99.6542C61.1099 98.1577 61.9525 96.1297 61.9575 94.0137V74.0225C61.9516 71.9073 61.1086 69.8805 59.6126 68.3851C58.1167 66.8898 56.0895 66.0475 53.9743 66.0425C51.8592 66.0475 49.832 66.8898 48.336 68.3851ZM48.337 39.6228C49.8327 41.118 51.8594 41.9605 53.9743 41.9663H61.9575V33.9863C61.9525 31.8703 61.1099 29.8424 59.6139 28.3458C58.118 26.8492 56.0903 26.0059 53.9743 26C51.8583 26.0059 49.8307 26.8492 48.3347 28.3458C46.8388 29.8424 45.9962 31.8703 45.9912 33.9863C45.9979 36.1013 46.8412 38.1276 48.337 39.6228ZM59.6128 48.3358C58.117 46.84 56.0898 45.997 53.9743 45.9912H33.9863C31.8703 45.9962 29.8424 46.8388 28.3458 48.3347C26.8492 49.8307 26.0059 51.8583 26 53.9743C26.0059 56.0903 26.8492 58.118 28.3458 59.6139C29.8424 61.1099 31.8703 61.9525 33.9863 61.9575H53.9743C56.0898 61.9516 58.117 61.1087 59.6128 59.6128C61.1087 58.117 61.9516 56.0898 61.9575 53.9743C61.9516 51.8589 61.1087 49.8317 59.6128 48.3358ZM88.3731 48.3347C86.8766 49.8307 86.0332 51.8583 86.0273 53.9743V61.9575H94.0137C96.1297 61.9525 98.1577 61.1099 99.6542 59.6139C101.151 58.118 101.994 56.0903 102 53.9743C101.994 51.8583 101.151 49.8307 99.6542 48.3347C98.1577 46.8388 96.1297 45.9962 94.0137 45.9912C91.8977 45.9962 89.8697 46.8388 88.3731 48.3347ZM79.6651 59.6151C81.1623 58.119 82.0061 56.0909 82.012 53.9743V33.9863C82.007 31.8692 81.1635 29.8403 79.6662 28.3436C78.1689 26.8468 76.1396 26.0042 74.0225 26C71.9068 26.0059 69.8795 26.8494 68.384 28.346C66.8886 29.8426 66.0467 31.8706 66.0425 33.9863V53.9743C66.0475 56.0895 66.8898 58.1167 68.3851 59.6126C69.8805 61.1086 71.9073 61.9516 74.0225 61.9575C76.1391 61.9533 78.1678 61.1111 79.6651 59.6151ZM79.6662 88.3709C78.1689 86.8742 76.1396 86.0315 74.0225 86.0273H66.0425V94.0137C66.0467 96.1294 66.8886 98.1574 68.384 99.654C69.8795 101.151 71.9068 101.994 74.0225 102C76.1396 101.996 78.1689 101.153 79.6662 99.6564C81.1635 98.1597 82.007 96.1308 82.012 94.0137C82.007 91.8966 81.1635 89.8676 79.6662 88.3709ZM68.3829 79.6649C69.8785 81.1622 71.9062 82.0061 74.0225 82.012H94.0137C96.1308 82.007 98.1597 81.1635 99.6564 79.6662C101.153 78.1689 101.996 76.1396 102 74.0225C101.994 71.9068 101.151 69.8795 99.654 68.384C98.1574 66.8886 96.1294 66.0467 94.0137 66.0425H74.0225C71.9076 66.0475 69.8808 66.8899 68.3853 68.3853C66.8899 69.8808 66.0475 71.9076 66.0425 74.0225C66.0458 76.1388 66.8874 78.1676 68.3829 79.6649Z" />
                </svg>
                `
      }
    }
  },
  'Sound Cloud': {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#FF3300"/>
            <path d="M24.3083 64.8606C24.1213 64.8606 23.9637 65.0365 23.938 65.2431L23.0837 73.4817L23.938 81.533C23.9637 81.7548 24.1213 81.9078 24.3083 81.9078C24.4917 81.9078 24.6383 81.7548 24.6713 81.533L25.6063 73.4817L24.6163 65.2431C24.6163 65.0251 24.4513 64.8606 24.2863 64.8606H24.3083ZM20.99 68.0275C20.77 68.0275 20.6563 68.1691 20.6087 68.3871L20 73.4817L20.605 78.4846C20.605 78.695 20.77 78.8441 20.935 78.8441C21.1 78.8441 21.2613 78.672 21.3163 78.4464L22.0863 73.4014L21.3163 68.2991C21.3163 68.0658 21.155 67.9549 20.9863 67.9549L20.99 68.0275ZM27.6963 63.2542C27.4727 63.2542 27.2563 63.4263 27.2563 63.6519L26.4863 73.455L27.3113 82.8564C27.3113 83.0859 27.4763 83.3154 27.7477 83.3154C27.9713 83.3154 28.1327 83.082 28.1913 82.8564L29.1227 73.3938L28.1913 63.6481C28.1327 63.4186 27.9677 63.1891 27.7477 63.1891L27.6963 63.2542ZM31.2127 62.8487C30.9377 62.8487 30.7177 63.0782 30.6627 63.3651L29.955 73.4626L30.725 83.193C30.7837 83.4875 31 83.7208 31.2713 83.7208C31.5463 83.7208 31.7663 83.4875 31.8213 83.1471L32.7013 73.4626L31.8213 63.4301C31.8213 63.1432 31.6013 62.9137 31.3263 62.9137L31.2127 62.8487V62.8487ZM35.4477 64.2257C35.4293 63.8814 35.1727 63.6558 34.8647 63.6558C34.5347 63.6558 34.2853 63.8853 34.2633 64.2257L33.4677 73.52L34.201 83.323C34.201 83.6672 34.476 83.9235 34.784 83.9235C35.0553 83.9235 35.3267 83.6634 35.3267 83.3192L36.159 73.5162L35.3267 64.1683L35.4477 64.2257V64.2257ZM38.414 57.689C38.0437 57.689 37.754 58.0333 37.754 58.3813L36.984 73.5162L37.6697 83.3192C37.6697 83.6634 37.963 83.9465 38.3297 83.9465C38.6743 83.9465 38.9677 83.6022 38.9897 83.258L39.756 73.455L38.9897 58.2628C38.9603 57.865 38.667 57.5743 38.3297 57.5743L38.414 57.689ZM41.846 54.0784C41.461 54.0784 41.131 54.4226 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4767 54.4226 42.1503 54.0784 41.7653 54.0784H41.846ZM45.3917 52.3611C44.948 52.3611 44.618 52.7015 44.5667 53.1605L43.9617 73.3364L44.5667 82.9749C44.618 83.4301 44.948 83.8355 45.3917 83.8355C45.828 83.8355 46.2167 83.4339 46.2167 82.9749L46.9317 73.3364L46.213 53.1605C46.213 52.7015 45.828 52.2999 45.388 52.2999L45.3917 52.3611ZM49.953 52.472C49.953 51.9556 49.568 51.554 49.073 51.554C48.6367 51.554 48.193 51.9556 48.193 52.472L47.6467 73.2828L48.193 82.8564C48.2517 83.3727 48.6367 83.7743 49.1317 83.7743C49.6267 83.7743 50.0117 83.3727 50.0117 82.8564L50.613 73.2828L50.0117 52.4146L49.953 52.472V52.472ZM52.6993 51.9595C52.2043 51.9595 51.7643 52.4146 51.7643 52.931L51.2143 73.2867L51.7643 82.7455C51.7643 83.3192 52.2043 83.7208 52.6993 83.7208C53.1943 83.7208 53.6343 83.2618 53.6343 82.6881L54.1843 73.2255L53.5793 52.9271C53.5793 52.3611 53.1393 51.8944 52.5857 51.8944L52.6993 51.9595ZM56.2707 52.5294C55.6693 52.5294 55.2293 53.0457 55.2293 53.6194L54.8517 73.2905L55.3467 82.7531C55.3467 83.323 55.783 83.8126 56.388 83.8126C56.9343 83.8126 57.3817 83.3536 57.4293 82.7225L57.873 73.3785L57.378 53.826C57.334 53.1987 56.883 52.7359 56.333 52.7359L56.2707 52.5294ZM60.6743 49.1214C60.5093 49.0105 60.2893 48.9531 60.0693 48.9531C59.8493 48.9531 59.633 49.0105 59.4643 49.1214C59.1343 49.328 58.918 49.6952 58.918 50.0968V50.3301L58.5367 73.4626L58.9583 82.8296V82.8602C58.9877 83.0897 59.0683 83.3766 59.2297 83.5487C59.4423 83.782 59.7503 83.9465 60.0877 83.9465C60.381 83.9465 60.667 83.7782 60.854 83.6022C61.0667 83.3727 61.1877 83.0859 61.1877 82.7416L61.2427 81.8237L61.6717 73.3976L61.1767 50.1197C61.1767 49.7219 60.953 49.3815 60.6817 49.2056L60.6743 49.1214V49.1214ZM64.363 47.0293C64.198 46.8571 64.033 46.7959 63.813 46.7959C63.5417 46.7959 63.2667 46.8571 63.0467 47.0293C62.7717 47.2626 62.6103 47.603 62.6103 47.9472V48.0581L62.108 73.3364L62.3867 77.9836L62.6103 82.516C62.6103 83.1432 63.153 83.717 63.813 83.717C64.4767 83.717 65.023 83.1432 65.023 82.4586L65.573 73.2255L65.023 47.8401C65.023 47.3811 64.7517 46.9948 64.418 46.7806L64.363 47.0293ZM97.176 61.227C95.691 61.227 94.261 61.5559 92.9997 62.1144C92.1197 51.9633 83.9797 44 73.977 44C71.5607 44 69.192 44.5164 67.0507 45.3731C66.2257 45.7173 66.0607 46.0616 66.0057 46.7462V82.5772C66.0643 83.2656 66.5557 83.8394 67.2157 83.8967H97.2273C103.164 83.9579 108 78.9704 108 72.7206C108 66.4708 103.164 61.4297 97.2273 61.4297" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#FF3300"/>
            <path d="M24.3083 64.8606C24.1213 64.8606 23.9637 65.0365 23.938 65.2431L23.0837 73.4817L23.938 81.533C23.9637 81.7548 24.1213 81.9078 24.3083 81.9078C24.4917 81.9078 24.6383 81.7548 24.6713 81.533L25.6063 73.4817L24.6163 65.2431C24.6163 65.0251 24.4513 64.8606 24.2863 64.8606H24.3083ZM20.99 68.0275C20.77 68.0275 20.6563 68.1691 20.6087 68.3871L20 73.4817L20.605 78.4846C20.605 78.695 20.77 78.8441 20.935 78.8441C21.1 78.8441 21.2613 78.672 21.3163 78.4464L22.0863 73.4014L21.3163 68.2991C21.3163 68.0658 21.155 67.9549 20.9863 67.9549L20.99 68.0275ZM27.6963 63.2542C27.4727 63.2542 27.2563 63.4263 27.2563 63.6519L26.4863 73.455L27.3113 82.8564C27.3113 83.0859 27.4763 83.3154 27.7477 83.3154C27.9713 83.3154 28.1327 83.082 28.1913 82.8564L29.1227 73.3938L28.1913 63.6481C28.1327 63.4186 27.9677 63.1891 27.7477 63.1891L27.6963 63.2542ZM31.2127 62.8487C30.9377 62.8487 30.7177 63.0782 30.6627 63.3651L29.955 73.4626L30.725 83.193C30.7837 83.4875 31 83.7208 31.2713 83.7208C31.5463 83.7208 31.7663 83.4875 31.8213 83.1471L32.7013 73.4626L31.8213 63.4301C31.8213 63.1432 31.6013 62.9137 31.3263 62.9137L31.2127 62.8487V62.8487ZM35.4477 64.2257C35.4293 63.8814 35.1727 63.6558 34.8647 63.6558C34.5347 63.6558 34.2853 63.8853 34.2633 64.2257L33.4677 73.52L34.201 83.323C34.201 83.6672 34.476 83.9235 34.784 83.9235C35.0553 83.9235 35.3267 83.6634 35.3267 83.3192L36.159 73.5162L35.3267 64.1683L35.4477 64.2257V64.2257ZM38.414 57.689C38.0437 57.689 37.754 58.0333 37.754 58.3813L36.984 73.5162L37.6697 83.3192C37.6697 83.6634 37.963 83.9465 38.3297 83.9465C38.6743 83.9465 38.9677 83.6022 38.9897 83.258L39.756 73.455L38.9897 58.2628C38.9603 57.865 38.667 57.5743 38.3297 57.5743L38.414 57.689ZM41.846 54.0784C41.461 54.0784 41.131 54.4226 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4767 54.4226 42.1503 54.0784 41.7653 54.0784H41.846ZM45.3917 52.3611C44.948 52.3611 44.618 52.7015 44.5667 53.1605L43.9617 73.3364L44.5667 82.9749C44.618 83.4301 44.948 83.8355 45.3917 83.8355C45.828 83.8355 46.2167 83.4339 46.2167 82.9749L46.9317 73.3364L46.213 53.1605C46.213 52.7015 45.828 52.2999 45.388 52.2999L45.3917 52.3611ZM49.953 52.472C49.953 51.9556 49.568 51.554 49.073 51.554C48.6367 51.554 48.193 51.9556 48.193 52.472L47.6467 73.2828L48.193 82.8564C48.2517 83.3727 48.6367 83.7743 49.1317 83.7743C49.6267 83.7743 50.0117 83.3727 50.0117 82.8564L50.613 73.2828L50.0117 52.4146L49.953 52.472V52.472ZM52.6993 51.9595C52.2043 51.9595 51.7643 52.4146 51.7643 52.931L51.2143 73.2867L51.7643 82.7455C51.7643 83.3192 52.2043 83.7208 52.6993 83.7208C53.1943 83.7208 53.6343 83.2618 53.6343 82.6881L54.1843 73.2255L53.5793 52.9271C53.5793 52.3611 53.1393 51.8944 52.5857 51.8944L52.6993 51.9595ZM56.2707 52.5294C55.6693 52.5294 55.2293 53.0457 55.2293 53.6194L54.8517 73.2905L55.3467 82.7531C55.3467 83.323 55.783 83.8126 56.388 83.8126C56.9343 83.8126 57.3817 83.3536 57.4293 82.7225L57.873 73.3785L57.378 53.826C57.334 53.1987 56.883 52.7359 56.333 52.7359L56.2707 52.5294ZM60.6743 49.1214C60.5093 49.0105 60.2893 48.9531 60.0693 48.9531C59.8493 48.9531 59.633 49.0105 59.4643 49.1214C59.1343 49.328 58.918 49.6952 58.918 50.0968V50.3301L58.5367 73.4626L58.9583 82.8296V82.8602C58.9877 83.0897 59.0683 83.3766 59.2297 83.5487C59.4423 83.782 59.7503 83.9465 60.0877 83.9465C60.381 83.9465 60.667 83.7782 60.854 83.6022C61.0667 83.3727 61.1877 83.0859 61.1877 82.7416L61.2427 81.8237L61.6717 73.3976L61.1767 50.1197C61.1767 49.7219 60.953 49.3815 60.6817 49.2056L60.6743 49.1214V49.1214ZM64.363 47.0293C64.198 46.8571 64.033 46.7959 63.813 46.7959C63.5417 46.7959 63.2667 46.8571 63.0467 47.0293C62.7717 47.2626 62.6103 47.603 62.6103 47.9472V48.0581L62.108 73.3364L62.3867 77.9836L62.6103 82.516C62.6103 83.1432 63.153 83.717 63.813 83.717C64.4767 83.717 65.023 83.1432 65.023 82.4586L65.573 73.2255L65.023 47.8401C65.023 47.3811 64.7517 46.9948 64.418 46.7806L64.363 47.0293ZM97.176 61.227C95.691 61.227 94.261 61.5559 92.9997 62.1144C92.1197 51.9633 83.9797 44 73.977 44C71.5607 44 69.192 44.5164 67.0507 45.3731C66.2257 45.7173 66.0607 46.0616 66.0057 46.7462V82.5772C66.0643 83.2656 66.5557 83.8394 67.2157 83.8967H97.2273C103.164 83.9579 108 78.9704 108 72.7206C108 66.4708 103.164 61.4297 97.2273 61.4297" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM23.938 65.2431C23.9632 65.0407 24.1149 64.8678 24.2968 64.8608C24.4582 64.8676 24.6163 65.0297 24.6163 65.2431L25.6063 73.4817L24.6713 81.533C24.6383 81.7548 24.4917 81.9078 24.3083 81.9078C24.1213 81.9078 23.9637 81.7548 23.938 81.533L23.0837 73.4817L23.938 65.2431ZM24.2968 64.8608C24.3006 64.8607 24.3045 64.8606 24.3083 64.8606H24.2863C24.2898 64.8606 24.2933 64.8607 24.2968 64.8608ZM20.6087 68.3871C20.6563 68.1691 20.77 68.0275 20.99 68.0275L20.9863 67.9549C21.155 67.9549 21.3163 68.0658 21.3163 68.2991L22.0863 73.4014L21.3163 78.4464C21.2613 78.672 21.1 78.8441 20.935 78.8441C20.77 78.8441 20.605 78.695 20.605 78.4846L20 73.4817L20.6087 68.3871ZM27.2563 63.6519C27.2563 63.4263 27.4727 63.2542 27.6963 63.2542L27.7477 63.1891C27.9677 63.1891 28.1327 63.4186 28.1913 63.6481L29.1227 73.3938L28.1913 82.8564C28.1327 83.082 27.9713 83.3154 27.7477 83.3154C27.4763 83.3154 27.3113 83.0859 27.3113 82.8564L26.4863 73.455L27.2563 63.6519ZM30.6627 63.3651C30.7177 63.0782 30.9377 62.8487 31.2127 62.8487L31.3263 62.9137C31.6013 62.9137 31.8213 63.1432 31.8213 63.4301L32.7013 73.4626L31.8213 83.1471C31.7663 83.4875 31.5463 83.7208 31.2713 83.7208C31 83.7208 30.7837 83.4875 30.725 83.193L29.955 73.4626L30.6627 63.3651ZM34.8647 63.6558C35.1727 63.6558 35.4293 63.8814 35.4477 64.2257L35.3267 64.1683L36.159 73.5162L35.3267 83.3192C35.3267 83.6634 35.0553 83.9235 34.784 83.9235C34.476 83.9235 34.201 83.6672 34.201 83.323L33.4677 73.52L34.2633 64.2257C34.2853 63.8853 34.5347 63.6558 34.8647 63.6558ZM37.754 58.3813C37.754 58.0333 38.0437 57.689 38.414 57.689L38.3297 57.5743C38.667 57.5743 38.9603 57.865 38.9897 58.2628L39.756 73.455L38.9897 83.258C38.9677 83.6022 38.6743 83.9465 38.3297 83.9465C37.963 83.9465 37.6697 83.6634 37.6697 83.3192L36.984 73.5162L37.754 58.3813ZM41.8056 54.0797C41.819 54.0788 41.8325 54.0784 41.846 54.0784H41.7653C41.7788 54.0784 41.7923 54.0788 41.8056 54.0797ZM41.8056 54.0797C41.4382 54.1027 41.13 54.4366 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4788 54.4387 42.1729 54.1027 41.8056 54.0797ZM44.5667 53.1605C44.618 52.7015 44.948 52.3611 45.3917 52.3611L45.388 52.2999C45.828 52.2999 46.213 52.7015 46.213 53.1605L46.9317 73.3364L46.2167 82.9749C46.2167 83.4339 45.828 83.8355 45.3917 83.8355C44.948 83.8355 44.618 83.4301 44.5667 82.9749L43.9617 73.3364L44.5667 53.1605ZM49.073 51.554C49.568 51.554 49.953 51.9556 49.953 52.472L50.0117 52.4146L50.613 73.2828L50.0117 82.8564C50.0117 83.3727 49.6267 83.7743 49.1317 83.7743C48.6367 83.7743 48.2517 83.3727 48.193 82.8564L47.6467 73.2828L48.193 52.472C48.193 51.9556 48.6367 51.554 49.073 51.554ZM51.7643 52.931C51.7643 52.4146 52.2043 51.9595 52.6993 51.9595L52.5857 51.8944C53.1393 51.8944 53.5793 52.3611 53.5793 52.9271L54.1843 73.2255L53.6343 82.6881C53.6343 83.2618 53.1943 83.7208 52.6993 83.7208C52.2043 83.7208 51.7643 83.3192 51.7643 82.7455L51.2143 73.2867L51.7643 52.931ZM55.2293 53.6194C55.2293 53.0457 55.6693 52.5294 56.2707 52.5294L56.333 52.7359C56.883 52.7359 57.334 53.1987 57.378 53.826L57.873 73.3785L57.4293 82.7225C57.3817 83.3536 56.9343 83.8126 56.388 83.8126C55.783 83.8126 55.3467 83.323 55.3467 82.7531L54.8517 73.2905L55.2293 53.6194ZM60.0693 48.9531C60.2893 48.9531 60.5093 49.0105 60.6743 49.1214L60.6817 49.2056C60.953 49.3815 61.1767 49.7219 61.1767 50.1197L61.6717 73.3976L61.2427 81.8237L61.1877 82.7416C61.1877 83.0859 61.0667 83.3727 60.854 83.6022C60.667 83.7782 60.381 83.9465 60.0877 83.9465C59.7503 83.9465 59.4423 83.782 59.2297 83.5487C59.0683 83.3766 58.9877 83.0897 58.9583 82.8602V82.8296L58.5367 73.4626L58.918 50.3301V50.0968C58.918 49.6952 59.1343 49.328 59.4643 49.1214C59.633 49.0105 59.8493 48.9531 60.0693 48.9531ZM63.813 46.7959C64.033 46.7959 64.198 46.8571 64.363 47.0293L64.418 46.7806C64.7517 46.9948 65.023 47.3811 65.023 47.8401L65.573 73.2255L65.023 82.4586C65.023 83.1432 64.4767 83.717 63.813 83.717C63.153 83.717 62.6103 83.1432 62.6103 82.516L62.3867 77.9836L62.108 73.3364L62.6103 48.0581V47.9472C62.6103 47.603 62.7717 47.2626 63.0467 47.0293C63.2667 46.8571 63.5417 46.7959 63.813 46.7959ZM92.9997 62.1144C94.261 61.5559 95.691 61.227 97.176 61.227L97.2273 61.4297C103.164 61.4297 108 66.4708 108 72.7206C108 78.9704 103.164 83.9579 97.2273 83.8967H67.2157C66.5557 83.8394 66.0643 83.2656 66.0057 82.5772V46.7462C66.0607 46.0616 66.2257 45.7173 67.0507 45.3731C69.192 44.5164 71.5607 44 73.977 44C83.9797 44 92.1197 51.9633 92.9997 62.1144Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM23.938 65.2431C23.9632 65.0407 24.1149 64.8678 24.2968 64.8608C24.4582 64.8676 24.6163 65.0297 24.6163 65.2431L25.6063 73.4817L24.6713 81.533C24.6383 81.7548 24.4917 81.9078 24.3083 81.9078C24.1213 81.9078 23.9637 81.7548 23.938 81.533L23.0837 73.4817L23.938 65.2431ZM24.2968 64.8608C24.3006 64.8607 24.3045 64.8606 24.3083 64.8606H24.2863C24.2898 64.8606 24.2933 64.8607 24.2968 64.8608ZM20.6087 68.3871C20.6563 68.1691 20.77 68.0275 20.99 68.0275L20.9863 67.9549C21.155 67.9549 21.3163 68.0658 21.3163 68.2991L22.0863 73.4014L21.3163 78.4464C21.2613 78.672 21.1 78.8441 20.935 78.8441C20.77 78.8441 20.605 78.695 20.605 78.4846L20 73.4817L20.6087 68.3871ZM27.2563 63.6519C27.2563 63.4263 27.4727 63.2542 27.6963 63.2542L27.7477 63.1891C27.9677 63.1891 28.1327 63.4186 28.1913 63.6481L29.1227 73.3938L28.1913 82.8564C28.1327 83.082 27.9713 83.3154 27.7477 83.3154C27.4763 83.3154 27.3113 83.0859 27.3113 82.8564L26.4863 73.455L27.2563 63.6519ZM30.6627 63.3651C30.7177 63.0782 30.9377 62.8487 31.2127 62.8487L31.3263 62.9137C31.6013 62.9137 31.8213 63.1432 31.8213 63.4301L32.7013 73.4626L31.8213 83.1471C31.7663 83.4875 31.5463 83.7208 31.2713 83.7208C31 83.7208 30.7837 83.4875 30.725 83.193L29.955 73.4626L30.6627 63.3651ZM34.8647 63.6558C35.1727 63.6558 35.4293 63.8814 35.4477 64.2257L35.3267 64.1683L36.159 73.5162L35.3267 83.3192C35.3267 83.6634 35.0553 83.9235 34.784 83.9235C34.476 83.9235 34.201 83.6672 34.201 83.323L33.4677 73.52L34.2633 64.2257C34.2853 63.8853 34.5347 63.6558 34.8647 63.6558ZM37.754 58.3813C37.754 58.0333 38.0437 57.689 38.414 57.689L38.3297 57.5743C38.667 57.5743 38.9603 57.865 38.9897 58.2628L39.756 73.455L38.9897 83.258C38.9677 83.6022 38.6743 83.9465 38.3297 83.9465C37.963 83.9465 37.6697 83.6634 37.6697 83.3192L36.984 73.5162L37.754 58.3813ZM41.8056 54.0797C41.819 54.0788 41.8325 54.0784 41.846 54.0784H41.7653C41.7788 54.0784 41.7923 54.0788 41.8056 54.0797ZM41.8056 54.0797C41.4382 54.1027 41.13 54.4366 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4788 54.4387 42.1729 54.1027 41.8056 54.0797ZM44.5667 53.1605C44.618 52.7015 44.948 52.3611 45.3917 52.3611L45.388 52.2999C45.828 52.2999 46.213 52.7015 46.213 53.1605L46.9317 73.3364L46.2167 82.9749C46.2167 83.4339 45.828 83.8355 45.3917 83.8355C44.948 83.8355 44.618 83.4301 44.5667 82.9749L43.9617 73.3364L44.5667 53.1605ZM49.073 51.554C49.568 51.554 49.953 51.9556 49.953 52.472L50.0117 52.4146L50.613 73.2828L50.0117 82.8564C50.0117 83.3727 49.6267 83.7743 49.1317 83.7743C48.6367 83.7743 48.2517 83.3727 48.193 82.8564L47.6467 73.2828L48.193 52.472C48.193 51.9556 48.6367 51.554 49.073 51.554ZM51.7643 52.931C51.7643 52.4146 52.2043 51.9595 52.6993 51.9595L52.5857 51.8944C53.1393 51.8944 53.5793 52.3611 53.5793 52.9271L54.1843 73.2255L53.6343 82.6881C53.6343 83.2618 53.1943 83.7208 52.6993 83.7208C52.2043 83.7208 51.7643 83.3192 51.7643 82.7455L51.2143 73.2867L51.7643 52.931ZM55.2293 53.6194C55.2293 53.0457 55.6693 52.5294 56.2707 52.5294L56.333 52.7359C56.883 52.7359 57.334 53.1987 57.378 53.826L57.873 73.3785L57.4293 82.7225C57.3817 83.3536 56.9343 83.8126 56.388 83.8126C55.783 83.8126 55.3467 83.323 55.3467 82.7531L54.8517 73.2905L55.2293 53.6194ZM60.0693 48.9531C60.2893 48.9531 60.5093 49.0105 60.6743 49.1214L60.6817 49.2056C60.953 49.3815 61.1767 49.7219 61.1767 50.1197L61.6717 73.3976L61.2427 81.8237L61.1877 82.7416C61.1877 83.0859 61.0667 83.3727 60.854 83.6022C60.667 83.7782 60.381 83.9465 60.0877 83.9465C59.7503 83.9465 59.4423 83.782 59.2297 83.5487C59.0683 83.3766 58.9877 83.0897 58.9583 82.8602V82.8296L58.5367 73.4626L58.918 50.3301V50.0968C58.918 49.6952 59.1343 49.328 59.4643 49.1214C59.633 49.0105 59.8493 48.9531 60.0693 48.9531ZM63.813 46.7959C64.033 46.7959 64.198 46.8571 64.363 47.0293L64.418 46.7806C64.7517 46.9948 65.023 47.3811 65.023 47.8401L65.573 73.2255L65.023 82.4586C65.023 83.1432 64.4767 83.717 63.813 83.717C63.153 83.717 62.6103 83.1432 62.6103 82.516L62.3867 77.9836L62.108 73.3364L62.6103 48.0581V47.9472C62.6103 47.603 62.7717 47.2626 63.0467 47.0293C63.2667 46.8571 63.5417 46.7959 63.813 46.7959ZM92.9997 62.1144C94.261 61.5559 95.691 61.227 97.176 61.227L97.2273 61.4297C103.164 61.4297 108 66.4708 108 72.7206C108 78.9704 103.164 83.9579 97.2273 83.8967H67.2157C66.5557 83.8394 66.0643 83.2656 66.0057 82.5772V46.7462C66.0607 46.0616 66.2257 45.7173 67.0507 45.3731C69.192 44.5164 71.5607 44 73.977 44C83.9797 44 92.1197 51.9633 92.9997 62.1144Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM23.938 65.2431C23.9632 65.0407 24.1149 64.8678 24.2968 64.8608C24.4582 64.8676 24.6163 65.0297 24.6163 65.2431L25.6063 73.4817L24.6713 81.533C24.6383 81.7548 24.4917 81.9078 24.3083 81.9078C24.1213 81.9078 23.9637 81.7548 23.938 81.533L23.0837 73.4817L23.938 65.2431ZM24.2968 64.8608C24.3006 64.8607 24.3045 64.8606 24.3083 64.8606H24.2863C24.2898 64.8606 24.2933 64.8607 24.2968 64.8608ZM20.6087 68.3871C20.6563 68.1691 20.77 68.0275 20.99 68.0275L20.9863 67.9549C21.155 67.9549 21.3163 68.0658 21.3163 68.2991L22.0863 73.4014L21.3163 78.4464C21.2613 78.672 21.1 78.8441 20.935 78.8441C20.77 78.8441 20.605 78.695 20.605 78.4846L20 73.4817L20.6087 68.3871ZM27.2563 63.6519C27.2563 63.4263 27.4727 63.2542 27.6963 63.2542L27.7477 63.1891C27.9677 63.1891 28.1327 63.4186 28.1913 63.6481L29.1227 73.3938L28.1913 82.8564C28.1327 83.082 27.9713 83.3154 27.7477 83.3154C27.4763 83.3154 27.3113 83.0859 27.3113 82.8564L26.4863 73.455L27.2563 63.6519ZM30.6627 63.3651C30.7177 63.0782 30.9377 62.8487 31.2127 62.8487L31.3263 62.9137C31.6013 62.9137 31.8213 63.1432 31.8213 63.4301L32.7013 73.4626L31.8213 83.1471C31.7663 83.4875 31.5463 83.7208 31.2713 83.7208C31 83.7208 30.7837 83.4875 30.725 83.193L29.955 73.4626L30.6627 63.3651ZM34.8647 63.6558C35.1727 63.6558 35.4293 63.8814 35.4477 64.2257L35.3267 64.1683L36.159 73.5162L35.3267 83.3192C35.3267 83.6634 35.0553 83.9235 34.784 83.9235C34.476 83.9235 34.201 83.6672 34.201 83.323L33.4677 73.52L34.2633 64.2257C34.2853 63.8853 34.5347 63.6558 34.8647 63.6558ZM37.754 58.3813C37.754 58.0333 38.0437 57.689 38.414 57.689L38.3297 57.5743C38.667 57.5743 38.9603 57.865 38.9897 58.2628L39.756 73.455L38.9897 83.258C38.9677 83.6022 38.6743 83.9465 38.3297 83.9465C37.963 83.9465 37.6697 83.6634 37.6697 83.3192L36.984 73.5162L37.754 58.3813ZM41.8056 54.0797C41.819 54.0788 41.8325 54.0784 41.846 54.0784H41.7653C41.7788 54.0784 41.7923 54.0788 41.8056 54.0797ZM41.8056 54.0797C41.4382 54.1027 41.13 54.4366 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4788 54.4387 42.1729 54.1027 41.8056 54.0797ZM44.5667 53.1605C44.618 52.7015 44.948 52.3611 45.3917 52.3611L45.388 52.2999C45.828 52.2999 46.213 52.7015 46.213 53.1605L46.9317 73.3364L46.2167 82.9749C46.2167 83.4339 45.828 83.8355 45.3917 83.8355C44.948 83.8355 44.618 83.4301 44.5667 82.9749L43.9617 73.3364L44.5667 53.1605ZM49.073 51.554C49.568 51.554 49.953 51.9556 49.953 52.472L50.0117 52.4146L50.613 73.2828L50.0117 82.8564C50.0117 83.3727 49.6267 83.7743 49.1317 83.7743C48.6367 83.7743 48.2517 83.3727 48.193 82.8564L47.6467 73.2828L48.193 52.472C48.193 51.9556 48.6367 51.554 49.073 51.554ZM51.7643 52.931C51.7643 52.4146 52.2043 51.9595 52.6993 51.9595L52.5857 51.8944C53.1393 51.8944 53.5793 52.3611 53.5793 52.9271L54.1843 73.2255L53.6343 82.6881C53.6343 83.2618 53.1943 83.7208 52.6993 83.7208C52.2043 83.7208 51.7643 83.3192 51.7643 82.7455L51.2143 73.2867L51.7643 52.931ZM55.2293 53.6194C55.2293 53.0457 55.6693 52.5294 56.2707 52.5294L56.333 52.7359C56.883 52.7359 57.334 53.1987 57.378 53.826L57.873 73.3785L57.4293 82.7225C57.3817 83.3536 56.9343 83.8126 56.388 83.8126C55.783 83.8126 55.3467 83.323 55.3467 82.7531L54.8517 73.2905L55.2293 53.6194ZM60.0693 48.9531C60.2893 48.9531 60.5093 49.0105 60.6743 49.1214L60.6817 49.2056C60.953 49.3815 61.1767 49.7219 61.1767 50.1197L61.6717 73.3976L61.2427 81.8237L61.1877 82.7416C61.1877 83.0859 61.0667 83.3727 60.854 83.6022C60.667 83.7782 60.381 83.9465 60.0877 83.9465C59.7503 83.9465 59.4423 83.782 59.2297 83.5487C59.0683 83.3766 58.9877 83.0897 58.9583 82.8602V82.8296L58.5367 73.4626L58.918 50.3301V50.0968C58.918 49.6952 59.1343 49.328 59.4643 49.1214C59.633 49.0105 59.8493 48.9531 60.0693 48.9531ZM63.813 46.7959C64.033 46.7959 64.198 46.8571 64.363 47.0293L64.418 46.7806C64.7517 46.9948 65.023 47.3811 65.023 47.8401L65.573 73.2255L65.023 82.4586C65.023 83.1432 64.4767 83.717 63.813 83.717C63.153 83.717 62.6103 83.1432 62.6103 82.516L62.3867 77.9836L62.108 73.3364L62.6103 48.0581V47.9472C62.6103 47.603 62.7717 47.2626 63.0467 47.0293C63.2667 46.8571 63.5417 46.7959 63.813 46.7959ZM92.9997 62.1144C94.261 61.5559 95.691 61.227 97.176 61.227L97.2273 61.4297C103.164 61.4297 108 66.4708 108 72.7206C108 78.9704 103.164 83.9579 97.2273 83.8967H67.2157C66.5557 83.8394 66.0643 83.2656 66.0057 82.5772V46.7462C66.0607 46.0616 66.2257 45.7173 67.0507 45.3731C69.192 44.5164 71.5607 44 73.977 44C83.9797 44 92.1197 51.9633 92.9997 62.1144Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM23.938 65.2431C23.9632 65.0407 24.1149 64.8678 24.2968 64.8608C24.4582 64.8676 24.6163 65.0297 24.6163 65.2431L25.6063 73.4817L24.6713 81.533C24.6383 81.7548 24.4917 81.9078 24.3083 81.9078C24.1213 81.9078 23.9637 81.7548 23.938 81.533L23.0837 73.4817L23.938 65.2431ZM24.2968 64.8608C24.3006 64.8607 24.3045 64.8606 24.3083 64.8606H24.2863C24.2898 64.8606 24.2933 64.8607 24.2968 64.8608ZM20.6087 68.3871C20.6563 68.1691 20.77 68.0275 20.99 68.0275L20.9863 67.9549C21.155 67.9549 21.3163 68.0658 21.3163 68.2991L22.0863 73.4014L21.3163 78.4464C21.2613 78.672 21.1 78.8441 20.935 78.8441C20.77 78.8441 20.605 78.695 20.605 78.4846L20 73.4817L20.6087 68.3871ZM27.2563 63.6519C27.2563 63.4263 27.4727 63.2542 27.6963 63.2542L27.7477 63.1891C27.9677 63.1891 28.1327 63.4186 28.1913 63.6481L29.1227 73.3938L28.1913 82.8564C28.1327 83.082 27.9713 83.3154 27.7477 83.3154C27.4763 83.3154 27.3113 83.0859 27.3113 82.8564L26.4863 73.455L27.2563 63.6519ZM30.6627 63.3651C30.7177 63.0782 30.9377 62.8487 31.2127 62.8487L31.3263 62.9137C31.6013 62.9137 31.8213 63.1432 31.8213 63.4301L32.7013 73.4626L31.8213 83.1471C31.7663 83.4875 31.5463 83.7208 31.2713 83.7208C31 83.7208 30.7837 83.4875 30.725 83.193L29.955 73.4626L30.6627 63.3651ZM34.8647 63.6558C35.1727 63.6558 35.4293 63.8814 35.4477 64.2257L35.3267 64.1683L36.159 73.5162L35.3267 83.3192C35.3267 83.6634 35.0553 83.9235 34.784 83.9235C34.476 83.9235 34.201 83.6672 34.201 83.323L33.4677 73.52L34.2633 64.2257C34.2853 63.8853 34.5347 63.6558 34.8647 63.6558ZM37.754 58.3813C37.754 58.0333 38.0437 57.689 38.414 57.689L38.3297 57.5743C38.667 57.5743 38.9603 57.865 38.9897 58.2628L39.756 73.455L38.9897 83.258C38.9677 83.6022 38.6743 83.9465 38.3297 83.9465C37.963 83.9465 37.6697 83.6634 37.6697 83.3192L36.984 73.5162L37.754 58.3813ZM41.8056 54.0797C41.819 54.0788 41.8325 54.0784 41.846 54.0784H41.7653C41.7788 54.0784 41.7923 54.0788 41.8056 54.0797ZM41.8056 54.0797C41.4382 54.1027 41.13 54.4366 41.1017 54.8204L40.4417 73.455L41.0467 83.2006C41.0467 83.6596 41.3767 84 41.7617 84C42.143 84 42.473 83.6596 42.5317 83.2006L43.2393 73.455L42.5353 54.8816C42.4788 54.4387 42.1729 54.1027 41.8056 54.0797ZM44.5667 53.1605C44.618 52.7015 44.948 52.3611 45.3917 52.3611L45.388 52.2999C45.828 52.2999 46.213 52.7015 46.213 53.1605L46.9317 73.3364L46.2167 82.9749C46.2167 83.4339 45.828 83.8355 45.3917 83.8355C44.948 83.8355 44.618 83.4301 44.5667 82.9749L43.9617 73.3364L44.5667 53.1605ZM49.073 51.554C49.568 51.554 49.953 51.9556 49.953 52.472L50.0117 52.4146L50.613 73.2828L50.0117 82.8564C50.0117 83.3727 49.6267 83.7743 49.1317 83.7743C48.6367 83.7743 48.2517 83.3727 48.193 82.8564L47.6467 73.2828L48.193 52.472C48.193 51.9556 48.6367 51.554 49.073 51.554ZM51.7643 52.931C51.7643 52.4146 52.2043 51.9595 52.6993 51.9595L52.5857 51.8944C53.1393 51.8944 53.5793 52.3611 53.5793 52.9271L54.1843 73.2255L53.6343 82.6881C53.6343 83.2618 53.1943 83.7208 52.6993 83.7208C52.2043 83.7208 51.7643 83.3192 51.7643 82.7455L51.2143 73.2867L51.7643 52.931ZM55.2293 53.6194C55.2293 53.0457 55.6693 52.5294 56.2707 52.5294L56.333 52.7359C56.883 52.7359 57.334 53.1987 57.378 53.826L57.873 73.3785L57.4293 82.7225C57.3817 83.3536 56.9343 83.8126 56.388 83.8126C55.783 83.8126 55.3467 83.323 55.3467 82.7531L54.8517 73.2905L55.2293 53.6194ZM60.0693 48.9531C60.2893 48.9531 60.5093 49.0105 60.6743 49.1214L60.6817 49.2056C60.953 49.3815 61.1767 49.7219 61.1767 50.1197L61.6717 73.3976L61.2427 81.8237L61.1877 82.7416C61.1877 83.0859 61.0667 83.3727 60.854 83.6022C60.667 83.7782 60.381 83.9465 60.0877 83.9465C59.7503 83.9465 59.4423 83.782 59.2297 83.5487C59.0683 83.3766 58.9877 83.0897 58.9583 82.8602V82.8296L58.5367 73.4626L58.918 50.3301V50.0968C58.918 49.6952 59.1343 49.328 59.4643 49.1214C59.633 49.0105 59.8493 48.9531 60.0693 48.9531ZM63.813 46.7959C64.033 46.7959 64.198 46.8571 64.363 47.0293L64.418 46.7806C64.7517 46.9948 65.023 47.3811 65.023 47.8401L65.573 73.2255L65.023 82.4586C65.023 83.1432 64.4767 83.717 63.813 83.717C63.153 83.717 62.6103 83.1432 62.6103 82.516L62.3867 77.9836L62.108 73.3364L62.6103 48.0581V47.9472C62.6103 47.603 62.7717 47.2626 63.0467 47.0293C63.2667 46.8571 63.5417 46.7959 63.813 46.7959ZM92.9997 62.1144C94.261 61.5559 95.691 61.227 97.176 61.227L97.2273 61.4297C103.164 61.4297 108 66.4708 108 72.7206C108 78.9704 103.164 83.9579 97.2273 83.8967H67.2157C66.5557 83.8394 66.0643 83.2656 66.0057 82.5772V46.7462C66.0607 46.0616 66.2257 45.7173 67.0507 45.3731C69.192 44.5164 71.5607 44 73.977 44C83.9797 44 92.1197 51.9633 92.9997 62.1144Z" />
                </svg>
                `
      }
    }
  },
  Twitch: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#9146FF"/>
            <path d="M62.6654 40.9277H68.0008V57.2138H62.6623L62.6654 40.9277ZM77.3339 40.9277H82.6661V57.2138H77.3339V40.9277ZM45.3339 26L32 39.5723V88.4277H48V102L61.3339 88.4277H71.9984L96 64V26H45.3339ZM90.6646 61.2862L80 72.1415H69.3323L59.9992 81.6415V72.1415H48V31.4277H90.6646V61.2862Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#9146FF"/>
            <path d="M62.6654 40.9277H68.0008V57.2138H62.6623L62.6654 40.9277ZM77.3339 40.9277H82.6661V57.2138H77.3339V40.9277ZM45.3339 26L32 39.5723V88.4277H48V102L61.3339 88.4277H71.9984L96 64V26H45.3339ZM90.6646 61.2862L80 72.1415H69.3323L59.9992 81.6415V72.1415H48V31.4277H90.6646V61.2862Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM68.0008 40.9277H62.6654L62.6623 57.2138H68.0008V40.9277ZM82.6661 40.9277H77.3339V57.2138H82.6661V40.9277ZM32 39.5723L45.3339 26H96V64L71.9984 88.4277H61.3339L48 102V88.4277H32V39.5723ZM80 72.1415L90.6646 61.2862V31.4277H48V72.1415H59.9992V81.6415L69.3323 72.1415H80Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM68.0008 40.9277H62.6654L62.6623 57.2138H68.0008V40.9277ZM82.6661 40.9277H77.3339V57.2138H82.6661V40.9277ZM32 39.5723L45.3339 26H96V64L71.9984 88.4277H61.3339L48 102V88.4277H32V39.5723ZM80 72.1415L90.6646 61.2862V31.4277H48V72.1415H59.9992V81.6415L69.3323 72.1415H80Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM68.0008 40.9277H62.6654L62.6623 57.2138H68.0008V40.9277ZM82.6661 40.9277H77.3339V57.2138H82.6661V40.9277ZM32 39.5723L45.3339 26H96V64L71.9984 88.4277H61.3339L48 102V88.4277H32V39.5723ZM80 72.1415L90.6646 61.2862V31.4277H48V72.1415H59.9992V81.6415L69.3323 72.1415H80Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM68.0008 40.9277H62.6654L62.6623 57.2138H68.0008V40.9277ZM82.6661 40.9277H77.3339V57.2138H82.6661V40.9277ZM32 39.5723L45.3339 26H96V64L71.9984 88.4277H61.3339L48 102V88.4277H32V39.5723ZM80 72.1415L90.6646 61.2862V31.4277H48V72.1415H59.9992V81.6415L69.3323 72.1415H80Z" />
                </svg>
                `
      }
    }
  },
  Twitter: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#1DA1F2"/>
            <path d="M49.363 96C79.1386 96 95.4298 71.3999 95.4298 50.05C95.4298 49.3501 95.4298 48.6499 95.3796 47.9499C98.5376 45.6499 101.295 42.7999 103.5 39.6C100.593 40.8499 97.4348 41.7501 94.1765 42.1C97.5348 40.1001 100.091 36.9501 101.294 33.2C98.1864 35.05 94.6776 36.4 91.0185 37.1C88.0608 33.95 83.8503 32 79.1884 32C70.2157 32 62.9974 39.25 62.9974 48.15C62.9974 49.4 63.1478 50.6499 63.3983 51.8499C49.9645 51.1498 37.984 44.7499 30.0139 34.9499C28.6101 37.3499 27.8084 40.0999 27.8084 43.0999C27.8084 48.6999 30.6654 53.6499 35.0264 56.5499C32.3699 56.4501 29.8633 55.7001 27.708 54.5001V54.7C27.708 62.55 33.2722 69.0498 40.691 70.55C39.3377 70.8999 37.884 71.0999 36.4303 71.0999C35.3775 71.0999 34.375 71 33.3724 70.85C35.4277 77.25 41.3929 81.9 48.5109 82.0501C42.9467 86.4001 35.9789 88.9501 28.4099 88.9501C27.0565 88.9501 25.8034 88.9 24.5 88.7502C31.6682 93.3499 40.1897 96 49.363 96Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#1DA1F2"/>
            <path d="M49.363 96C79.1386 96 95.4298 71.3999 95.4298 50.05C95.4298 49.3501 95.4298 48.6499 95.3796 47.9499C98.5376 45.6499 101.295 42.7999 103.5 39.6C100.593 40.8499 97.4348 41.7501 94.1765 42.1C97.5348 40.1001 100.091 36.9501 101.294 33.2C98.1864 35.05 94.6776 36.4 91.0185 37.1C88.0608 33.95 83.8503 32 79.1884 32C70.2157 32 62.9974 39.25 62.9974 48.15C62.9974 49.4 63.1478 50.6499 63.3983 51.8499C49.9645 51.1498 37.984 44.7499 30.0139 34.9499C28.6101 37.3499 27.8084 40.0999 27.8084 43.0999C27.8084 48.6999 30.6654 53.6499 35.0264 56.5499C32.3699 56.4501 29.8633 55.7001 27.708 54.5001V54.7C27.708 62.55 33.2722 69.0498 40.691 70.55C39.3377 70.8999 37.884 71.0999 36.4303 71.0999C35.3775 71.0999 34.375 71 33.3724 70.85C35.4277 77.25 41.3929 81.9 48.5109 82.0501C42.9467 86.4001 35.9789 88.9501 28.4099 88.9501C27.0565 88.9501 25.8034 88.9 24.5 88.7502C31.6682 93.3499 40.1897 96 49.363 96Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM95.4298 50.05C95.4298 71.3999 79.1386 96 49.363 96C40.1897 96 31.6682 93.3499 24.5 88.7502C25.8034 88.9 27.0565 88.9501 28.4099 88.9501C35.9789 88.9501 42.9467 86.4001 48.5109 82.0501C41.3929 81.9 35.4277 77.25 33.3724 70.85C34.375 71 35.3775 71.0999 36.4303 71.0999C37.884 71.0999 39.3377 70.8999 40.691 70.55C33.2722 69.0498 27.708 62.55 27.708 54.7V54.5001C29.8633 55.7001 32.3699 56.4501 35.0264 56.5499C30.6654 53.6499 27.8084 48.6999 27.8084 43.0999C27.8084 40.0999 28.6101 37.3499 30.0139 34.9499C37.984 44.7499 49.9645 51.1498 63.3983 51.8499C63.1478 50.6499 62.9974 49.4 62.9974 48.15C62.9974 39.25 70.2157 32 79.1884 32C83.8503 32 88.0608 33.95 91.0185 37.1C94.6776 36.4 98.1864 35.05 101.294 33.2C100.091 36.9501 97.5348 40.1001 94.1765 42.1C97.4348 41.7501 100.593 40.8499 103.5 39.6C101.295 42.7999 98.5376 45.6499 95.3796 47.9499C95.4298 48.6499 95.4298 49.3501 95.4298 50.05Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM95.4298 50.05C95.4298 71.3999 79.1386 96 49.363 96C40.1897 96 31.6682 93.3499 24.5 88.7502C25.8034 88.9 27.0565 88.9501 28.4099 88.9501C35.9789 88.9501 42.9467 86.4001 48.5109 82.0501C41.3929 81.9 35.4277 77.25 33.3724 70.85C34.375 71 35.3775 71.0999 36.4303 71.0999C37.884 71.0999 39.3377 70.8999 40.691 70.55C33.2722 69.0498 27.708 62.55 27.708 54.7V54.5001C29.8633 55.7001 32.3699 56.4501 35.0264 56.5499C30.6654 53.6499 27.8084 48.6999 27.8084 43.0999C27.8084 40.0999 28.6101 37.3499 30.0139 34.9499C37.984 44.7499 49.9645 51.1498 63.3983 51.8499C63.1478 50.6499 62.9974 49.4 62.9974 48.15C62.9974 39.25 70.2157 32 79.1884 32C83.8503 32 88.0608 33.95 91.0185 37.1C94.6776 36.4 98.1864 35.05 101.294 33.2C100.091 36.9501 97.5348 40.1001 94.1765 42.1C97.4348 41.7501 100.593 40.8499 103.5 39.6C101.295 42.7999 98.5376 45.6499 95.3796 47.9499C95.4298 48.6499 95.4298 49.3501 95.4298 50.05Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM95.032 50.05C95.032 71.3999 78.947 96 49.5483 96C40.4911 96 32.0775 93.3499 25 88.7502C26.2869 88.9 27.5241 88.9501 28.8604 88.9501C36.3336 88.9501 43.2132 86.4001 48.707 82.0501C41.6791 81.9 35.7894 77.25 33.7601 70.85C34.75 71 35.7398 71.0999 36.7793 71.0999C38.2146 71.0999 39.6499 70.8999 40.9861 70.55C33.6612 69.0498 28.1674 62.55 28.1674 54.7V54.5001C30.2954 55.7001 32.7703 56.4501 35.3932 56.5499C31.0874 53.6499 28.2665 48.6999 28.2665 43.0999C28.2665 40.0999 29.0581 37.3499 30.4441 34.9499C38.3133 44.7499 50.1422 51.1498 63.4059 51.8499C63.1586 50.6499 63.0101 49.4 63.0101 48.15C63.0101 39.25 70.137 32 78.9961 32C83.599 32 87.7562 33.95 90.6765 37.1C94.2893 36.4 97.7537 35.05 100.822 33.2C99.6341 36.9501 97.1103 40.1001 93.7945 42.1C97.0116 41.7501 100.13 40.8499 103 39.6C100.823 42.7999 98.1004 45.6499 94.9824 47.9499C95.032 48.6499 95.032 49.3501 95.032 50.05Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM95.032 50.05C95.032 71.3999 78.947 96 49.5483 96C40.4911 96 32.0775 93.3499 25 88.7502C26.2869 88.9 27.5241 88.9501 28.8604 88.9501C36.3336 88.9501 43.2132 86.4001 48.707 82.0501C41.6791 81.9 35.7894 77.25 33.7601 70.85C34.75 71 35.7398 71.0999 36.7793 71.0999C38.2146 71.0999 39.6499 70.8999 40.9861 70.55C33.6612 69.0498 28.1674 62.55 28.1674 54.7V54.5001C30.2954 55.7001 32.7703 56.4501 35.3932 56.5499C31.0874 53.6499 28.2665 48.6999 28.2665 43.0999C28.2665 40.0999 29.0581 37.3499 30.4441 34.9499C38.3133 44.7499 50.1422 51.1498 63.4059 51.8499C63.1586 50.6499 63.0101 49.4 63.0101 48.15C63.0101 39.25 70.137 32 78.9961 32C83.599 32 87.7562 33.95 90.6765 37.1C94.2893 36.4 97.7537 35.05 100.822 33.2C99.6341 36.9501 97.1103 40.1001 93.7945 42.1C97.0116 41.7501 100.13 40.8499 103 39.6C100.823 42.7999 98.1004 45.6499 94.9824 47.9499C95.032 48.6499 95.032 49.3501 95.032 50.05Z" />
                </svg>
                `
      }
    }
  },
  Whatsapp: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#25D366"/>
            <path d="M90.4643 37.1964C83.5089 30.0714 74.0089 26 63.8304 26C43.1339 26 26.1696 42.9643 26.1696 63.6607C26.1696 70.4464 28.0357 76.8929 31.2589 82.4911L26 102L45.8482 96.9107C51.4464 99.7946 57.5536 101.491 63.8304 101.491C84.6964 101.491 102 84.5268 102 63.8304C102 53.6518 97.5893 44.3214 90.4643 37.1964ZM63.8304 95.0446C58.2321 95.0446 52.8036 93.5179 47.8839 90.6339L46.8661 89.9554L34.9911 93.1786L38.2143 81.6429L37.3661 80.4554C34.3125 75.3661 32.6161 69.5982 32.6161 63.6607C32.6161 46.5268 46.6964 32.4464 64 32.4464C72.3125 32.4464 80.1161 35.6696 86.0536 41.6071C91.9911 47.5446 95.5536 55.3482 95.5536 63.8304C95.5536 80.9643 81.1339 95.0446 63.8304 95.0446ZM81.1339 71.6339C80.1161 71.125 75.5357 68.9196 74.6875 68.5804C73.8393 68.2411 73.1607 68.0714 72.4821 69.0893C71.9732 69.9375 70.1071 72.1429 69.5982 72.8214C68.9196 73.3304 68.4107 73.5 67.5625 72.9911C61.9643 70.2768 58.4018 68.0714 54.6696 61.7946C53.6518 60.0982 55.6875 60.2679 57.3839 56.7054C57.7232 56.0268 57.5536 55.5179 57.3839 55.0089C57.2143 54.5 55.1786 49.9196 54.5 48.0536C53.6518 46.1875 52.9732 46.3571 52.2946 46.3571C51.7857 46.3571 51.1071 46.3571 50.5982 46.3571C49.9196 46.3571 48.9018 46.5268 48.0536 47.5446C47.2054 48.5625 44.8304 50.7679 44.8304 55.3482C44.8304 60.0982 48.0536 64.5089 48.5625 65.1875C49.0714 65.6964 55.1786 75.1964 64.6786 79.2679C70.6161 81.9821 72.9911 82.1518 76.0446 81.6429C77.7411 81.4732 81.4732 79.4375 82.3214 77.2321C83.1696 75.0268 83.1696 73.1607 82.8304 72.8214C82.6607 72.3125 81.9821 72.1429 81.1339 71.6339Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#25D366"/>
            <path d="M90.4643 37.1964C83.5089 30.0714 74.0089 26 63.8304 26C43.1339 26 26.1696 42.9643 26.1696 63.6607C26.1696 70.4464 28.0357 76.8929 31.2589 82.4911L26 102L45.8482 96.9107C51.4464 99.7946 57.5536 101.491 63.8304 101.491C84.6964 101.491 102 84.5268 102 63.8304C102 53.6518 97.5893 44.3214 90.4643 37.1964ZM63.8304 95.0446C58.2321 95.0446 52.8036 93.5179 47.8839 90.6339L46.8661 89.9554L34.9911 93.1786L38.2143 81.6429L37.3661 80.4554C34.3125 75.3661 32.6161 69.5982 32.6161 63.6607C32.6161 46.5268 46.6964 32.4464 64 32.4464C72.3125 32.4464 80.1161 35.6696 86.0536 41.6071C91.9911 47.5446 95.5536 55.3482 95.5536 63.8304C95.5536 80.9643 81.1339 95.0446 63.8304 95.0446ZM81.1339 71.6339C80.1161 71.125 75.5357 68.9196 74.6875 68.5804C73.8393 68.2411 73.1607 68.0714 72.4821 69.0893C71.9732 69.9375 70.1071 72.1429 69.5982 72.8214C68.9196 73.3304 68.4107 73.5 67.5625 72.9911C61.9643 70.2768 58.4018 68.0714 54.6696 61.7946C53.6518 60.0982 55.6875 60.2679 57.3839 56.7054C57.7232 56.0268 57.5536 55.5179 57.3839 55.0089C57.2143 54.5 55.1786 49.9196 54.5 48.0536C53.6518 46.1875 52.9732 46.3571 52.2946 46.3571C51.7857 46.3571 51.1071 46.3571 50.5982 46.3571C49.9196 46.3571 48.9018 46.5268 48.0536 47.5446C47.2054 48.5625 44.8304 50.7679 44.8304 55.3482C44.8304 60.0982 48.0536 64.5089 48.5625 65.1875C49.0714 65.6964 55.1786 75.1964 64.6786 79.2679C70.6161 81.9821 72.9911 82.1518 76.0446 81.6429C77.7411 81.4732 81.4732 79.4375 82.3214 77.2321C83.1696 75.0268 83.1696 73.1607 82.8304 72.8214C82.6607 72.3125 81.9821 72.1429 81.1339 71.6339Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM63.8304 26C74.0089 26 83.5089 30.0714 90.4643 37.1964C97.5893 44.3214 102 53.6518 102 63.8304C102 84.5268 84.6964 101.491 63.8304 101.491C57.5536 101.491 51.4464 99.7946 45.8482 96.9107L26 102L31.2589 82.4911C28.0357 76.8929 26.1696 70.4464 26.1696 63.6607C26.1696 42.9643 43.1339 26 63.8304 26ZM47.8839 90.6339C52.8036 93.5179 58.2321 95.0446 63.8304 95.0446C81.1339 95.0446 95.5536 80.9643 95.5536 63.8304C95.5536 55.3482 91.9911 47.5446 86.0536 41.6071C80.1161 35.6696 72.3125 32.4464 64 32.4464C46.6964 32.4464 32.6161 46.5268 32.6161 63.6607C32.6161 69.5982 34.3125 75.3661 37.3661 80.4554L38.2143 81.6429L34.9911 93.1786L46.8661 89.9554L47.8839 90.6339ZM74.6875 68.5804C75.5357 68.9196 80.1161 71.125 81.1339 71.6339C81.3853 71.7847 81.6217 71.9058 81.8345 72.0147C82.3396 72.2732 82.711 72.4633 82.8304 72.8214C83.1696 73.1607 83.1696 75.0268 82.3214 77.2321C81.4732 79.4375 77.7411 81.4732 76.0446 81.6429C72.9911 82.1518 70.6161 81.9821 64.6786 79.2679C56.3653 75.705 50.6501 67.985 49.011 65.771C48.777 65.4549 48.6261 65.2511 48.5625 65.1875C48.0536 64.5089 44.8304 60.0982 44.8304 55.3482C44.8304 51.2146 46.7647 49.0153 47.7655 47.8774C47.8736 47.7544 47.9709 47.6439 48.0536 47.5446C48.9018 46.5268 49.9196 46.3571 50.5982 46.3571H52.2946C52.3467 46.3571 52.3988 46.3561 52.451 46.3551C53.0784 46.3431 53.7169 46.3308 54.5 48.0536C54.9153 49.1956 55.839 51.3544 56.5321 52.9743C56.9714 54.0012 57.3181 54.8115 57.3839 55.0089C57.5536 55.5179 57.7232 56.0268 57.3839 56.7054C56.6195 58.3106 55.7863 59.1581 55.1945 59.7599C54.4729 60.4938 54.1104 60.8626 54.6696 61.7946C58.4018 68.0714 61.9643 70.2768 67.5625 72.9911C68.4107 73.5 68.9196 73.3304 69.5982 72.8214C69.7616 72.6036 70.0649 72.2283 70.4182 71.7912C71.1654 70.8667 72.1366 69.6652 72.4821 69.0893C73.1607 68.0714 73.8393 68.2411 74.6875 68.5804Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM63.8304 26C74.0089 26 83.5089 30.0714 90.4643 37.1964C97.5893 44.3214 102 53.6518 102 63.8304C102 84.5268 84.6964 101.491 63.8304 101.491C57.5536 101.491 51.4464 99.7946 45.8482 96.9107L26 102L31.2589 82.4911C28.0357 76.8929 26.1696 70.4464 26.1696 63.6607C26.1696 42.9643 43.1339 26 63.8304 26ZM47.8839 90.6339C52.8036 93.5179 58.2321 95.0446 63.8304 95.0446C81.1339 95.0446 95.5536 80.9643 95.5536 63.8304C95.5536 55.3482 91.9911 47.5446 86.0536 41.6071C80.1161 35.6696 72.3125 32.4464 64 32.4464C46.6964 32.4464 32.6161 46.5268 32.6161 63.6607C32.6161 69.5982 34.3125 75.3661 37.3661 80.4554L38.2143 81.6429L34.9911 93.1786L46.8661 89.9554L47.8839 90.6339ZM74.6875 68.5804C75.5357 68.9196 80.1161 71.125 81.1339 71.6339C81.3853 71.7847 81.6217 71.9058 81.8345 72.0147C82.3396 72.2732 82.711 72.4633 82.8304 72.8214C83.1696 73.1607 83.1696 75.0268 82.3214 77.2321C81.4732 79.4375 77.7411 81.4732 76.0446 81.6429C72.9911 82.1518 70.6161 81.9821 64.6786 79.2679C56.3653 75.705 50.6501 67.985 49.011 65.771C48.777 65.4549 48.6261 65.2511 48.5625 65.1875C48.0536 64.5089 44.8304 60.0982 44.8304 55.3482C44.8304 51.2146 46.7647 49.0153 47.7655 47.8774C47.8736 47.7544 47.9709 47.6439 48.0536 47.5446C48.9018 46.5268 49.9196 46.3571 50.5982 46.3571H52.2946C52.3467 46.3571 52.3988 46.3561 52.451 46.3551C53.0784 46.3431 53.7169 46.3308 54.5 48.0536C54.9153 49.1956 55.839 51.3544 56.5321 52.9743C56.9714 54.0012 57.3181 54.8115 57.3839 55.0089C57.5536 55.5179 57.7232 56.0268 57.3839 56.7054C56.6195 58.3106 55.7863 59.1581 55.1945 59.7599C54.4729 60.4938 54.1104 60.8626 54.6696 61.7946C58.4018 68.0714 61.9643 70.2768 67.5625 72.9911C68.4107 73.5 68.9196 73.3304 69.5982 72.8214C69.7616 72.6036 70.0649 72.2283 70.4182 71.7912C71.1654 70.8667 72.1366 69.6652 72.4821 69.0893C73.1607 68.0714 73.8393 68.2411 74.6875 68.5804Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM63.8304 26C74.0089 26 83.5089 30.0714 90.4643 37.1964C97.5893 44.3214 102 53.6518 102 63.8304C102 84.5268 84.6964 101.491 63.8304 101.491C57.5536 101.491 51.4464 99.7946 45.8482 96.9107L26 102L31.2589 82.4911C28.0357 76.8929 26.1696 70.4464 26.1696 63.6607C26.1696 42.9643 43.1339 26 63.8304 26ZM47.8839 90.6339C52.8036 93.5179 58.2321 95.0446 63.8304 95.0446C81.1339 95.0446 95.5536 80.9643 95.5536 63.8304C95.5536 55.3482 91.9911 47.5446 86.0536 41.6071C80.1161 35.6696 72.3125 32.4464 64 32.4464C46.6964 32.4464 32.6161 46.5268 32.6161 63.6607C32.6161 69.5982 34.3125 75.3661 37.3661 80.4554L38.2143 81.6429L34.9911 93.1786L46.8661 89.9554L47.8839 90.6339ZM74.6875 68.5804C75.5357 68.9196 80.1161 71.125 81.1339 71.6339C81.3853 71.7847 81.6217 71.9058 81.8345 72.0147C82.3396 72.2732 82.711 72.4633 82.8304 72.8214C83.1696 73.1607 83.1696 75.0268 82.3214 77.2321C81.4732 79.4375 77.7411 81.4732 76.0446 81.6429C72.9911 82.1518 70.6161 81.9821 64.6786 79.2679C56.3653 75.705 50.6501 67.985 49.011 65.771C48.777 65.4549 48.6261 65.2511 48.5625 65.1875C48.0536 64.5089 44.8304 60.0982 44.8304 55.3482C44.8304 51.2146 46.7647 49.0153 47.7655 47.8774C47.8736 47.7544 47.9709 47.6439 48.0536 47.5446C48.9018 46.5268 49.9196 46.3571 50.5982 46.3571H52.2946C52.3467 46.3571 52.3988 46.3561 52.451 46.3551C53.0784 46.3431 53.7169 46.3308 54.5 48.0536C54.9153 49.1956 55.839 51.3544 56.5321 52.9743C56.9714 54.0012 57.3181 54.8115 57.3839 55.0089C57.5536 55.5179 57.7232 56.0268 57.3839 56.7054C56.6195 58.3106 55.7863 59.1581 55.1945 59.7599C54.4729 60.4938 54.1104 60.8626 54.6696 61.7946C58.4018 68.0714 61.9643 70.2768 67.5625 72.9911C68.4107 73.5 68.9196 73.3304 69.5982 72.8214C69.7616 72.6036 70.0649 72.2283 70.4182 71.7912C71.1654 70.8667 72.1366 69.6652 72.4821 69.0893C73.1607 68.0714 73.8393 68.2411 74.6875 68.5804Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM63.8304 26C74.0089 26 83.5089 30.0714 90.4643 37.1964C97.5893 44.3214 102 53.6518 102 63.8304C102 84.5268 84.6964 101.491 63.8304 101.491C57.5536 101.491 51.4464 99.7946 45.8482 96.9107L26 102L31.2589 82.4911C28.0357 76.8929 26.1696 70.4464 26.1696 63.6607C26.1696 42.9643 43.1339 26 63.8304 26ZM47.8839 90.6339C52.8036 93.5179 58.2321 95.0446 63.8304 95.0446C81.1339 95.0446 95.5536 80.9643 95.5536 63.8304C95.5536 55.3482 91.9911 47.5446 86.0536 41.6071C80.1161 35.6696 72.3125 32.4464 64 32.4464C46.6964 32.4464 32.6161 46.5268 32.6161 63.6607C32.6161 69.5982 34.3125 75.3661 37.3661 80.4554L38.2143 81.6429L34.9911 93.1786L46.8661 89.9554L47.8839 90.6339ZM74.6875 68.5804C75.5357 68.9196 80.1161 71.125 81.1339 71.6339C81.3853 71.7847 81.6217 71.9058 81.8345 72.0147C82.3396 72.2732 82.711 72.4633 82.8304 72.8214C83.1696 73.1607 83.1696 75.0268 82.3214 77.2321C81.4732 79.4375 77.7411 81.4732 76.0446 81.6429C72.9911 82.1518 70.6161 81.9821 64.6786 79.2679C56.3653 75.705 50.6501 67.985 49.011 65.771C48.777 65.4549 48.6261 65.2511 48.5625 65.1875C48.0536 64.5089 44.8304 60.0982 44.8304 55.3482C44.8304 51.2146 46.7647 49.0153 47.7655 47.8774C47.8736 47.7544 47.9709 47.6439 48.0536 47.5446C48.9018 46.5268 49.9196 46.3571 50.5982 46.3571H52.2946C52.3467 46.3571 52.3988 46.3561 52.451 46.3551C53.0784 46.3431 53.7169 46.3308 54.5 48.0536C54.9153 49.1956 55.839 51.3544 56.5321 52.9743C56.9714 54.0012 57.3181 54.8115 57.3839 55.0089C57.5536 55.5179 57.7232 56.0268 57.3839 56.7054C56.6195 58.3106 55.7863 59.1581 55.1945 59.7599C54.4729 60.4938 54.1104 60.8626 54.6696 61.7946C58.4018 68.0714 61.9643 70.2768 67.5625 72.9911C68.4107 73.5 68.9196 73.3304 69.5982 72.8214C69.7616 72.6036 70.0649 72.2283 70.4182 71.7912C71.1654 70.8667 72.1366 69.6652 72.4821 69.0893C73.1607 68.0714 73.8393 68.2411 74.6875 68.5804Z" />
                </svg>
                `
      }
    }
  },
  Youtube: {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#FF0000"/>
            <path d="M106.069 42.849C105.104 38.974 102.048 35.9062 98.3474 34.9375C91.4296 33 64.0804 33 64.0804 33C64.0804 33 36.5704 33 29.6527 34.9375C25.9525 35.9062 22.8958 38.974 21.9305 42.849C20 49.6302 20 64.1615 20 64.1615C20 64.1615 20 78.5312 21.9305 85.474C22.8958 89.349 25.9525 92.2552 29.6527 93.224C36.5704 95 64.0804 95 64.0804 95C64.0804 95 91.4296 95 98.3474 93.224C102.048 92.2552 105.104 89.349 106.069 85.474C108 78.5312 108 64.1615 108 64.1615C108 64.1615 108 49.6302 106.069 42.849ZM55.0713 77.2396V51.0833L77.9159 64.1615L55.0713 77.2396Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#FF0000"/>
            <path d="M106.069 42.849C105.104 38.974 102.048 35.9062 98.3474 34.9375C91.4296 33 64.0804 33 64.0804 33C64.0804 33 36.5704 33 29.6527 34.9375C25.9525 35.9062 22.8958 38.974 21.9305 42.849C20 49.6302 20 64.1615 20 64.1615C20 64.1615 20 78.5312 21.9305 85.474C22.8958 89.349 25.9525 92.2552 29.6527 93.224C36.5704 95 64.0804 95 64.0804 95C64.0804 95 91.4296 95 98.3474 93.224C102.048 92.2552 105.104 89.349 106.069 85.474C108 78.5312 108 64.1615 108 64.1615C108 64.1615 108 49.6302 106.069 42.849ZM55.0713 77.2396V51.0833L77.9159 64.1615L55.0713 77.2396Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM98.3474 34.9375C102.048 35.9062 105.104 38.974 106.069 42.849C108 49.6302 108 64.1615 108 64.1615C108 64.1615 108 78.5312 106.069 85.474C105.104 89.349 102.048 92.2552 98.3474 93.224C91.4296 95 64.0804 95 64.0804 95C64.0804 95 36.5704 95 29.6527 93.224C25.9525 92.2552 22.8958 89.349 21.9305 85.474C20 78.5312 20 64.1615 20 64.1615C20 64.1615 20 49.6302 21.9305 42.849C22.8958 38.974 25.9525 35.9062 29.6527 34.9375C36.5704 33 64.0804 33 64.0804 33C64.0804 33 91.4296 33 98.3474 34.9375ZM55.0713 51.0833V77.2396L77.9159 64.1615L55.0713 51.0833Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM98.3474 34.9375C102.048 35.9062 105.104 38.974 106.069 42.849C108 49.6302 108 64.1615 108 64.1615C108 64.1615 108 78.5312 106.069 85.474C105.104 89.349 102.048 92.2552 98.3474 93.224C91.4296 95 64.0804 95 64.0804 95C64.0804 95 36.5704 95 29.6527 93.224C25.9525 92.2552 22.8958 89.349 21.9305 85.474C20 78.5312 20 64.1615 20 64.1615C20 64.1615 20 49.6302 21.9305 42.849C22.8958 38.974 25.9525 35.9062 29.6527 34.9375C36.5704 33 64.0804 33 64.0804 33C64.0804 33 91.4296 33 98.3474 34.9375ZM55.0713 51.0833V77.2396L77.9159 64.1615L55.0713 51.0833Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM98.3474 34.9375C102.048 35.9062 105.104 38.974 106.069 42.849C108 49.6302 108 64.1615 108 64.1615C108 64.1615 108 78.5312 106.069 85.474C105.104 89.349 102.048 92.2552 98.3474 93.224C91.4296 95 64.0804 95 64.0804 95C64.0804 95 36.5704 95 29.6527 93.224C25.9525 92.2552 22.8958 89.349 21.9305 85.474C20 78.5312 20 64.1615 20 64.1615C20 64.1615 20 49.6302 21.9305 42.849C22.8958 38.974 25.9525 35.9062 29.6527 34.9375C36.5704 33 64.0804 33 64.0804 33C64.0804 33 91.4296 33 98.3474 34.9375ZM55.0713 51.0833V77.2396L77.9159 64.1615L55.0713 51.0833Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM98.3474 34.9375C102.048 35.9062 105.104 38.974 106.069 42.849C108 49.6302 108 64.1615 108 64.1615C108 64.1615 108 78.5312 106.069 85.474C105.104 89.349 102.048 92.2552 98.3474 93.224C91.4296 95 64.0804 95 64.0804 95C64.0804 95 36.5704 95 29.6527 93.224C25.9525 92.2552 22.8958 89.349 21.9305 85.474C20 78.5312 20 64.1615 20 64.1615C20 64.1615 20 49.6302 21.9305 42.849C22.8958 38.974 25.9525 35.9062 29.6527 34.9375C36.5704 33 64.0804 33 64.0804 33C64.0804 33 91.4296 33 98.3474 34.9375ZM55.0713 51.0833V77.2396L77.9159 64.1615L55.0713 51.0833Z" />
                </svg>
                `
      }
    }
  },
  LinkedIn: {
    brand: {
      circle: `
        <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#1B66C2"/>
        <path d="M42.9643 102V51.1633H27.1875V102H42.9643ZM34.9911 44.3624C40.0804 44.3624 44.1518 40.1119 44.1518 35.0112C44.1518 30.0805 40.0804 26 34.9911 26C30.0714 26 26 30.0805 26 35.0112C26 40.1119 30.0714 44.3624 34.9911 44.3624ZM101.83 102H102V74.1163C102 60.5145 98.9464 49.9732 83 49.9732C75.3661 49.9732 70.2768 54.2237 68.0714 58.1342H67.9018V51.1633H52.8036V102H68.5804V76.8367C68.5804 70.2058 69.7679 63.915 77.9107 63.915C86.0536 63.915 86.2232 71.396 86.2232 77.3468V102H101.83Z" fill="white"/>
        </svg>
        `,
      square: `
        <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="128" height="128" rx="10" fill="#1B66C2"/>
        <path d="M42.9643 102V51.1633H27.1875V102H42.9643ZM34.9911 44.3624C40.0804 44.3624 44.1518 40.1119 44.1518 35.0112C44.1518 30.0805 40.0804 26 34.9911 26C30.0714 26 26 30.0805 26 35.0112C26 40.1119 30.0714 44.3624 34.9911 44.3624ZM101.83 102H102V74.1163C102 60.5145 98.9464 49.9732 83 49.9732C75.3661 49.9732 70.2768 54.2237 68.0714 58.1342H67.9018V51.1633H52.8036V102H68.5804V76.8367C68.5804 70.2058 69.7679 63.915 77.9107 63.915C86.0536 63.915 86.2232 71.396 86.2232 77.3468V102H101.83Z" fill="white"/>
        </svg>
        `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM42.9643 51.1633V102H27.1875V51.1633H42.9643ZM44.1518 35.0112C44.1518 40.1119 40.0804 44.3624 34.9911 44.3624C30.0714 44.3624 26 40.1119 26 35.0112C26 30.0805 30.0714 26 34.9911 26C40.0804 26 44.1518 30.0805 44.1518 35.0112ZM102 102H101.83H86.2232V77.3468C86.2232 71.396 86.0536 63.915 77.9107 63.915C69.7679 63.915 68.5804 70.2058 68.5804 76.8367V102H52.8036V51.1633H67.9018V58.1342H68.0714C70.2768 54.2237 75.3661 49.9732 83 49.9732C98.9464 49.9732 102 60.5145 102 74.1163V102Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM42.9643 51.1633V102H27.1875V51.1633H42.9643ZM44.1518 35.0112C44.1518 40.1119 40.0804 44.3624 34.9911 44.3624C30.0714 44.3624 26 40.1119 26 35.0112C26 30.0805 30.0714 26 34.9911 26C40.0804 26 44.1518 30.0805 44.1518 35.0112ZM102 102H101.83H86.2232V77.3468C86.2232 71.396 86.0536 63.915 77.9107 63.915C69.7679 63.915 68.5804 70.2058 68.5804 76.8367V102H52.8036V51.1633H67.9018V58.1342H68.0714C70.2768 54.2237 75.3661 49.9732 83 49.9732C98.9464 49.9732 102 60.5145 102 74.1163V102Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM42.9643 51.1633V102H27.1875V51.1633H42.9643ZM44.1518 35.0112C44.1518 40.1119 40.0804 44.3624 34.9911 44.3624C30.0714 44.3624 26 40.1119 26 35.0112C26 30.0805 30.0714 26 34.9911 26C40.0804 26 44.1518 30.0805 44.1518 35.0112ZM102 102H101.83H86.2232V77.3468C86.2232 71.396 86.0536 63.915 77.9107 63.915C69.7679 63.915 68.5804 70.2058 68.5804 76.8367V102H52.8036V51.1633H67.9018V58.1342H68.0714C70.2768 54.2237 75.3661 49.9732 83 49.9732C98.9464 49.9732 102 60.5145 102 74.1163V102Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM42.9643 51.1633V102H27.1875V51.1633H42.9643ZM44.1518 35.0112C44.1518 40.1119 40.0804 44.3624 34.9911 44.3624C30.0714 44.3624 26 40.1119 26 35.0112C26 30.0805 30.0714 26 34.9911 26C40.0804 26 44.1518 30.0805 44.1518 35.0112ZM102 102H101.83H86.2232V77.3468C86.2232 71.396 86.0536 63.915 77.9107 63.915C69.7679 63.915 68.5804 70.2058 68.5804 76.8367V102H52.8036V51.1633H67.9018V58.1342H68.0714C70.2768 54.2237 75.3661 49.9732 83 49.9732C98.9464 49.9732 102 60.5145 102 74.1163V102Z" />
                </svg>
                `
      }
    }
  },
  'Apple Podcast': {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="url(#paint0_linear)"/>
            <path d="M61.5822 101.194C58.6877 100.151 58.0668 98.7318 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C67.9565 68.9071 70.8607 70.3455 71.9279 72.8766C72.7299 74.7683 72.4292 80.7535 71.0482 90.4178C70.1104 97.1694 69.5929 98.8753 68.2993 100.023C66.5206 101.612 63.998 102.052 61.6048 101.198L61.5822 101.194ZM49.2185 91.2789C39.2253 86.3212 32.8219 77.9322 30.4934 66.7708C29.9112 63.8907 29.8142 57.0184 30.364 54.3764C31.8193 47.2823 34.6006 41.7343 39.2253 36.8287C45.8874 29.7444 54.4576 26 63.998 26C73.4414 26 81.9793 29.6759 88.4797 36.5482C93.4278 41.7343 96.2091 47.2236 97.632 54.4547C98.1171 56.8618 98.1171 63.4243 97.6644 66.1315C96.1767 74.6901 91.455 82.4854 84.5665 87.7237C82.1086 89.5959 76.0933 92.864 75.1231 92.864C74.7674 92.864 74.735 92.4922 74.8967 90.9886C75.1878 88.575 75.4789 88.0727 76.8372 87.4986C79.004 86.5853 82.6908 83.9369 84.9546 81.6472C88.8678 77.7332 91.7461 72.6124 93.072 67.2307C93.9129 63.8711 93.8159 56.4019 92.878 52.9446C89.935 41.9528 81.0414 33.4072 70.1427 31.1241C66.9733 30.4717 61.2167 30.4717 58.015 31.1241C46.987 33.4072 37.867 42.3768 35.0857 53.6621C34.3419 56.7281 34.3419 64.1973 35.0857 67.2633C36.9291 74.7325 41.7155 81.582 47.9895 85.659C49.2185 86.4744 50.7061 87.3225 51.3206 87.5834C52.6789 88.1705 52.9699 88.6598 53.2287 91.0734C53.3904 92.5411 53.358 92.9652 53.0023 92.9652C52.7759 92.9652 51.1265 92.2476 49.3802 91.3996L49.2185 91.2789ZM49.3478 78.0104C45.9844 75.3033 43.0091 70.5021 41.7802 65.7923C41.0363 62.9481 41.0363 57.5403 41.8125 54.7026C43.85 47.0442 49.4448 41.108 57.2065 38.3193C59.8584 37.3734 65.7444 37.1614 69.0108 37.8888C80.2652 40.4165 88.2533 51.7834 86.701 63.059C86.0865 67.6025 84.5342 71.3338 81.7852 74.801C80.4269 76.5525 77.1282 79.4912 76.5461 79.4912C76.4491 79.4912 76.3521 78.3823 76.3521 77.0319V74.5661L78.0338 72.5439C84.3725 64.8855 83.9197 54.1873 76.9989 47.1682C74.3146 44.4349 71.2099 42.8302 67.1997 42.0898C64.6125 41.607 64.0627 41.607 61.3461 42.0572C57.2227 42.7356 54.0275 44.3468 51.1912 47.178C44.238 54.1253 43.7853 64.879 50.124 72.5439L51.7928 74.5661V77.045C51.7928 78.4149 51.686 79.5239 51.5534 79.5239C51.4241 79.5239 50.4862 78.8715 49.4837 78.0561L49.3478 78.0104ZM60.5699 64.6931C57.6916 63.3428 56.1393 60.7954 56.107 57.475C56.107 54.4906 57.7563 51.8878 60.6023 50.3646C62.4133 49.4057 65.615 49.4057 67.4261 50.3711C69.3989 51.4051 71.0159 53.411 71.6304 55.505C73.5061 61.937 66.747 67.5731 60.6346 64.6931H60.5699Z" fill="white"/>
            <path d="M64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.3314 68.9636 49.6895 64.5091 49.6895C60.0545 49.6895 56.4434 53.3314 56.4434 57.824C56.4434 62.3166 60.0545 65.9586 64.5091 65.9586Z" fill="white"/>
            <path d="M65.2821 69.1094C65.8416 69.1844 66.9541 69.3312 67.9826 69.6639C69.0174 69.99 69.9553 70.5021 70.6862 71.0272C71.3977 71.5491 71.9151 72.0905 72.2385 72.7494C72.5619 73.4082 72.7236 74.191 72.8207 75.4729C72.8854 76.758 72.8854 78.5388 72.5943 81.8657C72.3032 85.1828 71.7211 90.046 71.2683 93.1609C70.8156 96.2921 70.4922 97.662 70.0717 98.6731C69.6513 99.7168 69.1015 100.402 68.4871 100.891C67.8726 101.38 67.1611 101.674 66.482 101.837C65.8028 102 65.1883 102 64.6062 102C64.0241 102 63.4743 102 62.7305 101.837C61.9866 101.674 61.0164 101.38 60.3049 100.793C59.5611 100.206 59.076 99.358 58.6879 98.2491C58.2998 97.1401 58.0088 95.8028 57.6207 93.1282C57.2326 90.4537 56.7152 86.4745 56.3918 83.4411C56.0683 80.4078 55.939 78.3203 55.9131 76.9178C55.8808 75.5153 55.9455 74.8205 56.1072 74.1454C56.2689 73.493 56.5276 72.8733 56.8833 72.3515C57.2391 71.8296 57.7048 71.373 58.1769 71.0142C58.6394 70.6554 59.1148 70.3945 59.684 70.1922C60.2467 69.9639 60.9226 69.7682 61.7538 69.6051C62.5946 69.4421 63.5649 69.279 64.0629 69.2137C64.5609 69.1387 64.5609 69.1387 65.1172 69.2137L65.2821 69.1094Z" fill="white"/>
            <defs>
            <linearGradient id="paint0_linear" x1="64" y1="0" x2="64" y2="128.502" gradientUnits="userSpaceOnUse">
            <stop stop-color="#D56DFB"/>
            <stop offset="0.5" stop-color="#B150E2"/>
            <stop offset="1" stop-color="#872EC4"/>
            </linearGradient>
            </defs>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="url(#paint0_linear)"/>
            <path d="M61.5822 101.194C58.6877 100.151 58.0668 98.7318 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C67.9565 68.9071 70.8607 70.3455 71.9279 72.8766C72.7299 74.7683 72.4292 80.7535 71.0482 90.4178C70.1104 97.1694 69.5929 98.8753 68.2993 100.023C66.5206 101.612 63.998 102.052 61.6048 101.198L61.5822 101.194ZM49.2185 91.2789C39.2253 86.3212 32.8219 77.9322 30.4934 66.7708C29.9112 63.8907 29.8142 57.0184 30.364 54.3764C31.8193 47.2823 34.6006 41.7343 39.2253 36.8287C45.8874 29.7444 54.4576 26 63.998 26C73.4414 26 81.9793 29.6759 88.4797 36.5482C93.4278 41.7343 96.2091 47.2236 97.632 54.4547C98.1171 56.8618 98.1171 63.4243 97.6644 66.1315C96.1767 74.6901 91.455 82.4854 84.5665 87.7237C82.1086 89.5959 76.0933 92.864 75.1231 92.864C74.7674 92.864 74.735 92.4922 74.8967 90.9886C75.1878 88.575 75.4789 88.0727 76.8372 87.4986C79.004 86.5853 82.6908 83.9369 84.9546 81.6472C88.8678 77.7332 91.7461 72.6124 93.072 67.2307C93.9129 63.8711 93.8159 56.4019 92.878 52.9446C89.935 41.9528 81.0414 33.4072 70.1427 31.1241C66.9733 30.4717 61.2167 30.4717 58.015 31.1241C46.987 33.4072 37.867 42.3768 35.0857 53.6621C34.3419 56.7281 34.3419 64.1973 35.0857 67.2633C36.9291 74.7325 41.7155 81.582 47.9895 85.659C49.2185 86.4744 50.7061 87.3225 51.3206 87.5834C52.6789 88.1705 52.9699 88.6598 53.2287 91.0734C53.3904 92.5411 53.358 92.9652 53.0023 92.9652C52.7759 92.9652 51.1265 92.2476 49.3802 91.3996L49.2185 91.2789ZM49.3478 78.0104C45.9844 75.3033 43.0091 70.5021 41.7802 65.7923C41.0363 62.9481 41.0363 57.5403 41.8125 54.7026C43.85 47.0442 49.4448 41.108 57.2065 38.3193C59.8584 37.3734 65.7444 37.1614 69.0108 37.8888C80.2652 40.4165 88.2533 51.7834 86.701 63.059C86.0865 67.6025 84.5342 71.3338 81.7852 74.801C80.4269 76.5525 77.1282 79.4912 76.5461 79.4912C76.4491 79.4912 76.3521 78.3823 76.3521 77.0319V74.5661L78.0338 72.5439C84.3725 64.8855 83.9197 54.1873 76.9989 47.1682C74.3146 44.4349 71.2099 42.8302 67.1997 42.0898C64.6125 41.607 64.0627 41.607 61.3461 42.0572C57.2227 42.7356 54.0275 44.3468 51.1912 47.178C44.238 54.1253 43.7853 64.879 50.124 72.5439L51.7928 74.5661V77.045C51.7928 78.4149 51.686 79.5239 51.5534 79.5239C51.4241 79.5239 50.4862 78.8715 49.4837 78.0561L49.3478 78.0104ZM60.5699 64.6931C57.6916 63.3428 56.1393 60.7954 56.107 57.475C56.107 54.4906 57.7563 51.8878 60.6023 50.3646C62.4133 49.4057 65.615 49.4057 67.4261 50.3711C69.3989 51.4051 71.0159 53.411 71.6304 55.505C73.5061 61.937 66.747 67.5731 60.6346 64.6931H60.5699Z" fill="white"/>
            <path d="M64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.3314 68.9636 49.6895 64.5091 49.6895C60.0545 49.6895 56.4434 53.3314 56.4434 57.824C56.4434 62.3166 60.0545 65.9586 64.5091 65.9586Z" fill="white"/>
            <path d="M65.2821 69.1094C65.8416 69.1844 66.9541 69.3312 67.9826 69.6639C69.0174 69.99 69.9553 70.5021 70.6862 71.0272C71.3977 71.5491 71.9151 72.0905 72.2385 72.7494C72.5619 73.4082 72.7236 74.191 72.8207 75.4729C72.8854 76.758 72.8854 78.5388 72.5943 81.8657C72.3032 85.1828 71.7211 90.046 71.2683 93.1609C70.8156 96.2921 70.4922 97.662 70.0717 98.6731C69.6513 99.7168 69.1015 100.402 68.4871 100.891C67.8726 101.38 67.1611 101.674 66.482 101.837C65.8028 102 65.1883 102 64.6062 102C64.0241 102 63.4743 102 62.7305 101.837C61.9866 101.674 61.0164 101.38 60.3049 100.793C59.5611 100.206 59.076 99.358 58.6879 98.2491C58.2998 97.1401 58.0088 95.8028 57.6207 93.1282C57.2326 90.4537 56.7152 86.4745 56.3918 83.4411C56.0683 80.4078 55.939 78.3203 55.9131 76.9178C55.8808 75.5153 55.9455 74.8205 56.1072 74.1454C56.2689 73.493 56.5276 72.8733 56.8833 72.3515C57.2391 71.8296 57.7048 71.373 58.1769 71.0142C58.6394 70.6554 59.1148 70.3945 59.684 70.1922C60.2467 69.9639 60.9226 69.7682 61.7538 69.6051C62.5946 69.4421 63.5649 69.279 64.0629 69.2137C64.5609 69.1387 64.5609 69.1387 65.1172 69.2137L65.2821 69.1094Z" fill="white"/>
            <defs>
            <linearGradient id="paint0_linear" x1="64" y1="0" x2="64" y2="128" gradientUnits="userSpaceOnUse">
            <stop stop-color="#D56DFB"/>
            <stop offset="0.5" stop-color="#B150E2"/>
            <stop offset="1" stop-color="#872EC4"/>
            </linearGradient>
            </defs>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM59.5984 100.062C58.2886 98.7854 57.7291 96.3664 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C66.7169 68.9122 68.9529 69.5928 70.4257 70.8464C70.5152 70.9063 70.6022 70.9667 70.6864 71.0272C71.3979 71.5491 71.9153 72.0905 72.2387 72.7494C72.5621 73.4082 72.7238 74.191 72.8209 75.4729C72.8856 76.758 72.8856 78.5388 72.5945 81.8657C72.3034 85.1828 71.7213 90.046 71.2685 93.1609C70.8158 96.2921 70.4924 97.662 70.0719 98.6731C69.6515 99.7168 69.1017 100.402 68.4873 100.891C67.8728 101.38 67.1613 101.674 66.4822 101.837C65.803 102 65.1885 102 64.6064 102C64.0243 102 63.4745 102 62.7307 101.837C61.9868 101.674 61.0166 101.38 60.3051 100.793C60.0389 100.583 59.8058 100.339 59.5984 100.062ZM30.4934 66.7708C32.8219 77.9322 39.2253 86.3212 49.2185 91.2789L49.3802 91.3996C51.1265 92.2476 52.7759 92.9652 53.0023 92.9652C53.358 92.9652 53.3904 92.5411 53.2287 91.0734C52.9699 88.6598 52.6789 88.1705 51.3206 87.5834C50.7061 87.3225 49.2185 86.4744 47.9895 85.659C41.7155 81.582 36.9291 74.7325 35.0857 67.2633C34.3419 64.1973 34.3419 56.7281 35.0857 53.6621C37.867 42.3768 46.987 33.4072 58.015 31.1241C61.2167 30.4717 66.9733 30.4717 70.1427 31.1241C81.0414 33.4072 89.935 41.9528 92.878 52.9446C93.8159 56.4019 93.9129 63.8711 93.072 67.2307C91.7461 72.6124 88.8678 77.7332 84.9546 81.6472C82.6908 83.9369 79.004 86.5853 76.8372 87.4986C75.4789 88.0727 75.1878 88.575 74.8967 90.9886C74.735 92.4922 74.7674 92.864 75.1231 92.864C76.0933 92.864 82.1086 89.5959 84.5665 87.7237C91.455 82.4854 96.1767 74.6901 97.6644 66.1315C98.1171 63.4243 98.1171 56.8618 97.632 54.4547C96.2091 47.2236 93.4278 41.7343 88.4797 36.5482C81.9793 29.6759 73.4414 26 63.998 26C54.4576 26 45.8874 29.7444 39.2253 36.8287C34.6006 41.7343 31.8193 47.2823 30.364 54.3764C29.8142 57.0184 29.9112 63.8907 30.4934 66.7708ZM41.7802 65.7923C43.0091 70.5021 45.9844 75.3033 49.3478 78.0104L49.4837 78.0561C50.4862 78.8715 51.4241 79.5239 51.5534 79.5239C51.686 79.5239 51.7928 78.4149 51.7928 77.045V74.5661L50.124 72.5439C43.7853 64.879 44.238 54.1253 51.1912 47.178C54.0275 44.3468 57.2227 42.7356 61.3461 42.0572C64.0627 41.607 64.6125 41.607 67.1997 42.0898C71.2099 42.8302 74.3146 44.4349 76.9989 47.1682C83.9197 54.1873 84.3725 64.8855 78.0338 72.5439L76.3521 74.5661V77.0319C76.3521 78.3823 76.4491 79.4912 76.5461 79.4912C77.1282 79.4912 80.4269 76.5525 81.7852 74.801C84.5342 71.3338 86.0865 67.6025 86.701 63.059C88.2533 51.7834 80.2652 40.4165 69.0108 37.8888C65.7444 37.1614 59.8584 37.3734 57.2065 38.3193C49.4448 41.108 43.85 47.0442 41.8125 54.7026C41.0363 57.5403 41.0363 62.9481 41.7802 65.7923ZM56.107 57.475C56.1322 60.0582 57.0773 62.1735 58.851 63.6213C60.3069 65.0668 62.3047 65.9586 64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.5556 69.3149 50.055 65.1692 49.7164C63.5713 49.5198 61.7885 49.7365 60.6023 50.3646C57.7563 51.8878 56.107 54.4906 56.107 57.475Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM59.5984 100.062C58.2886 98.7853 57.7291 96.3664 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C66.7169 68.9122 68.953 69.5928 70.4257 70.8465C70.5153 70.9064 70.6022 70.9667 70.6864 71.0272C71.3979 71.5491 71.9153 72.0905 72.2387 72.7494C72.5621 73.4082 72.7238 74.191 72.8209 75.4729C72.8856 76.758 72.8856 78.5388 72.5945 81.8657C72.3034 85.1828 71.7213 90.046 71.2685 93.1609C70.8158 96.2921 70.4924 97.662 70.0719 98.6731C69.6515 99.7168 69.1017 100.402 68.4873 100.891C67.8728 101.38 67.1613 101.674 66.4822 101.837C65.803 102 65.1885 102 64.6064 102C64.0243 102 63.4745 102 62.7307 101.837C61.9868 101.674 61.0166 101.38 60.3051 100.793C60.0389 100.583 59.8058 100.339 59.5984 100.062ZM30.4934 66.7708C32.8219 77.9322 39.2253 86.3212 49.2185 91.2789L49.3802 91.3996C51.1265 92.2476 52.7759 92.9652 53.0023 92.9652C53.358 92.9652 53.3904 92.5411 53.2287 91.0734C52.9699 88.6598 52.6789 88.1705 51.3206 87.5834C50.7061 87.3225 49.2185 86.4744 47.9895 85.659C41.7155 81.582 36.9291 74.7325 35.0857 67.2633C34.3419 64.1973 34.3419 56.7281 35.0857 53.6621C37.867 42.3768 46.987 33.4072 58.015 31.1241C61.2167 30.4717 66.9733 30.4717 70.1427 31.1241C81.0414 33.4072 89.935 41.9528 92.878 52.9446C93.8159 56.4019 93.9129 63.8711 93.072 67.2307C91.7461 72.6124 88.8678 77.7332 84.9546 81.6472C82.6908 83.9369 79.004 86.5853 76.8372 87.4986C75.4789 88.0727 75.1878 88.575 74.8967 90.9886C74.735 92.4922 74.7674 92.864 75.1231 92.864C76.0933 92.864 82.1086 89.5959 84.5665 87.7237C91.455 82.4854 96.1767 74.6901 97.6644 66.1315C98.1171 63.4243 98.1171 56.8618 97.632 54.4547C96.2091 47.2236 93.4278 41.7343 88.4797 36.5482C81.9793 29.6759 73.4414 26 63.998 26C54.4576 26 45.8874 29.7444 39.2253 36.8287C34.6006 41.7343 31.8193 47.2823 30.364 54.3764C29.8142 57.0184 29.9112 63.8907 30.4934 66.7708ZM41.7802 65.7923C43.0091 70.5021 45.9844 75.3033 49.3478 78.0104L49.4837 78.0561C50.4862 78.8715 51.4241 79.5239 51.5534 79.5239C51.686 79.5239 51.7928 78.4149 51.7928 77.045V74.5661L50.124 72.5439C43.7853 64.879 44.238 54.1253 51.1912 47.178C54.0275 44.3468 57.2227 42.7356 61.3461 42.0572C64.0627 41.607 64.6125 41.607 67.1997 42.0898C71.2099 42.8302 74.3146 44.4349 76.9989 47.1682C83.9197 54.1873 84.3725 64.8855 78.0338 72.5439L76.3521 74.5661V77.0319C76.3521 78.3823 76.4491 79.4912 76.5461 79.4912C77.1282 79.4912 80.4269 76.5525 81.7852 74.801C84.5342 71.3338 86.0865 67.6025 86.701 63.059C88.2533 51.7834 80.2652 40.4165 69.0108 37.8888C65.7444 37.1614 59.8584 37.3734 57.2065 38.3193C49.4448 41.108 43.85 47.0442 41.8125 54.7026C41.0363 57.5403 41.0363 62.9481 41.7802 65.7923ZM56.107 57.475C56.1322 60.0582 57.0772 62.1735 58.8509 63.6213C60.3068 65.0667 62.3047 65.9586 64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.5557 69.3151 50.0553 65.1695 49.7164C63.5716 49.5198 61.7886 49.7365 60.6023 50.3646C57.7563 51.8878 56.107 54.4906 56.107 57.475Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM59.5985 100.062C58.2886 98.7855 57.7292 96.3665 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C66.7168 68.9122 68.9528 69.5927 70.4256 70.8463C70.5152 70.9063 70.6022 70.9666 70.6864 71.0272C71.3979 71.5491 71.9153 72.0905 72.2387 72.7494C72.5621 73.4082 72.7238 74.191 72.8209 75.4729C72.8856 76.758 72.8856 78.5388 72.5945 81.8657C72.3034 85.1828 71.7213 90.046 71.2685 93.1609C70.8158 96.2921 70.4924 97.662 70.0719 98.6731C69.6515 99.7168 69.1017 100.402 68.4873 100.891C67.8728 101.38 67.1613 101.674 66.4822 101.837C65.803 102 65.1885 102 64.6064 102C64.0243 102 63.4745 102 62.7307 101.837C61.9868 101.674 61.0166 101.38 60.3051 100.793C60.0389 100.583 59.8058 100.339 59.5985 100.062ZM30.4934 66.7708C32.8219 77.9322 39.2253 86.3212 49.2185 91.2789L49.3802 91.3996C51.1265 92.2476 52.7759 92.9652 53.0023 92.9652C53.358 92.9652 53.3904 92.5411 53.2287 91.0734C52.9699 88.6598 52.6789 88.1705 51.3206 87.5834C50.7061 87.3225 49.2185 86.4744 47.9895 85.659C41.7155 81.582 36.9291 74.7325 35.0857 67.2633C34.3419 64.1973 34.3419 56.7281 35.0857 53.6621C37.867 42.3768 46.987 33.4072 58.015 31.1241C61.2167 30.4717 66.9733 30.4717 70.1427 31.1241C81.0414 33.4072 89.935 41.9528 92.878 52.9446C93.8159 56.4019 93.9129 63.8711 93.072 67.2307C91.7461 72.6124 88.8678 77.7332 84.9546 81.6472C82.6908 83.9369 79.004 86.5853 76.8372 87.4986C75.4789 88.0727 75.1878 88.575 74.8967 90.9886C74.735 92.4922 74.7674 92.864 75.1231 92.864C76.0933 92.864 82.1086 89.5959 84.5665 87.7237C91.455 82.4854 96.1767 74.6901 97.6644 66.1315C98.1171 63.4243 98.1171 56.8618 97.632 54.4547C96.2091 47.2236 93.4278 41.7343 88.4797 36.5482C81.9793 29.6759 73.4414 26 63.998 26C54.4576 26 45.8874 29.7444 39.2253 36.8287C34.6006 41.7343 31.8193 47.2823 30.364 54.3764C29.8142 57.0184 29.9112 63.8907 30.4934 66.7708ZM41.7802 65.7923C43.0091 70.5021 45.9844 75.3033 49.3478 78.0104L49.4837 78.0561C50.4862 78.8715 51.4241 79.5239 51.5534 79.5239C51.686 79.5239 51.7928 78.4149 51.7928 77.045V74.5661L50.124 72.5439C43.7853 64.879 44.238 54.1253 51.1912 47.178C54.0275 44.3468 57.2227 42.7356 61.3461 42.0572C64.0627 41.607 64.6125 41.607 67.1997 42.0898C71.2099 42.8302 74.3146 44.4349 76.9989 47.1682C83.9197 54.1873 84.3725 64.8855 78.0338 72.5439L76.3521 74.5661V77.0319C76.3521 78.3823 76.4491 79.4912 76.5461 79.4912C77.1282 79.4912 80.4269 76.5525 81.7852 74.801C84.5342 71.3338 86.0865 67.6025 86.701 63.059C88.2533 51.7834 80.2652 40.4165 69.0108 37.8888C65.7444 37.1614 59.8584 37.3734 57.2065 38.3193C49.4448 41.108 43.85 47.0442 41.8125 54.7026C41.0363 57.5403 41.0363 62.9481 41.7802 65.7923ZM56.107 57.475C56.1322 60.0583 57.0774 62.1738 58.8513 63.6215C60.3071 65.0668 62.3048 65.9586 64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.5551 69.3143 50.0544 65.1681 49.7162C63.5705 49.5199 61.7882 49.7367 60.6023 50.3646C57.7563 51.8878 56.107 54.4906 56.107 57.475Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM59.5985 100.062C58.2886 98.7855 57.7292 96.3665 56.8799 90.4178C55.499 80.7535 55.1982 74.7716 55.997 72.8766C57.0578 70.3651 59.9361 68.9398 63.9624 68.9235C66.7168 68.9122 68.9528 69.5927 70.4256 70.8463C70.5152 70.9063 70.6022 70.9666 70.6864 71.0272C71.3979 71.5491 71.9153 72.0905 72.2387 72.7494C72.5621 73.4082 72.7238 74.191 72.8209 75.4729C72.8856 76.758 72.8856 78.5388 72.5945 81.8657C72.3034 85.1828 71.7213 90.046 71.2685 93.1609C70.8158 96.2921 70.4924 97.662 70.0719 98.6731C69.6515 99.7168 69.1017 100.402 68.4873 100.891C67.8728 101.38 67.1613 101.674 66.4822 101.837C65.803 102 65.1885 102 64.6064 102C64.0243 102 63.4745 102 62.7307 101.837C61.9868 101.674 61.0166 101.38 60.3051 100.793C60.0389 100.583 59.8058 100.339 59.5985 100.062ZM30.4934 66.7708C32.8219 77.9322 39.2253 86.3212 49.2185 91.2789L49.3802 91.3996C51.1265 92.2476 52.7759 92.9652 53.0023 92.9652C53.358 92.9652 53.3904 92.5411 53.2287 91.0734C52.9699 88.6598 52.6789 88.1705 51.3206 87.5834C50.7061 87.3225 49.2185 86.4744 47.9895 85.659C41.7155 81.582 36.9291 74.7325 35.0857 67.2633C34.3419 64.1973 34.3419 56.7281 35.0857 53.6621C37.867 42.3768 46.987 33.4072 58.015 31.1241C61.2167 30.4717 66.9733 30.4717 70.1427 31.1241C81.0414 33.4072 89.935 41.9528 92.878 52.9446C93.8159 56.4019 93.9129 63.8711 93.072 67.2307C91.7461 72.6124 88.8678 77.7332 84.9546 81.6472C82.6908 83.9369 79.004 86.5853 76.8372 87.4986C75.4789 88.0727 75.1878 88.575 74.8967 90.9886C74.735 92.4922 74.7674 92.864 75.1231 92.864C76.0933 92.864 82.1086 89.5959 84.5665 87.7237C91.455 82.4854 96.1767 74.6901 97.6644 66.1315C98.1171 63.4243 98.1171 56.8618 97.632 54.4547C96.2091 47.2236 93.4278 41.7343 88.4797 36.5482C81.9793 29.6759 73.4414 26 63.998 26C54.4576 26 45.8874 29.7444 39.2253 36.8287C34.6006 41.7343 31.8193 47.2823 30.364 54.3764C29.8142 57.0184 29.9112 63.8907 30.4934 66.7708ZM41.7802 65.7923C43.0091 70.5021 45.9844 75.3033 49.3478 78.0104L49.4837 78.0561C50.4862 78.8715 51.4241 79.5239 51.5534 79.5239C51.686 79.5239 51.7928 78.4149 51.7928 77.045V74.5661L50.124 72.5439C43.7853 64.879 44.238 54.1253 51.1912 47.178C54.0275 44.3468 57.2227 42.7356 61.3461 42.0572C64.0627 41.607 64.6125 41.607 67.1997 42.0898C71.2099 42.8302 74.3146 44.4349 76.9989 47.1682C83.9197 54.1873 84.3725 64.8855 78.0338 72.5439L76.3521 74.5661V77.0319C76.3521 78.3823 76.4491 79.4912 76.5461 79.4912C77.1282 79.4912 80.4269 76.5525 81.7852 74.801C84.5342 71.3338 86.0865 67.6025 86.701 63.059C88.2533 51.7834 80.2652 40.4165 69.0108 37.8888C65.7444 37.1614 59.8584 37.3734 57.2065 38.3193C49.4448 41.108 43.85 47.0442 41.8125 54.7026C41.0363 57.5403 41.0363 62.9481 41.7802 65.7923ZM56.107 57.475C56.1322 60.0583 57.0774 62.1738 58.8513 63.6215C60.3071 65.0668 62.3048 65.9586 64.5091 65.9586C68.9636 65.9586 72.5747 62.3166 72.5747 57.824C72.5747 53.5551 69.3143 50.0544 65.1681 49.7162C63.5705 49.5199 61.7882 49.7367 60.6023 50.3646C57.7563 51.8878 56.107 54.4906 56.107 57.475Z" />
                </svg>
                `
      }
    }
  },
  'Google Podcast': {
    brand: {
      circle: `
            <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#4285F4"/>
            <path d="M36.001 57.808C33.7871 57.808 32 59.5947 32 61.808V66.1547C32 66.6799 32.1035 67.2001 32.3046 67.6854C32.5056 68.1707 32.8003 68.6116 33.1719 68.9831C33.5434 69.3545 33.9845 69.6492 34.4699 69.8502C34.9553 70.0512 35.4756 70.1547 36.001 70.1547C36.5264 70.1547 37.0467 70.0512 37.5321 69.8502C38.0175 69.6492 38.4586 69.3545 38.8301 68.9831C39.2017 68.6116 39.4964 68.1707 39.6974 67.6854C39.8985 67.2001 40.002 66.6799 40.002 66.1547V61.808C40.002 59.5947 38.2149 57.808 36.001 57.808ZM91.999 57.808C89.7851 57.808 87.998 59.5947 87.998 61.808V66.1547C87.998 67.2155 88.4195 68.2329 89.1699 68.9831C89.9202 69.7332 90.9379 70.1547 91.999 70.1547C93.0601 70.1547 94.0778 69.7332 94.8281 68.9831C95.5785 68.2329 96 67.2155 96 66.1547V61.808C96 59.5947 94.2129 57.808 91.999 57.808ZM49.8098 70.8987C47.5959 70.8987 45.8088 72.6853 45.8088 74.8987V79.2453C45.8088 80.3062 46.2303 81.3236 46.9807 82.0737C47.731 82.8239 48.7487 83.2453 49.8098 83.2453C50.8709 83.2453 51.8886 82.8239 52.6389 82.0737C53.3893 81.3236 53.8108 80.3062 53.8108 79.2453V74.9253C53.8108 72.712 52.0237 70.9253 49.8098 70.9253V70.8987ZM49.8098 44.72C47.5959 44.72 45.8088 46.5067 45.8088 48.72V63.0053C45.8088 64.0662 46.2303 65.0836 46.9807 65.8337C47.731 66.5839 48.7487 67.0053 49.8098 67.0053C50.8709 67.0053 51.8886 66.5839 52.6389 65.8337C53.3893 65.0836 53.8108 64.0662 53.8108 63.0053V48.688C53.8108 46.4747 52.0237 44.688 49.8098 44.688V44.72ZM78.1849 44.72C75.971 44.72 74.1839 46.5067 74.1839 48.72V53.0933C74.1839 54.1542 74.6054 55.1716 75.3557 55.9218C76.1061 56.6719 77.1238 57.0933 78.1849 57.0933C79.246 57.0933 80.2637 56.6719 81.014 55.9218C81.7643 55.1716 82.1859 54.1542 82.1859 53.0933V48.72C82.1859 46.5067 80.3988 44.72 78.1849 44.72ZM64 32C61.7861 32 59.999 33.7867 59.999 36V40.3467C59.999 41.4075 60.4205 42.4249 61.1709 43.1751C61.9212 43.9252 62.9389 44.3467 64 44.3467C65.0611 44.3467 66.0788 43.9252 66.8291 43.1751C67.5795 42.4249 68.001 41.4075 68.001 40.3467V36C68.001 33.7867 66.2139 32.0027 64 32.0027V32ZM64 83.6133C61.7861 83.6133 59.999 85.4 59.999 87.6133V91.9866C59.9972 92.5119 60.099 93.0324 60.2985 93.5184C60.4979 94.0044 60.7911 94.4463 61.1614 94.819C61.5317 95.1917 61.9718 95.4878 62.4566 95.6904C62.9413 95.893 63.4612 95.9982 63.9867 96C64.5121 96.0017 65.0327 95.9 65.5188 95.7006C66.0049 95.5012 66.4469 95.208 66.8197 94.8378C67.1925 94.4676 67.4887 94.0277 67.6913 93.543C67.894 93.0584 67.9992 92.5386 68.001 92.0133V87.64C68.001 85.4533 66.2139 83.64 64 83.64V83.6133ZM78.1875 61.0613C75.9737 61.0613 74.1865 62.8747 74.1865 65.0613V79.2693C74.1865 80.3302 74.6081 81.3476 75.3584 82.0977C76.1087 82.8479 77.1264 83.2693 78.1875 83.2693C79.2487 83.2693 80.2663 82.8479 81.0167 82.0977C81.767 81.3476 82.1885 80.3302 82.1885 79.2693V65.0587C82.1885 62.8453 80.4014 61.0587 78.1875 61.0587V61.0613ZM64 48.3413C61.7861 48.3413 59.999 50.1547 59.999 52.3413V75.616C59.999 76.6769 60.4205 77.6943 61.1709 78.4444C61.9212 79.1946 62.9389 79.616 64 79.616C65.0611 79.616 66.0788 79.1946 66.8291 78.4444C67.5795 77.6943 68.001 76.6769 68.001 75.616V52.368C68.001 50.1547 66.2139 48.368 64 48.368V48.3413Z" fill="white"/>
            </svg>
            `,
      square: `
            <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="#4285F4"/>
            <path d="M36.001 57.808C33.7871 57.808 32 59.5947 32 61.808V66.1547C32 66.6799 32.1035 67.2001 32.3046 67.6854C32.5056 68.1707 32.8003 68.6116 33.1719 68.9831C33.5434 69.3545 33.9845 69.6492 34.4699 69.8502C34.9553 70.0512 35.4756 70.1547 36.001 70.1547C36.5264 70.1547 37.0467 70.0512 37.5321 69.8502C38.0175 69.6492 38.4586 69.3545 38.8301 68.9831C39.2017 68.6116 39.4964 68.1707 39.6974 67.6854C39.8985 67.2001 40.002 66.6799 40.002 66.1547V61.808C40.002 59.5947 38.2149 57.808 36.001 57.808ZM91.999 57.808C89.7851 57.808 87.998 59.5947 87.998 61.808V66.1547C87.998 67.2155 88.4195 68.2329 89.1699 68.9831C89.9202 69.7332 90.9379 70.1547 91.999 70.1547C93.0601 70.1547 94.0778 69.7332 94.8281 68.9831C95.5785 68.2329 96 67.2155 96 66.1547V61.808C96 59.5947 94.2129 57.808 91.999 57.808ZM49.8098 70.8987C47.5959 70.8987 45.8088 72.6853 45.8088 74.8987V79.2453C45.8088 80.3062 46.2303 81.3236 46.9807 82.0737C47.731 82.8239 48.7487 83.2453 49.8098 83.2453C50.8709 83.2453 51.8886 82.8239 52.6389 82.0737C53.3893 81.3236 53.8108 80.3062 53.8108 79.2453V74.9253C53.8108 72.712 52.0237 70.9253 49.8098 70.9253V70.8987ZM49.8098 44.72C47.5959 44.72 45.8088 46.5067 45.8088 48.72V63.0053C45.8088 64.0662 46.2303 65.0836 46.9807 65.8337C47.731 66.5839 48.7487 67.0053 49.8098 67.0053C50.8709 67.0053 51.8886 66.5839 52.6389 65.8337C53.3893 65.0836 53.8108 64.0662 53.8108 63.0053V48.688C53.8108 46.4747 52.0237 44.688 49.8098 44.688V44.72ZM78.1849 44.72C75.971 44.72 74.1839 46.5067 74.1839 48.72V53.0933C74.1839 54.1542 74.6054 55.1716 75.3557 55.9218C76.1061 56.6719 77.1238 57.0933 78.1849 57.0933C79.246 57.0933 80.2637 56.6719 81.014 55.9218C81.7643 55.1716 82.1859 54.1542 82.1859 53.0933V48.72C82.1859 46.5067 80.3988 44.72 78.1849 44.72ZM64 32C61.7861 32 59.999 33.7867 59.999 36V40.3467C59.999 41.4075 60.4205 42.4249 61.1709 43.1751C61.9212 43.9252 62.9389 44.3467 64 44.3467C65.0611 44.3467 66.0788 43.9252 66.8291 43.1751C67.5795 42.4249 68.001 41.4075 68.001 40.3467V36C68.001 33.7867 66.2139 32.0027 64 32.0027V32ZM64 83.6133C61.7861 83.6133 59.999 85.4 59.999 87.6133V91.9866C59.9972 92.5119 60.099 93.0324 60.2985 93.5184C60.4979 94.0044 60.7911 94.4463 61.1614 94.819C61.5317 95.1917 61.9718 95.4878 62.4566 95.6904C62.9413 95.893 63.4612 95.9982 63.9867 96C64.5121 96.0017 65.0327 95.9 65.5188 95.7006C66.0049 95.5012 66.4469 95.208 66.8197 94.8378C67.1925 94.4676 67.4887 94.0277 67.6913 93.543C67.894 93.0584 67.9992 92.5386 68.001 92.0133V87.64C68.001 85.4533 66.2139 83.64 64 83.64V83.6133ZM78.1875 61.0613C75.9737 61.0613 74.1865 62.8747 74.1865 65.0613V79.2693C74.1865 80.3302 74.6081 81.3476 75.3584 82.0977C76.1087 82.8479 77.1264 83.2693 78.1875 83.2693C79.2487 83.2693 80.2663 82.8479 81.0167 82.0977C81.767 81.3476 82.1885 80.3302 82.1885 79.2693V65.0587C82.1885 62.8453 80.4014 61.0587 78.1875 61.0587V61.0613ZM64 48.3413C61.7861 48.3413 59.999 50.1547 59.999 52.3413V75.616C59.999 76.6769 60.4205 77.6943 61.1709 78.4444C61.9212 79.1946 62.9389 79.616 64 79.616C65.0611 79.616 66.0788 79.1946 66.8291 78.4444C67.5795 77.6943 68.001 76.6769 68.001 75.616V52.368C68.001 50.1547 66.2139 48.368 64 48.368V48.3413Z" fill="white"/>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM32 61.808C32 59.5947 33.7871 57.808 36.001 57.808C38.2149 57.808 40.002 59.5947 40.002 61.808V66.1547C40.002 66.6799 39.8985 67.2001 39.6974 67.6854C39.4964 68.1707 39.2017 68.6116 38.8301 68.9831C38.4586 69.3545 38.0175 69.6492 37.5321 69.8502C37.0467 70.0512 36.5264 70.1547 36.001 70.1547C35.4756 70.1547 34.9553 70.0512 34.4699 69.8502C33.9845 69.6492 33.5434 69.3545 33.1719 68.9831C32.8003 68.6116 32.5056 68.1707 32.3046 67.6854C32.1035 67.2001 32 66.6799 32 66.1547V61.808ZM87.998 61.808C87.998 59.5947 89.7851 57.808 91.999 57.808C94.2129 57.808 96 59.5947 96 61.808V66.1547C96 67.2155 95.5785 68.2329 94.8281 68.9831C94.0778 69.7332 93.0601 70.1547 91.999 70.1547C90.9379 70.1547 89.9202 69.7332 89.1699 68.9831C88.4195 68.2329 87.998 67.2155 87.998 66.1547V61.808ZM45.8088 74.8987C45.8088 72.6853 47.5959 70.8987 49.8098 70.8987V70.9253C52.0237 70.9253 53.8108 72.712 53.8108 74.9253V79.2453C53.8108 80.3062 53.3893 81.3236 52.6389 82.0737C51.8886 82.8239 50.8709 83.2453 49.8098 83.2453C48.7487 83.2453 47.731 82.8239 46.9807 82.0737C46.2303 81.3236 45.8088 80.3062 45.8088 79.2453V74.8987ZM45.8088 48.72C45.8088 46.5067 47.5959 44.72 49.8098 44.72V44.688C52.0237 44.688 53.8108 46.4747 53.8108 48.688V63.0053C53.8108 64.0662 53.3893 65.0836 52.6389 65.8337C51.8886 66.5839 50.8709 67.0053 49.8098 67.0053C48.7487 67.0053 47.731 66.5839 46.9807 65.8337C46.2303 65.0836 45.8088 64.0662 45.8088 63.0053V48.72ZM74.1839 48.72C74.1839 46.5067 75.971 44.72 78.1849 44.72C80.3988 44.72 82.1859 46.5067 82.1859 48.72V53.0933C82.1859 54.1542 81.7643 55.1716 81.014 55.9218C80.2637 56.6719 79.246 57.0933 78.1849 57.0933C77.1238 57.0933 76.1061 56.6719 75.3557 55.9218C74.6054 55.1716 74.1839 54.1542 74.1839 53.0933V48.72ZM59.999 36C59.999 33.7867 61.7861 32 64 32V32.0027C66.2139 32.0027 68.001 33.7867 68.001 36V40.3467C68.001 41.4075 67.5795 42.4249 66.8291 43.1751C66.0788 43.9252 65.0611 44.3467 64 44.3467C62.9389 44.3467 61.9212 43.9252 61.1709 43.1751C60.4205 42.4249 59.999 41.4075 59.999 40.3467V36ZM59.999 87.6133C59.999 85.4 61.7861 83.6133 64 83.6133V83.64C66.2139 83.64 68.001 85.4533 68.001 87.64V92.0133C67.9992 92.5386 67.894 93.0584 67.6913 93.543C67.4887 94.0277 67.1925 94.4676 66.8197 94.8378C66.4469 95.208 66.0049 95.5012 65.5188 95.7006C65.0327 95.9 64.5121 96.0017 63.9867 96C63.4612 95.9982 62.9413 95.893 62.4566 95.6904C61.9718 95.4878 61.5317 95.1917 61.1614 94.819C60.7911 94.4463 60.4979 94.0044 60.2985 93.5184C60.099 93.0324 59.9972 92.5119 59.999 91.9866V87.6133ZM74.1865 65.0613C74.1865 62.8747 75.9737 61.0613 78.1875 61.0613V61.0587C80.4014 61.0587 82.1885 62.8453 82.1885 65.0587V79.2693C82.1885 80.3302 81.767 81.3476 81.0167 82.0977C80.2663 82.8479 79.2487 83.2693 78.1875 83.2693C77.1264 83.2693 76.1087 82.8479 75.3584 82.0977C74.6081 81.3476 74.1865 80.3302 74.1865 79.2693V65.0613ZM59.999 52.3413C59.999 50.1547 61.7861 48.3413 64 48.3413V48.368C66.2139 48.368 68.001 50.1547 68.001 52.368V75.616C68.001 76.6769 67.5795 77.6943 66.8291 78.4444C66.0788 79.1946 65.0611 79.616 64 79.616C62.9389 79.616 61.9212 79.1946 61.1709 78.4444C60.4205 77.6943 59.999 76.6769 59.999 75.616V52.3413Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM32 61.808C32 59.5947 33.7871 57.808 36.001 57.808C38.2149 57.808 40.002 59.5947 40.002 61.808V66.1547C40.002 66.6799 39.8985 67.2001 39.6974 67.6854C39.4964 68.1707 39.2017 68.6116 38.8301 68.9831C38.4586 69.3545 38.0175 69.6492 37.5321 69.8502C37.0467 70.0512 36.5264 70.1547 36.001 70.1547C35.4756 70.1547 34.9553 70.0512 34.4699 69.8502C33.9845 69.6492 33.5434 69.3545 33.1719 68.9831C32.8003 68.6116 32.5056 68.1707 32.3046 67.6854C32.1035 67.2001 32 66.6799 32 66.1547V61.808ZM87.998 61.808C87.998 59.5947 89.7851 57.808 91.999 57.808C94.2129 57.808 96 59.5947 96 61.808V66.1547C96 67.2155 95.5785 68.2329 94.8281 68.9831C94.0778 69.7332 93.0601 70.1547 91.999 70.1547C90.9379 70.1547 89.9202 69.7332 89.1699 68.9831C88.4195 68.2329 87.998 67.2155 87.998 66.1547V61.808ZM45.8088 74.8987C45.8088 72.6853 47.5959 70.8987 49.8098 70.8987V70.9253C52.0237 70.9253 53.8108 72.712 53.8108 74.9253V79.2453C53.8108 80.3062 53.3893 81.3236 52.6389 82.0737C51.8886 82.8239 50.8709 83.2453 49.8098 83.2453C48.7487 83.2453 47.731 82.8239 46.9807 82.0737C46.2303 81.3236 45.8088 80.3062 45.8088 79.2453V74.8987ZM45.8088 48.72C45.8088 46.5067 47.5959 44.72 49.8098 44.72V44.688C52.0237 44.688 53.8108 46.4747 53.8108 48.688V63.0053C53.8108 64.0662 53.3893 65.0836 52.6389 65.8337C51.8886 66.5839 50.8709 67.0053 49.8098 67.0053C48.7487 67.0053 47.731 66.5839 46.9807 65.8337C46.2303 65.0836 45.8088 64.0662 45.8088 63.0053V48.72ZM74.1839 48.72C74.1839 46.5067 75.971 44.72 78.1849 44.72C80.3988 44.72 82.1859 46.5067 82.1859 48.72V53.0933C82.1859 54.1542 81.7643 55.1716 81.014 55.9218C80.2637 56.6719 79.246 57.0933 78.1849 57.0933C77.1238 57.0933 76.1061 56.6719 75.3557 55.9218C74.6054 55.1716 74.1839 54.1542 74.1839 53.0933V48.72ZM59.999 36C59.999 33.7867 61.7861 32 64 32V32.0027C66.2139 32.0027 68.001 33.7867 68.001 36V40.3467C68.001 41.4075 67.5795 42.4249 66.8291 43.1751C66.0788 43.9252 65.0611 44.3467 64 44.3467C62.9389 44.3467 61.9212 43.9252 61.1709 43.1751C60.4205 42.4249 59.999 41.4075 59.999 40.3467V36ZM59.999 87.6133C59.999 85.4 61.7861 83.6133 64 83.6133V83.64C66.2139 83.64 68.001 85.4533 68.001 87.64V92.0133C67.9992 92.5386 67.894 93.0584 67.6913 93.543C67.4887 94.0277 67.1925 94.4676 66.8197 94.8378C66.4469 95.208 66.0049 95.5012 65.5188 95.7006C65.0327 95.9 64.5121 96.0017 63.9867 96C63.4612 95.9982 62.9413 95.893 62.4566 95.6904C61.9718 95.4878 61.5317 95.1917 61.1614 94.819C60.7911 94.4463 60.4979 94.0044 60.2985 93.5184C60.099 93.0324 59.9972 92.5119 59.999 91.9866V87.6133ZM74.1865 65.0613C74.1865 62.8747 75.9737 61.0613 78.1875 61.0613V61.0587C80.4014 61.0587 82.1885 62.8453 82.1885 65.0587V79.2693C82.1885 80.3302 81.767 81.3476 81.0167 82.0977C80.2663 82.8479 79.2487 83.2693 78.1875 83.2693C77.1264 83.2693 76.1087 82.8479 75.3584 82.0977C74.6081 81.3476 74.1865 80.3302 74.1865 79.2693V65.0613ZM59.999 52.3413C59.999 50.1547 61.7861 48.3413 64 48.3413V48.368C66.2139 48.368 68.001 50.1547 68.001 52.368V75.616C68.001 76.6769 67.5795 77.6943 66.8291 78.4444C66.0788 79.1946 65.0611 79.616 64 79.616C62.9389 79.616 61.9212 79.1946 61.1709 78.4444C60.4205 77.6943 59.999 76.6769 59.999 75.616V52.3413Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM32 61.808C32 59.5947 33.7871 57.808 36.001 57.808C38.2149 57.808 40.002 59.5947 40.002 61.808V66.1547C40.002 66.6799 39.8985 67.2001 39.6974 67.6854C39.4964 68.1707 39.2017 68.6116 38.8301 68.9831C38.4586 69.3545 38.0175 69.6492 37.5321 69.8502C37.0467 70.0512 36.5264 70.1547 36.001 70.1547C35.4756 70.1547 34.9553 70.0512 34.4699 69.8502C33.9845 69.6492 33.5434 69.3545 33.1719 68.9831C32.8003 68.6116 32.5056 68.1707 32.3046 67.6854C32.1035 67.2001 32 66.6799 32 66.1547V61.808ZM87.998 61.808C87.998 59.5947 89.7851 57.808 91.999 57.808C94.2129 57.808 96 59.5947 96 61.808V66.1547C96 67.2155 95.5785 68.2329 94.8281 68.9831C94.0778 69.7332 93.0601 70.1547 91.999 70.1547C90.9379 70.1547 89.9202 69.7332 89.1699 68.9831C88.4195 68.2329 87.998 67.2155 87.998 66.1547V61.808ZM45.8088 74.8987C45.8088 72.6853 47.5959 70.8987 49.8098 70.8987V70.9253C52.0237 70.9253 53.8108 72.712 53.8108 74.9253V79.2453C53.8108 80.3062 53.3893 81.3236 52.6389 82.0737C51.8886 82.8239 50.8709 83.2453 49.8098 83.2453C48.7487 83.2453 47.731 82.8239 46.9807 82.0737C46.2303 81.3236 45.8088 80.3062 45.8088 79.2453V74.8987ZM45.8088 48.72C45.8088 46.5067 47.5959 44.72 49.8098 44.72V44.688C52.0237 44.688 53.8108 46.4747 53.8108 48.688V63.0053C53.8108 64.0662 53.3893 65.0836 52.6389 65.8337C51.8886 66.5839 50.8709 67.0053 49.8098 67.0053C48.7487 67.0053 47.731 66.5839 46.9807 65.8337C46.2303 65.0836 45.8088 64.0662 45.8088 63.0053V48.72ZM74.1839 48.72C74.1839 46.5067 75.971 44.72 78.1849 44.72C80.3988 44.72 82.1859 46.5067 82.1859 48.72V53.0933C82.1859 54.1542 81.7643 55.1716 81.014 55.9218C80.2637 56.6719 79.246 57.0933 78.1849 57.0933C77.1238 57.0933 76.1061 56.6719 75.3557 55.9218C74.6054 55.1716 74.1839 54.1542 74.1839 53.0933V48.72ZM59.999 36C59.999 33.7867 61.7861 32 64 32V32.0027C66.2139 32.0027 68.001 33.7867 68.001 36V40.3467C68.001 41.4075 67.5795 42.4249 66.8291 43.1751C66.0788 43.9252 65.0611 44.3467 64 44.3467C62.9389 44.3467 61.9212 43.9252 61.1709 43.1751C60.4205 42.4249 59.999 41.4075 59.999 40.3467V36ZM59.999 87.6133C59.999 85.4 61.7861 83.6133 64 83.6133V83.64C66.2139 83.64 68.001 85.4533 68.001 87.64V92.0133C67.9992 92.5386 67.894 93.0584 67.6913 93.543C67.4887 94.0277 67.1925 94.4676 66.8197 94.8378C66.4469 95.208 66.0049 95.5012 65.5188 95.7006C65.0327 95.9 64.5121 96.0017 63.9867 96C63.4612 95.9982 62.9413 95.893 62.4566 95.6904C61.9718 95.4878 61.5317 95.1917 61.1614 94.819C60.7911 94.4463 60.4979 94.0044 60.2985 93.5184C60.099 93.0324 59.9972 92.5119 59.999 91.9866V87.6133ZM74.1865 65.0613C74.1865 62.8747 75.9737 61.0613 78.1875 61.0613V61.0587C80.4014 61.0587 82.1885 62.8453 82.1885 65.0587V79.2693C82.1885 80.3302 81.767 81.3476 81.0167 82.0977C80.2663 82.8479 79.2487 83.2693 78.1875 83.2693C77.1264 83.2693 76.1087 82.8479 75.3584 82.0977C74.6081 81.3476 74.1865 80.3302 74.1865 79.2693V65.0613ZM59.999 52.3413C59.999 50.1547 61.7861 48.3413 64 48.3413V48.368C66.2139 48.368 68.001 50.1547 68.001 52.368V75.616C68.001 76.6769 67.5795 77.6943 66.8291 78.4444C66.0788 79.1946 65.0611 79.616 64 79.616C62.9389 79.616 61.9212 79.1946 61.1709 78.4444C60.4205 77.6943 59.999 76.6769 59.999 75.616V52.3413Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM32 61.808C32 59.5947 33.7871 57.808 36.001 57.808C38.2149 57.808 40.002 59.5947 40.002 61.808V66.1547C40.002 66.6799 39.8985 67.2001 39.6974 67.6854C39.4964 68.1707 39.2017 68.6116 38.8301 68.9831C38.4586 69.3545 38.0175 69.6492 37.5321 69.8502C37.0467 70.0512 36.5264 70.1547 36.001 70.1547C35.4756 70.1547 34.9553 70.0512 34.4699 69.8502C33.9845 69.6492 33.5434 69.3545 33.1719 68.9831C32.8003 68.6116 32.5056 68.1707 32.3046 67.6854C32.1035 67.2001 32 66.6799 32 66.1547V61.808ZM87.998 61.808C87.998 59.5947 89.7851 57.808 91.999 57.808C94.2129 57.808 96 59.5947 96 61.808V66.1547C96 67.2155 95.5785 68.2329 94.8281 68.9831C94.0778 69.7332 93.0601 70.1547 91.999 70.1547C90.9379 70.1547 89.9202 69.7332 89.1699 68.9831C88.4195 68.2329 87.998 67.2155 87.998 66.1547V61.808ZM45.8088 74.8987C45.8088 72.6853 47.5959 70.8987 49.8098 70.8987V70.9253C52.0237 70.9253 53.8108 72.712 53.8108 74.9253V79.2453C53.8108 80.3062 53.3893 81.3236 52.6389 82.0737C51.8886 82.8239 50.8709 83.2453 49.8098 83.2453C48.7487 83.2453 47.731 82.8239 46.9807 82.0737C46.2303 81.3236 45.8088 80.3062 45.8088 79.2453V74.8987ZM45.8088 48.72C45.8088 46.5067 47.5959 44.72 49.8098 44.72V44.688C52.0237 44.688 53.8108 46.4747 53.8108 48.688V63.0053C53.8108 64.0662 53.3893 65.0836 52.6389 65.8337C51.8886 66.5839 50.8709 67.0053 49.8098 67.0053C48.7487 67.0053 47.731 66.5839 46.9807 65.8337C46.2303 65.0836 45.8088 64.0662 45.8088 63.0053V48.72ZM74.1839 48.72C74.1839 46.5067 75.971 44.72 78.1849 44.72C80.3988 44.72 82.1859 46.5067 82.1859 48.72V53.0933C82.1859 54.1542 81.7643 55.1716 81.014 55.9218C80.2637 56.6719 79.246 57.0933 78.1849 57.0933C77.1238 57.0933 76.1061 56.6719 75.3557 55.9218C74.6054 55.1716 74.1839 54.1542 74.1839 53.0933V48.72ZM59.999 36C59.999 33.7867 61.7861 32 64 32V32.0027C66.2139 32.0027 68.001 33.7867 68.001 36V40.3467C68.001 41.4075 67.5795 42.4249 66.8291 43.1751C66.0788 43.9252 65.0611 44.3467 64 44.3467C62.9389 44.3467 61.9212 43.9252 61.1709 43.1751C60.4205 42.4249 59.999 41.4075 59.999 40.3467V36ZM59.999 87.6133C59.999 85.4 61.7861 83.6133 64 83.6133V83.64C66.2139 83.64 68.001 85.4533 68.001 87.64V92.0133C67.9992 92.5386 67.894 93.0584 67.6913 93.543C67.4887 94.0277 67.1925 94.4676 66.8197 94.8378C66.4469 95.208 66.0049 95.5012 65.5188 95.7006C65.0327 95.9 64.5121 96.0017 63.9867 96C63.4612 95.9982 62.9413 95.893 62.4566 95.6904C61.9718 95.4878 61.5317 95.1917 61.1614 94.819C60.7911 94.4463 60.4979 94.0044 60.2985 93.5184C60.099 93.0324 59.9972 92.5119 59.999 91.9866V87.6133ZM74.1865 65.0613C74.1865 62.8747 75.9737 61.0613 78.1875 61.0613V61.0587C80.4014 61.0587 82.1885 62.8453 82.1885 65.0587V79.2693C82.1885 80.3302 81.767 81.3476 81.0167 82.0977C80.2663 82.8479 79.2487 83.2693 78.1875 83.2693C77.1264 83.2693 76.1087 82.8479 75.3584 82.0977C74.6081 81.3476 74.1865 80.3302 74.1865 79.2693V65.0613ZM59.999 52.3413C59.999 50.1547 61.7861 48.3413 64 48.3413V48.368C66.2139 48.368 68.001 50.1547 68.001 52.368V75.616C68.001 76.6769 67.5795 77.6943 66.8291 78.4444C66.0788 79.1946 65.0611 79.616 64 79.616C62.9389 79.616 61.9212 79.1946 61.1709 78.4444C60.4205 77.6943 59.999 76.6769 59.999 75.616V52.3413Z" />
                </svg>
                `
      }
    }
  },
  Instagram: {
    brand: {
      circle: `
    <svg width="128" height="129" viewBox="0 0 128 129" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="#E11B7E"/>
    <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="url(#paint0_linear)"/>
    <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="url(#paint1_radial)"/>
    <path d="M0 64.251C0 28.9048 28.6538 0 64 0C99.3462 0 128 28.9048 128 64.251C128 99.5972 99.3462 128.502 64 128.502C28.6538 128.502 0 99.5972 0 64.251Z" fill="url(#paint2_radial)"/>
    <path d="M64.0848 44.4062C53.2277 44.4062 44.5759 53.2277 44.5759 63.9152C44.5759 74.7723 53.2277 83.4241 64.0848 83.4241C74.7723 83.4241 83.5938 74.7723 83.5938 63.9152C83.5938 53.2277 74.7723 44.4062 64.0848 44.4062ZM64.0848 76.6384C57.1295 76.6384 51.3616 71.0402 51.3616 63.9152C51.3616 56.9598 56.9598 51.3616 64.0848 51.3616C71.0402 51.3616 76.6384 56.9598 76.6384 63.9152C76.6384 71.0402 71.0402 76.6384 64.0848 76.6384ZM88.8527 43.7277C88.8527 41.183 86.817 39.1473 84.2723 39.1473C81.7277 39.1473 79.692 41.183 79.692 43.7277C79.692 46.2723 81.7277 48.308 84.2723 48.308C86.817 48.308 88.8527 46.2723 88.8527 43.7277ZM101.746 48.308C101.406 42.2009 100.049 36.7723 95.6384 32.3616C91.2277 27.9509 85.7991 26.5938 79.692 26.2545C73.4152 25.9152 54.5848 25.9152 48.308 26.2545C42.2009 26.5938 36.942 27.9509 32.3616 32.3616C27.9509 36.7723 26.5938 42.2009 26.2545 48.308C25.9152 54.5848 25.9152 73.4152 26.2545 79.692C26.5938 85.7991 27.9509 91.058 32.3616 95.6384C36.942 100.049 42.2009 101.406 48.308 101.746C54.5848 102.085 73.4152 102.085 79.692 101.746C85.7991 101.406 91.2277 100.049 95.6384 95.6384C100.049 91.058 101.406 85.7991 101.746 79.692C102.085 73.4152 102.085 54.5848 101.746 48.308ZM93.6027 86.308C92.4152 89.7009 89.7009 92.2455 86.4777 93.6027C81.3884 95.6384 69.5134 95.1295 64.0848 95.1295C58.4866 95.1295 46.6116 95.6384 41.692 93.6027C38.2991 92.2455 35.7545 89.7009 34.3973 86.308C32.3616 81.3884 32.8705 69.5134 32.8705 63.9152C32.8705 58.4866 32.3616 46.6116 34.3973 41.5223C35.7545 38.2991 38.2991 35.7545 41.692 34.3973C46.6116 32.3616 58.4866 32.8705 64.0848 32.8705C69.5134 32.8705 81.3884 32.3616 86.4777 34.3973C89.7009 35.5848 92.2455 38.2991 93.6027 41.5223C95.6384 46.6116 95.1295 58.4866 95.1295 63.9152C95.1295 69.5134 95.6384 81.3884 93.6027 86.308Z" fill="white"/>
    <defs>
    <linearGradient id="paint0_linear" x1="14.0625" y1="-6.30702e-07" x2="56.4161" y2="115.03" gradientUnits="userSpaceOnUse">
    <stop offset="0.04" stop-color="#4263DF"/>
    <stop offset="1" stop-color="#D53585" stop-opacity="0"/>
    </linearGradient>
    <radialGradient id="paint1_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(28.1875 128.502) rotate(-36.2255) scale(118.384 118.525)">
    <stop offset="0.13" stop-color="#FFAD05"/>
    <stop offset="1" stop-color="#FF3F00" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="paint2_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(36.5625 128.502) rotate(-40.7121) scale(80.228 80.2754)">
    <stop offset="0.01" stop-color="#FDDB86"/>
    <stop offset="1" stop-color="#F06942" stop-opacity="0"/>
    </radialGradient>
    </defs>
    </svg>
    `,
      square: `
    <svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="128" height="128" rx="10" fill="#E11B7E"/>
    <rect width="128" height="128" rx="10" fill="url(#paint0_linear)"/>
    <rect width="128" height="128" rx="10" fill="url(#paint1_radial)"/>
    <rect width="128" height="128" rx="10" fill="url(#paint2_radial)"/>
    <path d="M64.0848 44.4062C53.2277 44.4062 44.5759 53.2277 44.5759 63.9152C44.5759 74.7723 53.2277 83.4241 64.0848 83.4241C74.7723 83.4241 83.5938 74.7723 83.5938 63.9152C83.5938 53.2277 74.7723 44.4062 64.0848 44.4062ZM64.0848 76.6384C57.1295 76.6384 51.3616 71.0402 51.3616 63.9152C51.3616 56.9598 56.9598 51.3616 64.0848 51.3616C71.0402 51.3616 76.6384 56.9598 76.6384 63.9152C76.6384 71.0402 71.0402 76.6384 64.0848 76.6384ZM88.8527 43.7277C88.8527 41.183 86.817 39.1473 84.2723 39.1473C81.7277 39.1473 79.692 41.183 79.692 43.7277C79.692 46.2723 81.7277 48.308 84.2723 48.308C86.817 48.308 88.8527 46.2723 88.8527 43.7277ZM101.746 48.308C101.406 42.2009 100.049 36.7723 95.6384 32.3616C91.2277 27.9509 85.7991 26.5938 79.692 26.2545C73.4152 25.9152 54.5848 25.9152 48.308 26.2545C42.2009 26.5938 36.942 27.9509 32.3616 32.3616C27.9509 36.7723 26.5938 42.2009 26.2545 48.308C25.9152 54.5848 25.9152 73.4152 26.2545 79.692C26.5938 85.7991 27.9509 91.058 32.3616 95.6384C36.942 100.049 42.2009 101.406 48.308 101.746C54.5848 102.085 73.4152 102.085 79.692 101.746C85.7991 101.406 91.2277 100.049 95.6384 95.6384C100.049 91.058 101.406 85.7991 101.746 79.692C102.085 73.4152 102.085 54.5848 101.746 48.308ZM93.6027 86.308C92.4152 89.7009 89.7009 92.2455 86.4777 93.6027C81.3884 95.6384 69.5134 95.1295 64.0848 95.1295C58.4866 95.1295 46.6116 95.6384 41.692 93.6027C38.2991 92.2455 35.7545 89.7009 34.3973 86.308C32.3616 81.3884 32.8705 69.5134 32.8705 63.9152C32.8705 58.4866 32.3616 46.6116 34.3973 41.5223C35.7545 38.2991 38.2991 35.7545 41.692 34.3973C46.6116 32.3616 58.4866 32.8705 64.0848 32.8705C69.5134 32.8705 81.3884 32.3616 86.4777 34.3973C89.7009 35.5848 92.2455 38.2991 93.6027 41.5223C95.6384 46.6116 95.1295 58.4866 95.1295 63.9152C95.1295 69.5134 95.6384 81.3884 93.6027 86.308Z" fill="white"/>
    <defs>
    <linearGradient id="paint0_linear" x1="14.0625" y1="-6.28238e-07" x2="56.125" y2="114.687" gradientUnits="userSpaceOnUse">
    <stop offset="0.04" stop-color="#4263DF"/>
    <stop offset="1" stop-color="#D53585" stop-opacity="0"/>
    </linearGradient>
    <radialGradient id="paint1_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(28.1875 128) rotate(-36.1187) scale(118.223)">
    <stop offset="0.13" stop-color="#FFAD05"/>
    <stop offset="1" stop-color="#FF3F00" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="paint2_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(36.5625 128) rotate(-40.6013) scale(80.0948)">
    <stop offset="0.01" stop-color="#FDDB86"/>
    <stop offset="1" stop-color="#F06942" stop-opacity="0"/>
    </radialGradient>
    </defs>
    </svg>
    `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.7919 0 64C0 99.2081 28.6538 128 64 128C99.3462 128 128 99.2081 128 64C128 28.7919 99.3462 0 64 0ZM44.5759 63.9152C44.5759 53.2277 53.2277 44.4062 64.0848 44.4062C74.7723 44.4062 83.5938 53.2277 83.5938 63.9152C83.5938 74.7723 74.7723 83.4241 64.0848 83.4241C53.2277 83.4241 44.5759 74.7723 44.5759 63.9152ZM51.3616 63.9152C51.3616 71.0402 57.1295 76.6384 64.0848 76.6384C71.0402 76.6384 76.6384 71.0402 76.6384 63.9152C76.6384 56.9598 71.0402 51.3616 64.0848 51.3616C56.9598 51.3616 51.3616 56.9598 51.3616 63.9152ZM84.2723 39.1473C86.817 39.1473 88.8527 41.183 88.8527 43.7277C88.8527 46.2723 86.817 48.308 84.2723 48.308C81.7277 48.308 79.692 46.2723 79.692 43.7277C79.692 41.183 81.7277 39.1473 84.2723 39.1473ZM95.6384 32.3616C100.049 36.7723 101.406 42.2009 101.746 48.308C102.085 54.5848 102.085 73.4152 101.746 79.692C101.406 85.7991 100.049 91.058 95.6384 95.6384C91.2277 100.049 85.7991 101.406 79.692 101.746C73.4152 102.085 54.5848 102.085 48.308 101.746C42.2009 101.406 36.942 100.049 32.3616 95.6384C27.9509 91.058 26.5938 85.7991 26.2545 79.692C25.9152 73.4152 25.9152 54.5848 26.2545 48.308C26.5938 42.2009 27.9509 36.7723 32.3616 32.3616C36.942 27.9509 42.2009 26.5938 48.308 26.2545C54.5848 25.9152 73.4152 25.9152 79.692 26.2545C85.7991 26.5938 91.2277 27.9509 95.6384 32.3616ZM86.4777 93.6027C89.7009 92.2455 92.4152 89.7009 93.6027 86.308C95.3325 82.1275 95.225 72.9247 95.1536 66.8187C95.141 65.7391 95.1295 64.7563 95.1295 63.9152C95.1295 63.1049 95.1408 62.1511 95.1533 61.0976C95.2247 55.0929 95.3345 45.852 93.6027 41.5223C92.2455 38.2991 89.7009 35.5848 86.4777 34.3973C82.148 32.6654 72.9071 32.7753 66.9024 32.8466C65.8489 32.8592 64.895 32.8705 64.0848 32.8705C63.2437 32.8705 62.2609 32.859 61.1813 32.8464C55.0753 32.775 45.8724 32.6674 41.692 34.3973C38.2991 35.7545 35.7545 38.2991 34.3973 41.5223C32.6654 45.852 32.7752 55.0929 32.8466 61.0976C32.8591 62.1511 32.8705 63.1049 32.8705 63.9152C32.8705 64.7563 32.859 65.7391 32.8464 66.8187C32.775 72.9247 32.6674 82.1275 34.3973 86.308C35.7545 89.7009 38.2991 92.2455 41.692 93.6027C45.8724 95.3326 55.0753 95.225 61.1813 95.1536C62.2609 95.141 63.2437 95.1295 64.0848 95.1295C64.895 95.1295 65.8489 95.1408 66.9024 95.1534C72.907 95.2247 82.148 95.3346 86.4777 93.6027Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10C0 4.47715 4.47715 0 10 0ZM44.5759 63.9152C44.5759 53.2277 53.2277 44.4062 64.0848 44.4062C74.7723 44.4062 83.5938 53.2277 83.5938 63.9152C83.5938 74.7723 74.7723 83.4241 64.0848 83.4241C53.2277 83.4241 44.5759 74.7723 44.5759 63.9152ZM51.3616 63.9152C51.3616 71.0402 57.1295 76.6384 64.0848 76.6384C71.0402 76.6384 76.6384 71.0402 76.6384 63.9152C76.6384 56.9598 71.0402 51.3616 64.0848 51.3616C56.9598 51.3616 51.3616 56.9598 51.3616 63.9152ZM84.2723 39.1473C86.817 39.1473 88.8527 41.183 88.8527 43.7277C88.8527 46.2723 86.817 48.308 84.2723 48.308C81.7277 48.308 79.692 46.2723 79.692 43.7277C79.692 41.183 81.7277 39.1473 84.2723 39.1473ZM95.6384 32.3616C100.049 36.7723 101.406 42.2009 101.746 48.308C102.085 54.5848 102.085 73.4152 101.746 79.692C101.406 85.7991 100.049 91.058 95.6384 95.6384C91.2277 100.049 85.7991 101.406 79.692 101.746C73.4152 102.085 54.5848 102.085 48.308 101.746C42.2009 101.406 36.942 100.049 32.3616 95.6384C27.9509 91.058 26.5938 85.7991 26.2545 79.692C25.9152 73.4152 25.9152 54.5848 26.2545 48.308C26.5938 42.2009 27.9509 36.7723 32.3616 32.3616C36.942 27.9509 42.2009 26.5938 48.308 26.2545C54.5848 25.9152 73.4152 25.9152 79.692 26.2545C85.7991 26.5938 91.2277 27.9509 95.6384 32.3616ZM86.4777 93.6027C89.7009 92.2455 92.4152 89.7009 93.6027 86.308C95.3325 82.1275 95.225 72.9247 95.1536 66.8187C95.141 65.7391 95.1295 64.7563 95.1295 63.9152C95.1295 63.105 95.1408 62.1511 95.1533 61.0976C95.2247 55.0929 95.3345 45.852 93.6027 41.5223C92.2455 38.2991 89.7009 35.5848 86.4777 34.3973C82.148 32.6654 72.9071 32.7753 66.9024 32.8466C65.8489 32.8592 64.895 32.8705 64.0848 32.8705C63.2437 32.8705 62.2609 32.859 61.1813 32.8464C55.0753 32.775 45.8724 32.6675 41.692 34.3973C38.2991 35.7545 35.7545 38.2991 34.3973 41.5223C32.6654 45.852 32.7752 55.0929 32.8466 61.0976C32.8591 62.1511 32.8705 63.105 32.8705 63.9152C32.8705 64.7563 32.859 65.7391 32.8464 66.8187C32.775 72.9247 32.6674 82.1275 34.3973 86.308C35.7545 89.7009 38.2991 92.2455 41.692 93.6027C45.8724 95.3326 55.0753 95.225 61.1813 95.1536C62.2609 95.141 63.2437 95.1295 64.0848 95.1295C64.895 95.1295 65.8489 95.1408 66.9024 95.1534C72.907 95.2247 82.148 95.3346 86.4777 93.6027Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM44.5759 63.9152C44.5759 53.2277 53.2277 44.4062 64.0848 44.4062C74.7723 44.4062 83.5938 53.2277 83.5938 63.9152C83.5938 74.7723 74.7723 83.4241 64.0848 83.4241C53.2277 83.4241 44.5759 74.7723 44.5759 63.9152ZM51.3616 63.9152C51.3616 71.0402 57.1295 76.6384 64.0848 76.6384C71.0402 76.6384 76.6384 71.0402 76.6384 63.9152C76.6384 56.9598 71.0402 51.3616 64.0848 51.3616C56.9598 51.3616 51.3616 56.9598 51.3616 63.9152ZM84.2723 39.1473C86.817 39.1473 88.8527 41.183 88.8527 43.7277C88.8527 46.2723 86.817 48.308 84.2723 48.308C81.7277 48.308 79.692 46.2723 79.692 43.7277C79.692 41.183 81.7277 39.1473 84.2723 39.1473ZM95.6384 32.3616C100.049 36.7723 101.406 42.2009 101.746 48.308C102.085 54.5848 102.085 73.4152 101.746 79.692C101.406 85.7991 100.049 91.058 95.6384 95.6384C91.2277 100.049 85.7991 101.406 79.692 101.746C73.4152 102.085 54.5848 102.085 48.308 101.746C42.2009 101.406 36.942 100.049 32.3616 95.6384C27.9509 91.058 26.5938 85.7991 26.2545 79.692C25.9152 73.4152 25.9152 54.5848 26.2545 48.308C26.5938 42.2009 27.9509 36.7723 32.3616 32.3616C36.942 27.9509 42.2009 26.5938 48.308 26.2545C54.5848 25.9152 73.4152 25.9152 79.692 26.2545C85.7991 26.5938 91.2277 27.9509 95.6384 32.3616ZM86.4777 93.6027C89.7009 92.2455 92.4152 89.7009 93.6027 86.308C95.3325 82.1275 95.225 72.9247 95.1536 66.8186C95.141 65.7391 95.1295 64.7563 95.1295 63.9152C95.1295 63.1049 95.1408 62.1511 95.1533 61.0976C95.2247 55.0929 95.3345 45.852 93.6027 41.5223C92.2455 38.2991 89.7009 35.5848 86.4777 34.3973C82.148 32.6654 72.9071 32.7752 66.9024 32.8466C65.8489 32.8591 64.895 32.8705 64.0848 32.8705C63.2437 32.8705 62.2609 32.859 61.1813 32.8464C55.0753 32.775 45.8724 32.6674 41.692 34.3973C38.2991 35.7545 35.7545 38.2991 34.3973 41.5223C32.6654 45.852 32.7752 55.0929 32.8466 61.0976C32.8591 62.1511 32.8705 63.1049 32.8705 63.9152C32.8705 64.7563 32.859 65.7391 32.8464 66.8186C32.775 72.9247 32.6674 82.1275 34.3973 86.308C35.7545 89.7009 38.2991 92.2455 41.692 93.6027C45.8724 95.3325 55.0753 95.225 61.1813 95.1536C62.2609 95.141 63.2437 95.1295 64.0848 95.1295C64.895 95.1295 65.8489 95.1408 66.9024 95.1533C72.907 95.2247 82.148 95.3345 86.4777 93.6027Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.14286 4H118.857C121.697 4 124 6.30253 124 9.14286V118.857C124 121.697 121.697 124 118.857 124H9.14286C6.30253 124 4 121.697 4 118.857V9.14286C4 6.30254 6.30254 4 9.14286 4ZM118.857 0H9.14286C4.0934 0 0 4.0934 0 9.14286V118.857C0 123.907 4.0934 128 9.14286 128H118.857C123.907 128 128 123.907 128 118.857V9.14286C128 4.0934 123.907 0 118.857 0ZM44.5759 63.9152C44.5759 53.2277 53.2277 44.4062 64.0849 44.4062C74.7724 44.4062 83.5938 53.2277 83.5938 63.9152C83.5938 74.7723 74.7724 83.4241 64.0849 83.4241C53.2277 83.4241 44.5759 74.7723 44.5759 63.9152ZM51.3617 63.9152C51.3617 71.0402 57.1295 76.6384 64.0849 76.6384C71.0402 76.6384 76.6384 71.0402 76.6384 63.9152C76.6384 56.9598 71.0402 51.3616 64.0849 51.3616C56.9599 51.3616 51.3617 56.9598 51.3617 63.9152ZM84.2724 39.1473C86.817 39.1473 88.8527 41.183 88.8527 43.7277C88.8527 46.2723 86.817 48.308 84.2724 48.308C81.7277 48.308 79.692 46.2723 79.692 43.7277C79.692 41.183 81.7277 39.1473 84.2724 39.1473ZM95.6384 32.3616C100.049 36.7723 101.406 42.2009 101.746 48.308C102.085 54.5848 102.085 73.4152 101.746 79.692C101.406 85.7991 100.049 91.058 95.6384 95.6384C91.2277 100.049 85.7992 101.406 79.692 101.746C73.4152 102.085 54.5849 102.085 48.3081 101.746C42.2009 101.406 36.942 100.049 32.3617 95.6384C27.9509 91.058 26.5938 85.7991 26.2545 79.692C25.9152 73.4152 25.9152 54.5848 26.2545 48.308C26.5938 42.2009 27.9509 36.7723 32.3617 32.3616C36.942 27.9509 42.2009 26.5938 48.3081 26.2545C54.5849 25.9152 73.4152 25.9152 79.692 26.2545C85.7992 26.5938 91.2277 27.9509 95.6384 32.3616ZM86.4777 93.6027C89.7009 92.2455 92.4152 89.7009 93.6027 86.308C95.3325 82.1275 95.225 72.9247 95.1536 66.8186C95.141 65.7391 95.1295 64.7563 95.1295 63.9152C95.1295 63.1049 95.1408 62.1511 95.1533 61.0976C95.2247 55.0929 95.3345 45.852 93.6027 41.5223C92.2456 38.2991 89.7009 35.5848 86.4777 34.3973C82.148 32.6654 72.907 32.7752 66.9024 32.8466C65.849 32.8591 64.8951 32.8705 64.0849 32.8705C63.2438 32.8705 62.261 32.859 61.1814 32.8464C55.0754 32.775 45.8725 32.6674 41.692 34.3973C38.2992 35.7545 35.7545 38.2991 34.3974 41.5223C32.6655 45.852 32.7753 55.0929 32.8467 61.0976C32.8592 62.1511 32.8706 63.1049 32.8706 63.9152C32.8706 64.7563 32.8591 65.7391 32.8465 66.8187C32.7751 72.9247 32.6675 82.1275 34.3974 86.308C35.7545 89.7009 38.2992 92.2455 41.692 93.6027C45.8725 95.3325 55.0754 95.225 61.1814 95.1536C62.261 95.141 63.2438 95.1295 64.0849 95.1295C64.8951 95.1295 65.8489 95.1408 66.9024 95.1533C72.907 95.2247 82.148 95.3345 86.4777 93.6027Z" />
                </svg>
                `
      }
    }
  },
  Telegram: {
    brand: {
      circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="64" fill="url(#paint0_linear)"/>
            <path d="M101.782 37.8045C102.801 33.0312 100.084 31.156 97.0287 32.3493L29.4619 58.432C24.8782 60.3072 25.048 62.8643 28.7828 64.0577L45.9292 69.3424L85.9939 44.1121C87.8613 42.7483 89.7288 43.6007 88.2009 44.794L55.7756 74.1157L54.5872 92.0156C56.4547 92.0156 57.1337 91.3337 58.1523 90.3108L66.4708 82.128L83.9567 95.0841C87.1823 96.9593 89.559 95.9365 90.4078 92.186L101.782 37.8045Z" fill="white"/>
            <defs>
            <linearGradient id="paint0_linear" x1="82.5704" y1="19.921" x2="50.5554" y2="94.6198" gradientUnits="userSpaceOnUse">
            <stop stop-color="#37AEE2"/>
            <stop offset="1" stop-color="#1E96C8"/>
            </linearGradient>
            </defs>
            </svg>
            `,
      square: `<svg width="128" height="128" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="128" height="128" rx="10" fill="url(#paint0_linear)"/>
            <path d="M101.782 37.8045C102.801 33.0312 100.084 31.156 97.0287 32.3493L29.4619 58.432C24.8782 60.3072 25.048 62.8643 28.7828 64.0577L45.9292 69.3424L85.9939 44.1121C87.8613 42.7483 89.7288 43.6007 88.2009 44.794L55.7756 74.1157L54.5872 92.0156C56.4547 92.0156 57.1337 91.3337 58.1523 90.3108L66.4708 82.128L83.9567 95.0841C87.1823 96.9593 89.559 95.9365 90.4078 92.186L101.782 37.8045Z" fill="white"/>
            <defs>
            <linearGradient id="paint0_linear" x1="82.5704" y1="19.921" x2="50.5554" y2="94.6198" gradientUnits="userSpaceOnUse">
            <stop stop-color="#37AEE2"/>
            <stop offset="1" stop-color="#1E96C8"/>
            </linearGradient>
            </defs>
            </svg>
            `
    },
    custom: {
      filled: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128" fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64C128 28.6538 99.3462 0 64 0ZM97.0287 32.3493C100.084 31.156 102.801 33.0312 101.782 37.8045L90.4078 92.186C89.559 95.9365 87.1823 96.9593 83.9567 95.0841L66.4708 82.128L58.1523 90.3108C57.1337 91.3337 56.4547 92.0156 54.5872 92.0156L55.7756 74.1157L88.2009 44.794C89.7288 43.6007 87.8613 42.7483 85.9939 44.1121L45.9292 69.3424L28.7828 64.0577C25.048 62.8643 24.8782 60.3072 29.4619 58.432L97.0287 32.3493Z" />
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128"  fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0C4.47715 0 0 4.47715 0 10V118C0 123.523 4.47715 128 10 128H118C123.523 128 128 123.523 128 118V10C128 4.47715 123.523 0 118 0H10ZM97.0287 32.3493C100.084 31.156 102.801 33.0312 101.782 37.8045L90.4078 92.186C89.559 95.9365 87.1823 96.9593 83.9567 95.0841L66.4708 82.128L58.1523 90.3108C57.1337 91.3337 56.4547 92.0156 54.5872 92.0156L55.7756 74.1157L88.2009 44.794C89.7288 43.6007 87.8613 42.7483 85.9939 44.1121L45.9292 69.3424L28.7828 64.0577C25.048 62.8643 24.8782 60.3072 29.4619 58.432L97.0287 32.3493Z" />
                </svg>
                `
      },
      outline: {
        circle: `<svg width="128" height="128" viewBox="0 0 128 128"  fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M64 4C97.1371 4 124 30.8629 124 64C124 97.1371 97.1371 124 64 124C30.8629 124 4 97.1371 4 64C4 30.8629 30.8629 4 64 4ZM128 64C128 28.6538 99.3462 0 64 0C28.6538 0 0 28.6538 0 64C0 99.3462 28.6538 128 64 128C99.3462 128 128 99.3462 128 64ZM97.0287 32.3493C100.084 31.156 102.801 33.0312 101.782 37.8045L90.4078 92.186C89.559 95.9365 87.1823 96.9593 83.9567 95.0841L66.4708 82.128L58.1523 90.3108C57.1337 91.3337 56.4547 92.0156 54.5872 92.0156L55.7756 74.1157L88.2009 44.794C89.7288 43.6007 87.8613 42.7483 85.9939 44.1121L45.9292 69.3424L28.7828 64.0577C25.048 62.8643 24.8782 60.3072 29.4619 58.432L97.0287 32.3493Z"/>
                </svg>
                `,
        square: `<svg width="128" height="128" viewBox="0 0 128 128"  fill="#23242E" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 4H118C121.314 4 124 6.68629 124 10V118C124 121.314 121.314 124 118 124H10C6.68629 124 4 121.314 4 118V10C4 6.68629 6.68629 4 10 4ZM0 10C0 4.47715 4.47715 0 10 0H118C123.523 0 128 4.47715 128 10V118C128 123.523 123.523 128 118 128H10C4.47715 128 0 123.523 0 118V10ZM97.0287 32.3493C100.084 31.156 102.801 33.0312 101.782 37.8045L90.4078 92.186C89.559 95.9365 87.1823 96.9593 83.9567 95.0841L66.4708 82.128L58.1523 90.3108C57.1337 91.3337 56.4547 92.0156 54.5872 92.0156L55.7756 74.1157L88.2009 44.794C89.7288 43.6007 87.8613 42.7483 85.9939 44.1121L45.9292 69.3424L28.7828 64.0577C25.048 62.8643 24.8782 60.3072 29.4619 58.432L97.0287 32.3493Z" />
                </svg>
                `
      }
    }
  }
};

function SocialIcon({
  item: {
    socialIcons: {
      items,
      shape,
      type
    },
    settings: {
      socialIcons: {
        brandType
      }
    }
  },
  className,
  wrapperClass
}) {
  const selectedIcon = (name, brandType, shape, type) => {
    if (name.includes('Item')) {
      name = 'Facebook';
    }

    if (brandType === 'brand') {
      return socialIcons[name].brand[shape];
    }

    return socialIcons[name].custom[type][shape];
  };

  return React.createElement("ul", {
    className: `${className} ${wrapperClass}`
  }, items.map(item => {
    return React.createElement("li", {
      key: item.id
    }, React.createElement("a", {
      href: item.url,
      target: item.target,
      dangerouslySetInnerHTML: {
        __html: selectedIcon(item.label, brandType, shape, type)
      }
    }));
  }));
}

const CounterDown = ({
  item,
  className,
  wrapperClass
}) => {
  const {
    time,
    date,
    finishText,
    GMT,
    settings: {
      zero,
      onlyHours,
      style
    },
    text: counterText
  } = item.counter;
  let deadLine;

  if (date && time) {
    deadLine = Date.parse(`${date} ${time}  ${GMT ? 'GMT' + GMT : ''}`);
  }

  let timesUnit = ['days', 'hours', 'minutes', 'seconds'];
  return React.createElement("div", {
    className: `${className} ${wrapperClass}`
  }, React.createElement(React.Fragment, null, deadLine - Date.now() > 0 ? timesUnit.map((time, key) => {
    return React.createElement(React.Fragment, {
      key: key
    }, React.createElement("div", {
      className: `counter-wrapper`
    }, React.createElement("span", {
      className: `counter-value ${time}`
    }), React.createElement("span", {
      className: `counter-text ${time}-text`
    }, counterText[time])), style !== 'none' && time !== 'seconds' && React.createElement("div", {
      className: `counter--separator ${time}-separator`
    }, style === 'colon' && React.createElement("span", {
      className: "counter--separator__colon"
    }, ":"), style === 'line' && React.createElement("span", {
      className: "counter--separator__line"
    })));
  }) : React.createElement("span", {
    className: "finish-text"
  }, finishText), React.createElement("span", {
    "data-time": `${deadLine}`,
    className: "getTime",
    "data-only-hours": `${onlyHours}`,
    "data-zero": `${zero}`,
    "data-counter": wrapperClass,
    "data-finish-text": finishText
  })));
};

function ContactForm({
  item,
  className,
  interactionDisabled
}) {
  const {
    id,
    fields = {},
    form = {},
    settings = {}
  } = item;
  const {
    service = 'email'
  } = settings;
  const previewFields = [{
    type: 'text',
    name: 'name',
    fieldType: 'name',
    component: 'input',
    className: 'form-control'
  }, {
    type: 'email',
    name: 'email',
    required: true,
    fieldType: 'email',
    component: 'input',
    className: 'form-control'
  }, {
    type: 'text',
    name: 'phone',
    fieldType: 'phone',
    component: 'input',
    className: 'form-control'
  }, {
    type: 'text',
    name: 'subject',
    fieldType: 'subject',
    component: 'input',
    className: 'form-control'
  }, {
    name: 'message',
    fieldType: 'message',
    component: 'textarea',
    className: 'form-control'
  }, {
    type: 'submit',
    name: 'button',
    fieldType: 'button',
    component: 'button',
    onClick: e => interactionDisabled && e.preventDefault()
  }];

  const isEnabled = data => data ? data._enabled : false;

  const mergedFields = previewFields.map(field => {
    const data = fields[field.fieldType];
    return data ? { ...field,
      data
    } : field;
  });

  const getFormGroupClass = field => `form-group ${field.fieldType === 'button' ? 'form-button' : ''}`.trim();

  const toEmail = form.toEmail ? Buffer.from(form.toEmail).toString('base64') : '';
  return React.createElement("form", {
    id: id,
    className: className
  }, service === 'email' ? React.createElement("input", {
    type: "hidden",
    name: "_t",
    value: toEmail
  }) : null, React.createElement("div", {
    className: "fields-wrapper"
  }, mergedFields.map((field, index) => isEnabled(field.data) ? React.createElement("div", {
    className: getFormGroupClass(field)
  }, field.data.label && React.createElement("label", null, field.data.label), React.createElement(RenderField, {
    field: field
  })) : null)));
}

function RenderField({
  field
}) {
  const {
    component,
    fieldType,
    data = {},
    ...fieldAttr
  } = field;
  const {
    text
  } = data;
  return React.createElement(component, { ...fieldAttr,
    ...data.attr
  }, text);
}

function getACId(formUrl) {
  return parseACUrl(formUrl)[1];
}
function getACUrl(formUrl) {
  return parseACUrl(formUrl)[0];
}

function parseACUrl(formUrl = '') {
  return formUrl.split('/f/');
}

const hiddenFields = {
  mailerlite: {
    ajax: 1,
    'ml-submit': 1
  }
};

function Subscription({
  item,
  className,
  interactionDisabled
}) {
  var _form$button;

  const {
    form = {},
    fields = {},
    settings = {}
  } = item;
  const {
    service = 'mailchimp',
    webhook = ''
  } = settings;

  const getName = name => {
    const serviceKeys = {
      default: {
        email: 'email',
        firstName: 'firstName',
        lastName: 'lastName'
      },
      mailchimp: {
        email: 'EMAIL',
        firstName: 'FNAME',
        lastName: 'LNAME'
      },
      sendinblue: {
        email: 'EMAIL',
        firstName: 'NAME',
        lastName: 'SURNAME'
      },
      emailoctopus: {
        email: 'field_0',
        firstName: 'field_1',
        lastName: 'field_2'
      },
      mailerlite: {
        email: 'fields[email]',
        firstName: 'fields[name]',
        lastName: 'fields[last_name]'
      },
      activecampaign: {
        email: 'email',
        firstName: 'firstname',
        lastName: 'lastname'
      },
      convertkit: {
        email: 'email_address',
        firstName: 'fields[first_name]',
        lastName: 'fields[last_name]'
      }
    };
    const names = serviceKeys[service];
    return names ? names[name] : serviceKeys.default[name];
  };

  const formFields = Object.entries(fields).map(([name, field], i) => false === field._enabled ? null : React.createElement("div", {
    className: "form-group",
    key: i
  }, field.label && React.createElement("label", {
    className: "form-label"
  }, field.label), React.createElement("input", _extends({
    name: getName(name),
    className: "form-control",
    required: name === 'email',
    type: name !== 'email' ? 'text' : 'email'
  }, field.attr))));

  const renderHiddenFields = serviceFields => {
    if (!serviceFields) return null;
    const data = typeof serviceFields === 'function' ? serviceFields(settings) : serviceFields;
    return Object.entries(data).map(([name, value]) => React.createElement("input", {
      type: "hidden",
      value: value,
      name: name
    }));
  };

  return React.createElement("form", {
    id: item.id,
    className: className
  }, React.createElement("div", {
    className: "fields-wrapper"
  }, formFields, renderHiddenFields(hiddenFields[service]), React.createElement("button", {
    onClick: e => interactionDisabled && e.preventDefault(),
    type: "submit"
  }, React.createElement("span", {
    className: "loader hidden"
  }, React.createElement("svg", {
    viewBox: "0 0 100 100",
    xmlns: "http://www.w3.org/2000/svg",
    style: {
      verticalAlign: 'middle',
      height: '2em'
    }
  }, React.createElement("path", {
    fill: "#fff",
    d: "M73 50c0-12.7-10.3-23-23-23S27 37.3 27 50m3.9 0c0-10.5 8.5-19.1 19.1-19.1S69.1 39.5 69.1 50"
  }, React.createElement("animateTransform", {
    attributeName: "transform",
    attributeType: "XML",
    type: "rotate",
    dur: "1s",
    from: "0 50 50",
    to: "360 50 50",
    repeatCount: "indefinite"
  })))), React.createElement("span", {
    className: "button-content"
  }, form === null || form === void 0 ? void 0 : (_form$button = form.button) === null || _form$button === void 0 ? void 0 : _form$button.content))), service === 'activecampaign' && React.createElement("div", {
    style: {
      display: 'none'
    },
    className: "success-message"
  }, form.successMessage));
}

function buildAnimAttr(item) {
  if (!item.animation) return null;
  const digitPat = /^\d/;
  const prefix = 'data-aos';
  return Object.entries(item.animation).map(([prop, val]) => {
    const dataAttr = prop === 'type' ? prefix : `${prefix}-${prop}`;
    return [dataAttr, digitPat.test(val) ? parseInt(val) : val];
  }).filter(([_, val]) => val != null && val !== '');
}

/**
 *
 * @param {array} attrs
 */

function buildAttributes(attrs = []) {
  const props = {};
  [...attrs].map(([prop, val]) => {
    if (!prop.startsWith('__')) {
      props[kebabCase_1(prop)] = val;
    } else if (prop.includes('__data')) {
      const dataAttr = getDataAttr$1(val);
      dataAttr.filter(v => v).forEach(item => props[item.key] = item.value);
    }
  });
  return props;
}

function getDataAttr$1(val = []) {
  return val.map(item => {
    const [key, val] = item.split('=');

    if (key || val) {
      const value = val.replace(/["']/g, '');
      return {
        key,
        value
      };
    }
  });
}

function getWrapperClass(className, item) {
  var _item$settings;

  const [dc] = className.split(' ');
  const hideClass = getHideClass(item === null || item === void 0 ? void 0 : (_item$settings = item.settings) === null || _item$settings === void 0 ? void 0 : _item$settings.hideOn);
  return `${dc}-wrapper ${hideClass}`;
}

const planElements = {};

function getCurrentPlan(roles) {
  let role = '';

  if (roles.includes('ADMIN')) {
    role = 'PLAN_3';
  } else if (roles.includes('PLAN_1')) {
    role = 'PLAN_1';
  } else if (roles.includes('PLAN_2')) {
    role = 'PLAN_2';
  } else if (roles.includes('PLAN_3')) {
    role = 'PLAN_3';
  } else if (roles.includes('DORIKBETA')) {
    role = 'PLAN_3';
  }

  return role;
}

library.add(fas);
library.add(fab);
library.add(far);
function RenderComponent({
  item,
  children,
  userRoles,
  addIntegration,
  interactionDisabled
}) {
  const className = getClasses(item);
  const wrapperClass = getWrapperClass(className, item);
  const TheComponent = getComponent(item);
  const animProps = buildAnimAttr(item);
  const itemAttributes = item.attr ? Object.entries(item.attr).filter(([_, v]) => v) : [];
  const attrs = buildAttributes([...itemAttributes, ...(animProps ? animProps : [])]); // Avoid rendering premium element if the user is not subscribed to a plan

  const planName = getCurrentPlan(userRoles);

  if (item.type in planElements && !planElements[item.type].includes(planName)) {
    return null;
  }

  if (!TheComponent) {
    return null;
  }

  return React.createElement(TheComponent, {
    item,
    attrs,
    className,
    wrapperClass,
    addIntegration,
    interactionDisabled
  }, children);
}

function getComponent(item) {
  const components = {
    nav: Nav,
    row: Row,
    text: Text,
    line: Line,
    icon: Icon$1,
    code: Code,
    tabs: Tabs,
    title: Title,
    links: Links,
    video: Video,
    image: Image,
    button: Button,
    socialIcon: SocialIcon,
    iframe: Iframe,
    column: Column,
    section: Section,
    iconText: IconText,
    accordion: Accordion,
    counterdown: CounterDown,
    subscription: Subscription,
    contactForm: ContactForm
  };

  if (item.component_path in components) {
    return components[item.component_path];
  } else {
    return null;
  }
}

function App({
  data,
  userRoles = [],
  interactionDisabled = false
}) {
  function renderElement(content) {
    if (typeof content === 'string') {
      return content;
    }

    if (Array.isArray(content)) {
      return content.filter(v => v).map((item, index) => React.createElement(RenderComponent, {
        item: item,
        key: index,
        userRoles: userRoles,
        interactionDisabled: interactionDisabled
      }, renderElement(item.content)));
    }
  }

  if (!data.page) return null;
  return renderElement(data.page.content);
}

function Footer({
  footerScripts,
  footerScriptsInit,
  disableIntegration
}) {
  return React.createElement(React.Fragment, null, !disableIntegration && footerScripts, !disableIntegration && React.createElement("script", {
    type: "text/javascript"
  }, footerScriptsInit.join('\n')));
}

function createFontLink(data) {
  const fonts = [];
  const {
    style,
    heading
  } = data || {};
  if (style) fonts.push({
    style
  });
  if (heading) fonts.push(...Object.values(heading));
  return fonts.reduce(fontReducer, []).map(font => {
    let fontString = '';

    if (font.fontFamily) {
      fontString += plusify(font.fontFamily);

      if (font.fontWeights.length) {
        fontString += `:${font.fontWeights.join(',')}`;
      }
    }

    return fontString;
  }).filter(v => v).join('|');
}

function plusify(str) {
  return str.replace(/\s/g, '+');
}

function fontReducer(acc, next) {
  const {
    fontFamily = '',
    fontWeight = ''
  } = next.style;
  const found = acc.find(font => font.fontFamily === fontFamily);

  if (!found) {
    const fontWeights = [];
    fontWeight && fontWeights.push(fontWeight);
    return acc.concat({
      fontFamily,
      fontWeights
    });
  } else {
    fontWeight && found.fontWeights.push(fontWeight);
    return acc;
  }
}

function getMLUrl(id) {
  return `https://app.mailerlite.com/webforms/submit/${id}`;
}

function recursivelyAddIntegration({
  data,
  addIntegration,
  addCredentials
}) {
  const resources = {
    subscription(el) {
      const {
        form = {},
        settings = {}
      } = el;
      let {
        service,
        webhook = ''
      } = settings;

      if (service === 'activecampaign') {
        const formId = getACId(webhook);
        const account = getACUrl(webhook);
        const ACId = getACId(webhook);
        const initCode = `$(\\#${el.id}\\).activeCampaign({
                    id: \\${formId}\\, 
                    account: \\${account}\\
                })`;
        addIntegration({
          initCode,
          scripts: ['https://assets.dorik.io/shared/jquery-3.5.1.min.js', 'https://assets.dorik.com/common/activecampaign-1.0.0.js'],
          footer: true
        });
        return;
      }

      let data = 'data,';
      let headers = '';
      let dataType = '';
      let queryParams = '';
      let requestType = 'POST';
      let responseCondition = 'data.result === \\success\\';

      if (service === 'mailchimp') {
        dataType = 'dataType: \\jsonp\\,';
        webhook = webhook.replace(/post\?/, 'post-json?').concat('&c=?');
      } else if (service === 'mailerlite') {
        webhook = getMLUrl(webhook);
      } else if (service === 'activecampaign') {
        data = '';
        requestType = 'GET';
        queryParams = `+ \\?jsonp=true&\\ + data`;
        webhook = getACUrl(webhook) + '/proc.php';
      } else if (service === 'convertkit') {
        headers = 'headers: { Accept: \\application/json\\ },';
        responseCondition = 'data.status === \\success\\ || data.status === \\quarantine\\';
      } else if (service === 'sendinblue') {
        queryParams = '+ \\?isAjax=1\\';
      }

      if (service === 'zapier') {
        responseCondition = 'data.status === \\success\\';
      } else if (service === 'integromat' || service === 'pabbly') {
        responseCondition = 'true';
      } else if (service === 'emailoctopus' || service === 'mailerlite' || service === 'sendinblue') {
        responseCondition = 'data.success !== false';
      } else if (service === 'activecampaign') {
        responseCondition = 'data.includes(\\_show_thank_you\\)';
      } // success action


      let successAction = '';

      if (form.successAction === 'forward') {
        successAction = `if(${responseCondition}) {
                    window.location.href = \\${form.forwardTo}\\;
                }`;
      }

      const initCode = `
                $(\\#${el.id}\\).on(\\submit\\, function(e) {
                    e.preventDefault();
                    var that = this;
                    $(this).find(\\.success-message\\).remove();
                    $(this).find(\\.error-message\\).remove();
                    var data = $(this).serialize();
                    $.ajax({
                        url: \\${webhook}\\ ${queryParams},
                        method: \\${requestType}\\,
                        ${data}
                        ${headers}
                        ${dataType}
                        crossDomain: true,
                        success: function(data) {
                            var msgClass = ${responseCondition} ? \\success-message\\: \\error-message\\;
                            var message = ${responseCondition} ? \\${form.successMessage}\\ : \\Something went wrong! Check the fields\\;
                            var $div = $(\\<div>\\, {
                                class: msgClass,
                                html: message
                            });
                            $(that).append($div);
                            ${successAction}
                        },
                        error: function(error) {
                            var $div = $(\\<div>\\, {
                                class: \\error-message\\,
                                html: error.message || \\Something went wrong! Check the fields\\
                            });
                            $(that).append($div);
                        }
                    });
                });
            `;
      addIntegration({
        initCode,
        script: 'https://assets.dorik.io/shared/jquery-3.5.1.min.js',
        footer: true
      });
    },

    heading(el) {
      var _settings$dynamicText;

      const {
        settings = {},
        dynamicText = {}
      } = el;
      if (!((_settings$dynamicText = settings.dynamicText) === null || _settings$dynamicText === void 0 ? void 0 : _settings$dynamicText._enabled) || dynamicText.items.length <= 0) return;
      let backSpeed,
          loop,
          cursorChar,
          typeSpeed,
          wrapperClass = `dorik-${el.type}-${el.id}-wrapper`;

      if (dynamicText.options) {
        ({
          backSpeed,
          loop,
          cursorChar,
          typeSpeed
        } = dynamicText.options);
      }

      cursorChar = cursorChar === true ? '\\|\\' : '\\\\';
      const initCode = `
                ;(function() {
                    var dynamicClass =  document.querySelector(\\.${wrapperClass}\\);
                        var type = dynamicClass.querySelector(\\.typed\\);
                        var typedString = dynamicClass.querySelector(\\.typed-strings\\);
                        var typed = new Typed(type, {
                            stringsElement: typedString,
                            loop: ${loop},
                            backSpeed: ${backSpeed},
                            typeSpeed: ${typeSpeed},
                            cursorChar: ${cursorChar},
                        });
                })();
            `;
      addIntegration({
        initCode,
        footer: true,
        script: 'https://assets.dorik.com/common/typed.js@2.0.11'
      });
    },

    tabs(el) {
      const wrapperClass = `dorik-${el.type}-${el.id}-wrapper`;
      const breakpoint = parseInt(el.tabs.options.breakpoint);
      const selectedTab = Number(el.tabs.options.selectedTab);
      const initCode = `
            ;(
                function makeTabs() {
                    var tabWrapperClass =  document.querySelector(\\.${wrapperClass}\\);
                    var tabs = tabWrapperClass.querySelector(\\.dorik-tabs\\);
                    
                    new AccordionTabs(tabs, {
                        breakpoint: ${breakpoint},
                        selectedTab: ${selectedTab},
                        });
                }
            )()`;
      addIntegration({
        initCode,
        script: 'https://cdn.jsdelivr.net/npm/a11y-accordion-tabs@1.0.2/a11y-accordion-tabs.js',
        footer: true
      });
    },

    contactForm(el) {
      const {
        settings = {},
        form = {}
      } = el;
      const {
        service = 'email',
        webhook = ''
      } = settings;
      const url = service !== 'email' ? webhook : '/v2/sites/action/contact';
      let responseCondition = '';

      if (service === 'email') {
        responseCondition = 'data.success === true';
      } else if (service === 'zapier') {
        responseCondition = 'data.status === \\success\\';
      } else if (service === 'integromat' || service === 'pabbly') {
        responseCondition = 'true';
      } // success action


      let successAction = '';

      if (form.successAction === 'forward') {
        successAction = `if(${responseCondition}) {
                    window.location.href = \\${form.forwardTo}\\;
                }`;
      }

      const initCode = `$(\\#${el.id}\\).on(\\submit\\, function (e) {
                e.preventDefault();
                var that = this;
                $(that).find(\\.success-message\\).remove();
                $(that).find(\\.error-message\\).remove();

                $.ajax({
                    url: \\${url}\\,
                    method: \\POST\\,
                    data: $(this).serialize(),
                    success: function(data) {
                        var msgClass = ${responseCondition} ? \\success-message\\: \\error-message\\;
                        var message = ${responseCondition} ? \\${form.successMessage}\\ : data.message;
                        var $div = $(\\<div>\\, {
                            class: msgClass,
                            html: message
                        });
                        $(that).append($div);
                        ${successAction}
                    },
                    error: function(error) {
                        var $div = $(\\<div>\\, {
                            class: \\error-message\\,
                            html: error.message || \\Something went wrong! Check the fields\\
                        });
                        $(that).append($div);
                    }
                });
            });`;
      addCredentials({
        type: 'contactForm',
        data: {
          formId: el.id,
          email: form.toEmail
        }
      });
      addIntegration({
        initCode,
        script: 'https://assets.dorik.io/shared/jquery-3.5.1.min.js',
        footer: true
      });
    },

    counterdown({
      counter,
      id,
      type
    }) {
      const {
        date,
        time,
        GMT,
        finishText,
        settings: {
          zero,
          onlyHours
        }
      } = counter;
      let wrapperClass = `dorik-${type}-${id}-wrapper `,
          deadLine;

      if (date && time) {
        deadLine = Date.parse(`${date} ${time}  ${GMT ? 'GMT' + GMT : ''}`);
      }

      const initCode = `
                ;(function() {
                    var element = document.querySelector(\\.${wrapperClass}\\);
                    CountDownTimer(element, {
                        zero: \\${zero}\\,
                        endtime: ${deadLine},
                        onlyHours: \\${onlyHours}\\
                    });
                })();
            `;
      addIntegration({
        initCode,
        footer: true,
        script: 'https://assets.dorik.com/common/CountDownTimer.js'
      });
    },

    nav(el) {
      const {
        settings = {}
      } = el;
      const className = `.dorik-nav-${el.id}`;
      let toggleCode = `
            (function toggleNav() {
                var toggleBtn = document.querySelector(\\.dorik-navbar--toggle\\);
                toggleBtn.addEventListener(\\click\\, function (e) {
                    var navbarId = document.querySelector(toggleBtn.dataset.target);
                    var hasShow = navbarId.classList.contains(\\show\\);
                    if (!hasShow) {
                        expend();
                    } else {
                        collapse();
                    }
                    navbarId.addEventListener(\\click\\, function (e) {
                        if (e.target.closest(\\a\\)) {
                            collapse();
                        }
                    });
            
                    function expend() {
                        navbarId.classList.add(\\show\\);
                        var height = navbarId.clientHeight + \\px\\;
                        navbarId.classList.add(\\collapsing\\);
                        setTimeout(() => {
                            navbarId.style.height = height;
                        }, 0);
                        setTimeout(() => {
                            navbarId.style.height = null;
                            navbarId.classList.remove(\\collapsing\\);
                        }, 350);
                    }
                    function collapse() {
                        navbarId.style.height = navbarId.clientHeight + \\px\\;
                        navbarId.classList.add(\\collapsing\\);
                        setTimeout(() => {
                            navbarId.style.height = null;
                        }, 0);
                        setTimeout(() => {
                            navbarId.classList.remove(\\show\\);
                            navbarId.classList.remove(\\collapsing\\);
                        }, 350);
                    }
                });
            })();`;
      const strickyCode = `
            (function stickyNav() {
                var navbar = document.querySelector(\\.dorik-navbar\\);
                if (navbar) {
                    var last_known_scroll_position = 0;
                    var ticking = false;
            
                    function scroll(scroll_pos) {
                        var parent = navbar.parentNode;
                        var classList = parent.classList;
                        var upWard = scroll_pos <= 50 && classList.contains(\\sticky\\);
                        var downWard = scroll_pos > 50 && !classList.contains(\\sticky\\);
                        if (downWard) {
                            var height = parent.clientHeight;
                            parent.style.top = \\-\\ + height + \\px\\;
                            setTimeout(() => {
                                parent.classList.add(\\sticky\\);
                                parent.style.top = null;
                            }, 0);
                        } else if (upWard) {
                            parent.classList.remove(\\sticky\\);
                        }
                    }
            
                    document.addEventListener(\\scroll\\, function (e) {
                        last_known_scroll_position = window.scrollY;
            
                        if (!ticking) {
                            window.requestAnimationFrame(function () {
                                scroll(last_known_scroll_position);
            
                                ticking = false;
                            });
                            ticking = true;
                        }
                    });
                }
            })();`;
      addIntegration({
        initCode: toggleCode + (settings.sticky ? strickyCode : ''),
        script: 'https://assets.dorik.io/shared/jquery-3.5.1.min.js',
        footer: true
      });
    }

  };

  (function recursive(el) {
    if (!el) return;

    if (typeof resources[el.type] === 'function') {
      resources[el.type](el);
    }

    if (Array.isArray(el.content)) {
      el.content.forEach(recursive);
    }
  })(data);
}

function hasAnimation(data) {
  let result = false;

  (function recursive(el) {
    if (result || !el) return;

    if (el.animation && el.animation.type) {
      result = true;
      return;
    } else if (Array.isArray(el.content)) {
      el.content.forEach(recursive);
    }
  })(data);

  return result;
}

function parseIntegrationAssets ({
  data,
  userRoles = []
}) {
  const {
    global,
    page
  } = data;
  const {
    integrations = {}
  } = global || {};
  const {
    GTM
  } = integrations;
  const currentPlan = getCurrentPlan(userRoles);
  const stylesheets = [],
        footerScripts = [],
        headerScripts = [],
        footerScriptsInit = [],
        headerScriptsInit = [],
        credentials = [],
        bodyBegins = [];

  if (GTM) {
    bodyBegins.push(React.createElement("noscript", null, React.createElement("iframe", {
      width: "0",
      height: "0",
      style: {
        display: 'none',
        visibility: 'hidden'
      },
      src: `https://www.googletagmanager.com/ns.html?id=${GTM}`
    })));
  }

  function addCredentials(value) {
    credentials.push(value);
  }
  /**
   * @typedef {Object} AddIntegration
   * @property {string} [script]
   * @property {string} initCode
   * @property {boolean} [footer]
   * @property {boolean} [header]
   *
   * @param {AddIntegration} param0
   */


  function addIntegration({
    script,
    scripts = [],
    initCode,
    footer = false,
    header = false,
    once = false
  }) {
    const scriptsStore = header ? headerScripts : footerScripts;
    const init = header ? headerScriptsInit : footerScriptsInit; // TODO: don't init same resource twice
    // if (once) {}

    if (script && !scriptsStore.includes(script)) {
      scriptsStore.push(React.createElement("script", {
        src: script
      }));
    }

    if (scripts.length) {
      scripts.forEach(script => {
        if (!scriptsStore.includes(script)) {
          scriptsStore.push(React.createElement("script", {
            type: "text/javascript",
            src: script
          }));
        }
      });
    }

    initCode && init.push(initCode);
  }

  recursivelyAddIntegration({
    data: page,
    addIntegration,
    addCredentials
  });

  if (hasAnimation(page)) {
    stylesheets.push(React.createElement("link", {
      rel: "stylesheet",
      href: "https://assets.dorik.io/shared/aos.css"
    }));
    footerScripts.push(React.createElement("script", {
      src: "https://assets.dorik.io/shared/aos.js"
    }));
    footerScriptsInit.push(`
            window.AOS.init({disable: \\mobile\\});
        `);
  }

  return {
    footer: React.createElement(Footer, {
      footerScripts: footerScripts,
      footerScriptsInit: footerScriptsInit
    }),
    header: React.createElement(Header, {
      data: data,
      userRoles: userRoles,
      stylesheets: stylesheets,
      headerScripts: headerScripts,
      headerScriptsInit: headerScriptsInit
    }),
    credentials,
    stylesheets,
    bodyBegins,
    headerScripts,
    footerScripts,
    headerScriptsInit,
    footerScriptsInit
  };
}

function OgMeta({
  pageMeta = {},
  globalMeta = {}
}) {
  const properties = ['og:title', 'og:image', 'og:description'];
  const ogMetaData = properties.map(property => {
    const content = pageMeta[property] || globalMeta[property];
    return {
      property,
      content
    };
  }).filter(meta => meta.content);
  const twitterCardMeta = ogMetaData.filter(meta => meta.property === 'og:image').map(meta => ({
    name: 'twitter:image',
    content: meta.content
  }));
  const combinedMeta = ogMetaData.concat(twitterCardMeta);
  return combinedMeta.map((item, i) => React.createElement("meta", _extends({
    key: i
  }, item)));
}

function Header({
  data,
  children,
  userRoles,
  disableIntegration
}) {
  const {
    settings: pageSettings = {}
  } = (data === null || data === void 0 ? void 0 : data.page) || {};
  const {
    settings: globalSettings = {}
  } = (data === null || data === void 0 ? void 0 : data.global) || {};
  const {
    meta: pageMeta = {}
  } = pageSettings;
  const {
    meta: globalMeta = {}
  } = globalSettings;
  const fontFamilies = createFontLink(data === null || data === void 0 ? void 0 : data.global);
  const {
    stylesheets,
    headerScripts,
    headerScriptsInit
  } = parseIntegrationAssets({
    data,
    userRoles
  });
  return React.createElement(React.Fragment, null, React.createElement("meta", {
    charSet: "UTF-8"
  }), pageMeta.title && React.createElement("title", null, pageMeta.title || ''), pageMeta.description && React.createElement("meta", {
    name: "description",
    content: pageMeta.description || ''
  }), React.createElement("link", {
    rel: "icon",
    href: globalMeta.favicon
  }), React.createElement("meta", {
    name: "viewport",
    content: "width=device-width,initial-scale=1"
  }), React.createElement(OgMeta, {
    globalMeta: globalMeta,
    pageMeta: pageMeta
  }), !disableIntegration && headerScripts, !disableIntegration && React.createElement("script", {
    type: "text/javascript"
  }, headerScriptsInit.join('\n').trim()), fontFamilies && React.createElement("link", {
    href: `https://fonts.googleapis.com/css?family=${fontFamilies}&display=swap`,
    rel: "stylesheet"
  }), React.createElement("link", {
    rel: "stylesheet",
    href: "./css/main.css"
  }), !disableIntegration ? stylesheets : null, children);
}

function BodyBegins({
  data
}) {
  const {
    bodyBegins
  } = parseIntegrationAssets({
    data
  });
  return bodyBegins.map((item, i) => React.createElement(React.Fragment, {
    key: i
  }, item));
}

function getCustomCode(data) {
  var _global$advanced, _page$advanced, _global$advanced2, _page$advanced2;

  const {
    page = {},
    global = {}
  } = data;
  const globalHeader = (global === null || global === void 0 ? void 0 : (_global$advanced = global.advanced) === null || _global$advanced === void 0 ? void 0 : _global$advanced.headerInject) || '';
  const pageHeader = (page === null || page === void 0 ? void 0 : (_page$advanced = page.advanced) === null || _page$advanced === void 0 ? void 0 : _page$advanced.headerInject) || '';
  const globalFooter = (global === null || global === void 0 ? void 0 : (_global$advanced2 = global.advanced) === null || _global$advanced2 === void 0 ? void 0 : _global$advanced2.footerInject) || '';
  const pageFooter = (page === null || page === void 0 ? void 0 : (_page$advanced2 = page.advanced) === null || _page$advanced2 === void 0 ? void 0 : _page$advanced2.footerInject) || '';
  return [`${globalHeader}\n${pageHeader}`, `${globalFooter}\n${pageFooter}`];
}

function getIntegrations(integrations = {}) {
  const header = buildIntegrationValue(['GTM', 'googleAnalytics'], integrations);
  const footer = buildIntegrationValue(['crisp', 'hotjar', 'pixel', 'intercom'], integrations);
  return [integration.init(header), integration.init(footer)];
}

function buildIntegrationValue(items, integrations) {
  return items.map(key => {
    const value = integrations[key];

    if (typeof value === 'string' && value.trim()) {
      return {
        key,
        value
      };
    }
  }).filter(v => v);
}

const integration = {
  init(options) {
    return options.map(opt => opt.key in this.resource ? this.resource[opt.key](opt.value) : null).filter(v => v).join('\n');
  },

  resource: {
    GTM,
    crisp,
    hotjar,
    pixel,
    intercom,
    googleAnalytics
  }
};

function GTM(id) {
  return `<script type="text/javascript">
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start":
        new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src=
        "https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,"script","dataLayer","${id}");
    </script>`;
}

function googleAnalytics(id) {
  return `
        <script async src="https://www.googletagmanager.com/gtag/js?id=${id}"></script>
        <script type="text/javascript">
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag("js", new Date());
            gtag("config", "${id}");
        </script>
    `;
}

function crisp(id) {
  return `<script type="text/javascript">window.$crisp=[];window.CRISP_WEBSITE_ID="${id}";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();</script>`;
}

function hotjar(id) {
  return `<script>
    (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${id},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
</script>`;
}

function intercom(id) {
  return `<script>
        window.intercomSettings = {
            app_id: "${id}"
        };
        (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${id}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
    </script>`;
}

function pixel(id) {
  return `<!-- Facebook Pixel Code -->
    <script>
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    </script>
    <noscript>
      <img height="1" width="1" style="display:none" 
           src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/>
    </noscript>
    <!-- End Facebook Pixel Code -->`;
}

const {
  renderToStaticMarkup
} = require('react-dom/server');
async function parseHtmlString({
  data,
  userRoles
}) {
  var _data$global;

  const {
    footer,
    header,
    bodyBegins,
    credentials
  } = parseIntegrationAssets({
    data,
    userRoles
  });
  const bodyHtmlString = renderToStaticMarkup(React.createElement(App, {
    data: data,
    userRoles: userRoles
  }));
  const beforeBody = renderToStaticMarkup(bodyBegins);
  const footerHtmlString = renderToStaticMarkup(footer).replace(/\\/gi, '"').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&sbquo;/gi, "'").replace(/&gt;/gi, '>');
  const headerHtmlString = renderToStaticMarkup(header).replace(/\\/gi, '"').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>');
  const [headerCode, footerCode] = getCustomCode(data);
  const [headerIntegrations, footerIntegrations] = getIntegrations(data === null || data === void 0 ? void 0 : (_data$global = data.global) === null || _data$global === void 0 ? void 0 : _data$global.integrations);
  const html = `<!DOCTYPE html>
        <html lang="en">
        <head>
            ${headerHtmlString}
            ${headerIntegrations}
            ${headerCode}
        </head>
        <body>
            ${beforeBody}
            ${bodyHtmlString}
            ${footerHtmlString}
            ${footerIntegrations}
            ${footerCode}
        </body>
    </html>`;
  return {
    html,
    credentials
  };
}

export { App as Body, BodyBegins, Footer, Header, parseHtmlString, parseIntegrationAssets, socialIcons as socialIconSvgs };
